import React, { useState, useEffect, useMemo, useRef } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import { useAuth } from './context/AuthContext';
import LandingPage from './LandingPage';
import LegalPage from './LegalPage';
import Accounting from './Accounting';
import {
  useJobs, useAdminDashboard, useAdminUsers, useAdminJobs,
  usePriceTable, useAdminPriceTable, useMyWithdrawals,
  useAdminWithdrawals, useMyDocuments, useFetch, useAction,
} from './hooks/index';
import api, { API_BASE_URL, isPrivateUploadUrl, fetchPrivateUpload } from './api/client';
import { isTerminalPaymentState, paymentState, withoutReusablePix } from './utils/paymentState';
import { financialPolicySnapshot, jobCommissionRateSnapshot, jobWorkerPayoutRateSnapshot, currentCommissionLabel } from './utils/financialSnapshot';
import { createJobCreationAttempt } from './utils/jobCreationAttempt';
import { maskMoney, parseMoney, cardInstallments } from './utils/money';
import { requestUserCameraStream, waitForVideoElement, attachStreamToVideo, captureVideoFrame } from './utils/livenessCamera';

// ━━━ DESIGN TOKENS ━━━
const C = {
  bg: '#1a1a2e', surface: '#0F1218', card: '#161B26', cardHov: '#1C2233',
  border: '#1E2535', borderHi: '#2A3550',
  accent: '#00D4A0', accentDim: '#00D4A015', accentHov: '#00EDBA',
  gold: '#F0A500', goldDim: '#F0A50015',
  danger: '#FF4560', dangerDim: '#FF456015',
  info: '#3D8EFF', infoDim: '#3D8EFF15',
  purple: '#9B6DFF',
  text: '#E8EDF8', sub: '#7A8699', muted: '#404B60',
  font: "'DM Sans', sans-serif", mono: "'JetBrains Mono', monospace",
};

const roleColor = { admin: C.gold, client: C.accent, worker: C.info, partner: C.purple };
const SECTION_HEADER_BG = '#1a1a2e';
const FIXED_SECTION_WRAPPER_STYLE = {
  height: '100%',
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
};
const FIXED_SECTION_HEADER_STYLE = {
  flexShrink: 0,
  background: SECTION_HEADER_BG,
  padding: '24px 32px 16px',
  borderBottom: `1px solid ${C.border}`,
  boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
};
const FIXED_SECTION_CONTENT_STYLE = {
  flex: '1 1 auto',
  minHeight: 0,
  overflowY: 'auto',
  overflowX: 'hidden',
  padding: '20px 32px 40px',
};

// ━━━ KEYFRAMES ━━━
const injectStyles = () => {
  if (document.getElementById('qs-styles')) return;
  const s = document.createElement('style');
  s.id = 'qs-styles';
  s.textContent = `
    html, body { margin: 0; padding: 0; width: 100%; max-width: 100%; overflow-x: hidden; }
    #root { height: 100%; min-height: 100%; }
    body.app-mode { overflow: hidden; height: 100%; }
    *, *::before, *::after { box-sizing: border-box; }
    @keyframes spin { to { transform: rotate(360deg); } }
    @keyframes fadeIn { from { opacity:0; } to { opacity:1; } }
    .qs-card-hover:hover { background: ${C.cardHov} !important; cursor: pointer; }
    .qs-input:focus { outline: none; border-color: ${C.accent} !important; box-shadow: 0 0 0 2px ${C.accentDim}; }
    .qs-btn-primary:hover:not(:disabled) { background: ${C.accentHov} !important; }
    .qs-btn-ghost:hover:not(:disabled) { background: ${C.accentDim} !important; color: ${C.accent} !important; }
    .qs-btn-danger:hover:not(:disabled) { opacity: 0.85; }
    .qs-btn-gold:hover:not(:disabled) { opacity: 0.85; }
    .qs-btn-outline:hover:not(:disabled) { border-color: ${C.accent} !important; color: ${C.accent} !important; }
    .qs-nav-item:hover { background: ${C.accentDim} !important; color: ${C.accent} !important; }
    .qs-nav-active { background: ${C.accentDim} !important; color: ${C.accent} !important; border-left: 3px solid ${C.accent} !important; }
    .qs-nav-active-gold { background: ${C.goldDim} !important; color: ${C.gold} !important; border-left: 3px solid ${C.gold} !important; }
    .qs-nav-active-info { background: ${C.infoDim} !important; color: ${C.info} !important; border-left: 3px solid ${C.info} !important; }
    .qs-section-header { flex-shrink: 0; background: ${SECTION_HEADER_BG}; }
    .qs-admin-workers-fixed-header {
      position: sticky;
      top: 0;
      z-index: 30;
      background: ${SECTION_HEADER_BG};
    }
    .qs-admin-workers-scroll {
      flex: 1 1 auto;
      min-height: 0;
      overflow-y: auto;
      overflow-x: hidden;
      -webkit-overflow-scrolling: touch;
    }
    .qs-app-shell { display: flex; height: 100vh; min-height: 0; overflow: hidden; background: ${C.bg}; }
    .qs-app-main { flex: 1; height: 100vh; min-height: 0; min-width: 0; overflow: hidden; }
    .qs-card { max-width: 100%; }
    .qs-price-card {
      position: relative;
      min-height: 142px;
      background: ${C.card};
      border: 1px solid ${C.border};
      border-radius: 12px;
      padding: 16px 14px;
      text-align: center;
      cursor: pointer;
      transition: background 0.15s, border-color 0.15s, transform 0.15s, box-shadow 0.15s;
    }
    .qs-price-card:hover,
    .qs-price-card:focus,
    .qs-price-card-active {
      background: ${C.cardHov};
      border-color: ${C.accent}80;
      box-shadow: 0 10px 24px rgba(0,0,0,0.22);
      transform: translateY(-1px);
      outline: none;
    }
    .qs-price-preview {
      min-height: 62px;
      margin-top: 12px;
      opacity: 0;
      transform: translateY(4px);
      transition: opacity 0.16s, transform 0.16s;
      pointer-events: none;
    }
    .qs-price-card:hover .qs-price-preview,
    .qs-price-card:focus .qs-price-preview,
    .qs-price-card-active .qs-price-preview {
      opacity: 1;
      transform: translateY(0);
    }
    .qs-price-card:hover .qs-price-hint,
    .qs-price-card:focus .qs-price-hint,
    .qs-price-card-active .qs-price-hint {
      color: ${C.accent};
    }
    @media (max-width: 768px) {
      body.app-mode { overflow: hidden; height: 100%; }
      .qs-app-shell {
        flex-direction: column !important;
        height: 100dvh !important;
        min-height: 100dvh !important;
        width: 100% !important;
        overflow: hidden !important;
      }
      .qs-app-main {
        width: 100% !important;
        height: auto !important;
        min-height: 0 !important;
        flex: 1 1 auto !important;
        overflow: hidden !important;
      }
      .qs-sidebar {
        position: relative !important;
        top: auto !important;
        left: auto !important;
        width: 100% !important;
        height: auto !important;
        max-height: none !important;
        border-right: 0 !important;
        border-bottom: 1px solid ${C.border} !important;
        overflow: visible !important;
        flex: 0 0 auto !important;
      }
      .qs-sidebar-spacer { display: none !important; }
      .qs-sidebar-header {
        padding: 10px 14px !important;
        display: flex !important;
        align-items: center !important;
        justify-content: space-between !important;
        gap: 12px !important;
      }
      .qs-sidebar-brand {
        font-size: 20px !important;
        margin-bottom: 0 !important;
        letter-spacing: 0 !important;
        flex-shrink: 0 !important;
      }
      .qs-sidebar-user {
        max-width: 54% !important;
        justify-content: flex-end !important;
      }
      .qs-sidebar-nav {
        flex: 0 0 auto !important;
        display: flex !important;
        gap: 8px !important;
        padding: 8px 12px !important;
        overflow-x: auto !important;
        overflow-y: hidden !important;
        -webkit-overflow-scrolling: touch;
        scrollbar-width: none;
      }
      .qs-sidebar-nav::-webkit-scrollbar { display: none; }
      .qs-nav-entry {
        flex: 0 0 auto !important;
        min-height: 40px !important;
        padding: 9px 12px !important;
        border-left: 0 !important;
        border-bottom: 3px solid transparent !important;
        border-radius: 10px !important;
        background: ${C.card} !important;
        white-space: nowrap !important;
      }
      .qs-nav-active {
        background: ${C.accentDim} !important;
        color: ${C.accent} !important;
        border-left: 0 !important;
        border-bottom-color: ${C.accent} !important;
      }
      .qs-nav-active-gold {
        background: ${C.goldDim} !important;
        color: ${C.gold} !important;
        border-left: 0 !important;
        border-bottom-color: ${C.gold} !important;
      }
      .qs-nav-active-info {
        background: ${C.infoDim} !important;
        color: ${C.info} !important;
        border-left: 0 !important;
        border-bottom-color: ${C.info} !important;
      }
      .qs-sidebar-footer {
        padding: 6px 12px 10px !important;
        border-top: 0 !important;
      }
      .qs-sidebar-logout {
        min-height: 38px !important;
      }
      .qs-section {
        height: 100% !important;
        min-height: 0 !important;
      }
      .qs-section-header {
        padding: 16px 16px 12px !important;
      }
      .qs-admin-workers-fixed-header {
        top: 0;
        z-index: 35;
      }
      .qs-section-content {
        padding: 14px 14px 24px !important;
        -webkit-overflow-scrolling: touch;
        overflow-wrap: anywhere;
      }
      .qs-section-inner {
        max-width: 100% !important;
      }
      .qs-section-title {
        font-size: 21px !important;
        line-height: 1.18 !important;
      }
      .qs-section-subtitle {
        font-size: 13px !important;
        line-height: 1.35 !important;
      }
      .qs-card {
        padding: 16px !important;
        border-radius: 10px !important;
        overflow: hidden !important;
      }
      .qs-price-card {
        min-height: 150px !important;
        padding: 15px 12px !important;
      }
      .qs-login-shell {
        flex-direction: column !important;
        height: auto !important;
        min-height: 100dvh !important;
        overflow-y: auto !important;
        overflow-x: hidden !important;
      }
      .qs-login-left {
        width: 100% !important;
        padding: 24px 20px 18px !important;
        border-right: 0 !important;
        border-bottom: 1px solid ${C.border} !important;
        justify-content: flex-start !important;
        overflow: visible !important;
      }
      .qs-login-title {
        font-size: 30px !important;
        letter-spacing: 0 !important;
      }
      .qs-login-intro {
        margin-bottom: 0 !important;
      }
      .qs-login-features {
        display: none !important;
      }
      .qs-login-right {
        width: 100% !important;
        flex: 1 1 auto !important;
        align-items: flex-start !important;
        padding: 24px 18px 34px !important;
        overflow: visible !important;
      }
      .qs-login-card {
        max-width: 100% !important;
      }
      .qs-compact-row {
        flex-direction: column !important;
      }
    }
    @media (max-width: 420px) {
      .qs-sidebar-header { align-items: flex-start !important; }
      .qs-sidebar-user { max-width: 48% !important; }
      .qs-nav-entry { font-size: 13px !important; }
      .qs-section-header { padding: 14px 14px 10px !important; }
      .qs-section-content { padding: 12px 12px 22px !important; }
      .qs-login-left { padding: 22px 18px 16px !important; }
      .qs-login-right { padding: 22px 14px 30px !important; }
    }
  `;
  document.head.appendChild(s);
};
injectStyles();

// ━━━ CONSTANTS ━━━
const AREAS = [
  'Diarista', 'Limpeza', 'Jardinagem', 'Elétrica', 'Encanamento', 'Pintura', 'Reformas',
  'Operador de Som', 'Operador de Iluminação', 'Técnico de Palco', 'Roadie',
  'DJ', 'Fotógrafo de Eventos', 'Segurança', 'Outros',
];

const DOCUMENT_TYPES = [
  { key: 'selfie',        label: 'Foto do rosto (selfie)' },
  { key: 'rg_front',     label: 'RG — frente' },
  { key: 'rg_back',      label: 'RG — verso' },
  { key: 'cpf_doc',      label: 'CPF' },
  { key: 'proof_address',label: 'Comprovante de residência' },
];

const WORKER_REGISTRATION_DOCUMENT_TYPES = DOCUMENT_TYPES.filter((dt) => dt.key !== 'selfie');
const documentTypeLabel = (type) =>
  DOCUMENT_TYPES.find((dt) => dt.key === type)?.label || type;

const chipColors = {
  open: { bg: C.accentDim, color: C.accent },
  accepted: { bg: C.infoDim, color: C.info },
  in_progress: { bg: '#9B6DFF15', color: C.purple },
  done: { bg: C.goldDim, color: C.gold },
  cancelled: { bg: C.dangerDim, color: C.danger },
  pending_payment: { bg: C.goldDim, color: C.gold },
  pending: { bg: C.goldDim, color: C.gold },
  active: { bg: C.accentDim, color: C.accent },
  approved: { bg: C.accentDim, color: C.accent },
  rejected: { bg: C.dangerDim, color: C.danger },
  quote: { bg: '#9B6DFF15', color: C.purple },
  pix: { bg: C.accentDim, color: C.accent },
  card: { bg: C.infoDim, color: C.info },
  boleto: { bg: C.goldDim, color: C.gold },
  fixo: { bg: C.accentDim, color: C.accent },
  hora: { bg: C.infoDim, color: C.info },
  m2: { bg: '#9B6DFF15', color: C.purple },
  AGUARDANDO_PAGAMENTO: { bg: C.goldDim, color: C.gold },
  PAGO: { bg: C.infoDim, color: C.info },
  PENDENTE_LIBERACAO: { bg: C.goldDim, color: C.gold },
  LIBERADO: { bg: C.infoDim, color: C.info },
  TRANSFERIDO: { bg: C.accentDim, color: C.accent },
  REJEITADO: { bg: C.dangerDim, color: C.danger },
};

const chipLabels = {
  open: 'Aberta', pending_payment: 'Aguardando pagamento', accepted: 'Aceita', in_progress: 'Em andamento',
  done: 'Concluída', cancelled: 'Cancelada', pending: 'Pendente',
  active: 'Ativo', approved: 'Aprovado', rejected: 'Reprovado',
  quote: 'Orçamento', pix: 'PIX', card: 'Cartão', boleto: 'Boleto',
  fixo: 'Fixo', hora: '/hora', m2: '/m²',
};

const releaseStatusLabels = {
  AGUARDANDO_PAGAMENTO: 'Aguardando Pagamento',
  PAGO: 'Pago',
  PENDENTE_LIBERACAO: 'Pendente Liberacao',
  LIBERADO: 'Liberado',
  TRANSFERIDO: 'Transferido',
  REJEITADO: 'Rejeitado',
};

// ━━━ BASE COMPONENTS ━━━
const Chip = ({ type, label }) => {
  const c = chipColors[type] || { bg: C.border, color: C.sub };
  return (
    <span style={{
      background: c.bg, color: c.color, border: `1px solid ${c.color}30`,
      borderRadius: 6, padding: '2px 10px', fontSize: 11, fontWeight: 700,
      letterSpacing: 0.5, textTransform: 'uppercase', display: 'inline-block',
    }}>
      {label || chipLabels[type] || releaseStatusLabels[type] || type}
    </span>
  );
};

const safeExternalUrl = (value) => {
  if (!value) return null;
  try {
    const parsed = new URL(String(value), window.location.origin);
    if (parsed.protocol === 'https:') return parsed.toString();
    const localBrowser = ['localhost', '127.0.0.1'].includes(window.location.hostname);
    const localTarget = ['localhost', '127.0.0.1'].includes(parsed.hostname);
    return parsed.protocol === 'http:' && localBrowser && localTarget ? parsed.toString() : null;
  } catch (_) {
    return null;
  }
};

const resolveAssetUrl = (value) => {
  if (!value) return null;
  const fp = String(value).replace(/\\/g, '/');
  if (fp.startsWith('http://') || fp.startsWith('https://')) return safeExternalUrl(fp);
  const base = API_BASE_URL;
  if (fp.startsWith('/uploads')) return `${base}${fp}`;
  const rel = fp.split('/uploads/')[1];
  return rel ? `${base}/uploads/${rel}` : null;
};

const useProtectedAssetUrl = (source) => {
  const resolved = resolveAssetUrl(source);
  const [displayUrl, setDisplayUrl] = useState(null);
  useEffect(() => {
    if (!resolved || !isPrivateUploadUrl(resolved)) {
      setDisplayUrl(resolved);
      return undefined;
    }
    let active = true;
    let objectUrl = null;
    setDisplayUrl(null);
    fetchPrivateUpload(resolved)
      .then((blob) => {
        objectUrl = URL.createObjectURL(blob);
        if (active) setDisplayUrl(objectUrl);
        else URL.revokeObjectURL(objectUrl);
      })
      .catch(() => { if (active) setDisplayUrl(null); });
    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [resolved]);
  return displayUrl;
};

const ProtectedAsset = ({ source, children }) => children(useProtectedAssetUrl(source));

const MOBILE_WORKER_REGISTRATION_MESSAGE = 'Cadastro de colaborador deve ser feito pelo celular, usando a camera frontal para a selfie de validacao.';

const isMobileRegistrationDevice = () => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  const mobileUa = /Android|iPhone|iPad|iPod|Mobile|Windows Phone|BlackBerry|Opera Mini|IEMobile/i.test(ua);
  const desktopUa = /Windows NT|Macintosh|X11|Linux x86_64/i.test(ua);
  const touchSmallScreen = (navigator.maxTouchPoints || 0) > 0 && window.innerWidth <= 920;
  return mobileUa || (touchSmallScreen && !desktopUa);
};

const Avatar = ({ name = '?', size = 36, color = C.accent, src }) => {
  const [imgError, setImgError] = useState(false);
  const url = useProtectedAssetUrl(src);
  if (url && !imgError) {
    return (
      <img src={url} alt={name} onError={() => setImgError(true)}
        style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover',
          border: `2px solid ${color}40`, flexShrink: 0 }} />
    );
  }
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%',
      background: `${color}20`, border: `2px solid ${color}40`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color, fontWeight: 800, fontSize: size * 0.4, flexShrink: 0, fontFamily: C.font,
    }}>
      {name.charAt(0).toUpperCase()}
    </div>
  );
};

const Card = ({ children, style, onClick }) => (
  <div
    className={`qs-card ${onClick ? 'qs-card-hover' : ''}`}
    onClick={onClick}
    style={{
      background: C.card, border: `1px solid ${C.border}`,
      borderRadius: 12, padding: 20, transition: 'background 0.15s', ...style,
    }}
  >
    {children}
  </div>
);

const Btn = ({ children, variant = 'primary', onClick, full, small, disabled, loading: ld, type }) => {
  const styles = {
    primary: { background: C.accent, color: '#000', border: 'none' },
    ghost: { background: 'transparent', color: C.sub, border: `1px solid ${C.border}` },
    danger: { background: C.danger, color: '#fff', border: 'none' },
    gold: { background: C.gold, color: '#000', border: 'none' },
    outline: { background: 'transparent', color: C.sub, border: `1px solid ${C.border}` },
  };
  return (
    <button
      className={`qs-btn-${variant}`}
      type={type}
      onClick={onClick}
      disabled={disabled || ld}
      style={{
        ...styles[variant],
        padding: small ? '6px 14px' : '10px 20px',
        borderRadius: 8, fontWeight: 700, fontSize: small ? 12 : 14,
        cursor: disabled || ld ? 'not-allowed' : 'pointer',
        opacity: disabled || ld ? 0.5 : 1,
        width: full ? '100%' : 'auto',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
        transition: 'all 0.15s', fontFamily: C.font, whiteSpace: 'nowrap',
      }}
    >
      {ld ? <Spinner size={14} /> : children}
    </button>
  );
};

const FInput = ({ label, type = 'text', placeholder, value, onChange, icon, extra, disabled, ...inputProps }) => (
  <div style={{ marginBottom: 14 }}>
    {label && <label style={{ display: 'block', fontSize: 12, color: C.sub, marginBottom: 5, fontWeight: 600 }}>{label}</label>}
    <div style={{ position: 'relative' }}>
      {icon && <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: C.muted, fontSize: 16 }}>{icon}</span>}
      <input
        className="qs-input"
        type={type}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        disabled={disabled}
        {...inputProps}
        style={{
          width: '100%', background: C.surface, border: `1px solid ${C.border}`,
          borderRadius: 8, padding: icon ? '10px 12px 10px 38px' : '10px 12px',
          color: C.text, fontSize: 14, fontFamily: C.font,
          transition: 'border-color 0.15s, box-shadow 0.15s',
          opacity: disabled ? 0.6 : 1,
        }}
      />
      {extra}
    </div>
  </div>
);

const MoneyInput = ({ label, digits, onChange, disabled = false }) => (
  <FInput
    label={label}
    placeholder="R$ 0,00"
    value={maskMoney(digits)}
    onChange={(e) => { onChange(e.target.value.replace(/\D/g, '')); }}
    disabled={disabled}
  />
);

// Formata valor numérico para exibição em Reais (R$ 1.234,56)
const fmtMoney = (n) =>
  'R$ ' + Number(n || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('pt-BR');
};

const toDateInputValue = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
};

const repairText = (value) => {
  const text = String(value || '');
  if (!/[ÃÂ]/.test(text)) return text;
  try {
    return decodeURIComponent(escape(text));
  } catch (_) {
    return text;
  }
};

const serviceKey = (value) =>
  repairText(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

const priceUnitLabel = (unit) => {
  const normalized = serviceKey(unit);
  if (normalized === 'hora') return 'por hora';
  if (normalized === 'm2') return 'por m2';
  return 'por servico';
};

const stateKey = (value) => serviceKey(value).toUpperCase();
const priceScope = (price) => String(price?.scope || 'global').toLowerCase();

const priceScopeRank = (price, region = {}) => {
  const scope = priceScope(price);
  const requestState = stateKey(region.state);
  const requestCity = serviceKey(region.city);
  const requestPartnerId = String(region.partnerId || '').trim();
  const priceState = stateKey(price.state);
  const priceCity = serviceKey(price.city);
  const pricePartnerId = String(price.partnerId || '').trim();

  if (scope === 'partner') return requestPartnerId && pricePartnerId === requestPartnerId ? 100 : -1;
  if (scope === 'city') return requestState && requestCity && priceState === requestState && priceCity === requestCity ? 80 : -1;
  if (scope === 'state') return requestState && priceState === requestState ? 60 : -1;
  return 10;
};

const findServicePrice = (prices = [], category, subcategory, region = {}) => {
  const catKey = serviceKey(category);
  const subKey = serviceKey(subcategory);
  return prices
    .filter((p) => serviceKey(p.category) === catKey && serviceKey(p.subcategory) === subKey)
    .map((p) => ({ price: p, rank: priceScopeRank(p, region) }))
    .filter((entry) => entry.rank >= 0)
    .sort((a, b) => b.rank - a.rank)[0]?.price || null;
};

const priceScopeLabel = (price, partners = []) => {
  if (!price) return 'Sem regra de preco';
  const scope = priceScope(price);
  if (scope === 'state') return `UF ${String(price.state || '').toUpperCase()}`;
  if (scope === 'city') return `${price.city || 'Cidade'}/${String(price.state || '').toUpperCase()}`;
  if (scope === 'partner') {
    const partner = partners.find((p) => p.id === price.partnerId);
    return partner ? `Parceiro: ${partner.name}` : (price.scopeLabel || 'Parceiro especifico');
  }
  return 'Global';
};

const REVIEW_TAGS = ['Pontual', 'Educado', 'Serviço impecável', 'Preço justo', 'Recomendo', 'Profissional'];

const ReviewModal = ({ job, onClose, onDone }) => {
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState('');
  const [tags, setTags] = useState([]);
  const action = useAction();

  const toggleTag = (t) => setTags((prev) => prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]);

  const submit = async () => {
    if (!rating) return;
    await action.run(() => api.reviews.create({ jobId: job.id, rating, comment, tags }));
    onDone();
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: 20 }}>
      <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 16, padding: 28, width: '100%', maxWidth: 480 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div>
            <div style={{ fontWeight: 800, fontSize: 18, color: C.text }}>Avalie o serviço</div>
            <div style={{ fontSize: 13, color: C.sub, marginTop: 2 }}>{job.category} / {job.subcategory}</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: C.sub, fontSize: 20, cursor: 'pointer' }}>✕</button>
        </div>
        <div style={{ display: 'flex', gap: 6, marginBottom: 20, justifyContent: 'center' }}>
          {[1,2,3,4,5].map((i) => (
            <span key={i}
              onClick={() => setRating(i)}
              onMouseEnter={() => setHover(i)}
              onMouseLeave={() => setHover(0)}
              style={{ fontSize: 36, cursor: 'pointer', color: i <= (hover || rating) ? C.gold : C.muted, transition: 'color 0.1s' }}>★</span>
          ))}
        </div>
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 12, color: C.sub, fontWeight: 600, marginBottom: 8 }}>Tags rápidas</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {REVIEW_TAGS.map((t) => (
              <button key={t} onClick={() => toggleTag(t)} style={{
                background: tags.includes(t) ? C.accentDim : 'transparent',
                border: `1px solid ${tags.includes(t) ? C.accent : C.border}`,
                color: tags.includes(t) ? C.accent : C.sub,
                borderRadius: 20, padding: '5px 12px', fontSize: 12, cursor: 'pointer',
                fontWeight: 600, fontFamily: C.font, transition: 'all 0.15s',
              }}>{t}</button>
            ))}
          </div>
        </div>
        <FInput label="Comentário (opcional, máx 300 caracteres)" placeholder="Conte como foi o serviço..."
          value={comment} onChange={(e) => setComment(e.target.value.slice(0, 300))} />
        {action.error && <Alert type="danger">{action.error}</Alert>}
        {action.success && <Alert type="success">Avaliação enviada! Obrigado.</Alert>}
        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
          <Btn variant="gold" full loading={action.loading} disabled={!rating || !!action.success} onClick={submit}>
            Enviar avaliação
          </Btn>
          <Btn variant="ghost" onClick={onClose}>Avaliar depois</Btn>
        </div>
      </div>
    </div>
  );
};

// Exibe QR Code PIX em modal
const PixModal = ({ job, payment, onClose, onPaid }) => {
  const [pix, setPix] = useState(payment || null);
  const [loading, setLoading] = useState(!payment);
  const [copied, setCopied] = useState(false);
  const confirmAction = useAction();
  const currentPaymentState = paymentState({
    status: pix?.status,
    paymentStatus: pix?.paymentStatus || job.paymentStatus,
    releaseStatus: pix?.releaseStatus || job.releaseStatus,
  });
  const paymentConfirmed = currentPaymentState === 'paid';
  const paymentTerminal = isTerminalPaymentState(currentPaymentState);

  useEffect(() => {
    if (payment) {
      setPix(payment);
      setLoading(false);
      return;
    }
    api.payments.createCharge(job.id)
      .then(setPix)
      .catch((e) => setPix({ error: e.message }))
      .finally(() => setLoading(false));
  }, [job.id, payment]);

  useEffect(() => {
    if (paymentTerminal || pix?.mock || pix?.error) return undefined;
    const timer = setInterval(async () => {
      try {
        const status = await api.payments.status(job.id);
        const nextState = paymentState({
          status: status.status,
          paymentStatus: status.job?.paymentStatus,
          releaseStatus: status.releaseStatus || status.job?.releaseStatus,
        });
        if (isTerminalPaymentState(nextState)) {
          const updatedPayment = withoutReusablePix(pix, {
            status: status.status,
            paymentStatus: status.job?.paymentStatus,
            paymentState: nextState,
            message: status.message,
            releaseStatus: status.releaseStatus || status.job?.releaseStatus,
          });
          setPix(updatedPayment);
          if (nextState === 'paid' && onPaid) {
            const fresh = await api.jobs.get(job.id);
            onPaid(fresh?.job || null, updatedPayment);
          }
        }
      } catch (_) {}
    }, 10000);
    return () => clearInterval(timer);
  }, [job.id, paymentTerminal, pix, onPaid]);

  const copy = () => {
    if (pix?.pixCode) {
      navigator.clipboard.writeText(pix.pixCode).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
    }
  };

  const simulatePayment = async () => {
    try {
      const result = await confirmAction.run(() => api.payments.mockConfirm(job.id));
      if (result?.payment) setPix(result.payment);
      if (onPaid) onPaid(result?.job || null, result?.payment || null);
    } catch (_) {}
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: 20 }}>
      <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 16, padding: 28, width: '100%', maxWidth: 440, textAlign: 'center' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div style={{ fontWeight: 800, fontSize: 18, color: C.accent }}>⚡ Pagar via PIX</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: C.sub, fontSize: 20, cursor: 'pointer' }}>✕</button>
        </div>
        <div style={{ fontWeight: 900, fontSize: 28, color: C.gold, marginBottom: 16 }}>{fmtMoney(job.price)}</div>
        {loading && <Spinner />}
        {pix?.error && <Alert type="danger">{pix.error}</Alert>}
        {pix?.pixQrCodeBase64 && (
          <img src={`data:image/png;base64,${pix.pixQrCodeBase64}`} alt="QR Code PIX"
            style={{ width: 200, height: 200, border: `4px solid ${C.accent}40`, borderRadius: 12, marginBottom: 16 }} />
        )}
        {!pix?.pixQrCodeBase64 && pix?.pixQrCodeUrl && (
          <img src={pix.pixQrCodeUrl} alt="QR Code PIX"
            style={{ width: 200, height: 200, border: `4px solid ${C.accent}40`, borderRadius: 12, marginBottom: 16, objectFit: 'cover' }} />
        )}
        {!pix?.pixQrCodeBase64 && !pix?.pixQrCodeUrl && pix?.mock && (
          <div style={{ width: 200, height: 200, margin: '0 auto 16px', borderRadius: 12, border: `2px dashed ${C.accent}`, background: C.accentDim, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: C.accent, fontWeight: 800 }}>
            <div style={{ fontSize: 18, letterSpacing: 2 }}>PIX MOCK</div>
            <div style={{ fontSize: 12, color: C.sub, marginTop: 8, maxWidth: 140 }}>Use o botao abaixo para simular a confirmacao.</div>
          </div>
        )}
        {pix?.pixCode && (
          <>
            <div
              onClick={copy}
              title="Clique para copiar o codigo PIX"
              style={{ background: C.card, border: `1px solid ${copied ? C.accent : C.border}`, borderRadius: 8, padding: '10px 14px', fontFamily: C.mono, fontSize: 11, color: copied ? C.accent : C.sub, wordBreak: 'break-all', marginBottom: 8, textAlign: 'left', cursor: 'pointer' }}
            >
              {pix.pixCode}
            </div>
            <div style={{ color: copied ? C.accent : C.muted, fontSize: 11, marginBottom: 12 }}>
              {copied ? 'Codigo PIX copiado.' : 'Clique no codigo ou no botao abaixo para copiar.'}
            </div>
            <Btn variant="primary" full onClick={copy}>{copied ? '✓ Copiado!' : 'Copiar código PIX'}</Btn>
          </>
        )}
        {pix?.mock && !paymentTerminal && (
          <div style={{ marginTop: 12 }}>
            <Btn variant="gold" full loading={confirmAction.loading} onClick={simulatePayment}>Simular pagamento PIX</Btn>
          </div>
        )}
        {confirmAction.error && <Alert type="danger">{confirmAction.error}</Alert>}
        {pix?.message && <Alert type={paymentConfirmed ? 'success' : ['refunded', 'expired'].includes(currentPaymentState) ? 'danger' : 'warn'}>{pix.message}</Alert>}
        <div style={{ fontSize: 12, color: C.muted, marginTop: 12 }}>
          {pix?.mock
            ? 'Modo mock ativo: use a simulacao para confirmar o pagamento localmente.'
            : 'O pagamento e confirmado automaticamente pelo webhook do Asaas.'}
        </div>
      </div>
    </div>
  );
};

const Alert = ({ type = 'info', children }) => {
  const colors = { info: C.info, warn: C.gold, danger: C.danger, success: C.accent };
  const icons = { info: 'ℹ', warn: '⚠', danger: '✕', success: '✓' };
  const c = colors[type];
  return (
    <div style={{
      background: `${c}15`, border: `1px solid ${c}40`, borderRadius: 8,
      padding: '10px 14px', color: c, fontSize: 13, display: 'flex',
      alignItems: 'center', gap: 8, marginBottom: 12,
    }}>
      <span style={{ fontWeight: 800, flexShrink: 0 }}>{icons[type]}</span>
      <span>{children}</span>
    </div>
  );
};

const Spinner = ({ size = 22 }) => (
  <div style={{
    width: size, height: size, border: `2px solid ${C.border}`,
    borderTopColor: C.accent, borderRadius: '50%',
    animation: 'spin 0.7s linear infinite', flexShrink: 0,
  }} />
);

const Stars = ({ rating = 0 }) => (
  <span style={{ color: C.gold, fontSize: 14, letterSpacing: 1 }}>
    {[1, 2, 3, 4, 5].map((i) => (
      <span key={i} style={{ opacity: i <= Math.round(rating) ? 1 : 0.25 }}>★</span>
    ))}
    <span style={{ color: C.sub, fontSize: 12, marginLeft: 4 }}>{rating ? Number(rating).toFixed(1) : '—'}</span>
  </span>
);

const TokenDisplay = ({ token, used }) => {
  const [copied, setCopied] = useState(false);
  const copy = () => { navigator.clipboard.writeText(token); setCopied(true); setTimeout(() => setCopied(false), 2000); };
  return (
    <div style={{ background: C.goldDim, border: `1px solid ${C.gold}40`, borderRadius: 10, padding: '14px 18px', marginTop: 8 }}>
      <div style={{ fontSize: 11, color: C.gold, fontWeight: 700, marginBottom: 6, letterSpacing: 1, textTransform: 'uppercase' }}>
        {used ? '✓ Token Usado' : 'Token de Conclusão'}
      </div>
      <div style={{ fontFamily: C.mono, fontSize: 22, fontWeight: 900, color: used ? C.muted : C.gold, letterSpacing: 3 }}>{token}</div>
      {!used && (
        <button onClick={copy} style={{
          marginTop: 8, background: 'transparent', border: `1px solid ${C.gold}40`,
          color: C.gold, borderRadius: 6, padding: '4px 12px', fontSize: 12, cursor: 'pointer', fontFamily: C.font,
        }}>
          {copied ? '✓ Copiado!' : 'Copiar'}
        </button>
      )}
    </div>
  );
};

const Section = ({ title, sub, children, headerContent, stickyContent, className = '', headerClassName = '', contentClassName = '' }) => (
  <div className={`qs-section ${className}`.trim()} style={FIXED_SECTION_WRAPPER_STYLE}>
    <div className={`qs-section-header ${headerClassName}`.trim()} style={FIXED_SECTION_HEADER_STYLE}>
      <div className="qs-section-inner" style={{ maxWidth: 960, margin: '0 auto' }}>
        <h2 className="qs-section-title" style={{ fontSize: 22, fontWeight: 800, color: C.text, marginBottom: sub ? 4 : 0 }}>{title}</h2>
        {sub && <p className="qs-section-subtitle" style={{ fontSize: 14, color: C.sub, marginTop: 2 }}>{sub}</p>}
        {headerContent && <div style={{ marginTop: 14 }}>{headerContent}</div>}
        {stickyContent && <div style={{ marginTop: 14 }}>{stickyContent}</div>}
      </div>
    </div>
    <div className={`qs-section-content ${contentClassName}`.trim()} style={FIXED_SECTION_CONTENT_STYLE}>
      <div className="qs-section-inner" style={{ maxWidth: 960, margin: '0 auto' }}>
        {children}
      </div>
    </div>
  </div>
);

const Divider = () => <div style={{ height: 1, background: C.border, margin: '16px 0' }} />;

const Metric = ({ label, value, color = C.accent, icon }) => (
  <Card style={{ flex: 1, minWidth: 160 }}>
    <div style={{ fontSize: 12, color: C.sub, fontWeight: 600, marginBottom: 8 }}>{icon} {label}</div>
    <div style={{ fontSize: 28, fontWeight: 900, color }}>{value}</div>
  </Card>
);

const Modal = ({ title, onClose, children }) => (
  <div
    style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
    onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
  >
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, padding: 24, width: '90%', maxWidth: 540, maxHeight: '80vh', overflowY: 'auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div style={{ fontWeight: 800, fontSize: 16, color: C.text }}>{title}</div>
        <button onClick={onClose} style={{ background: 'none', border: 'none', color: C.sub, fontSize: 22, cursor: 'pointer', lineHeight: 1 }}>✕</button>
      </div>
      {children}
    </div>
  </div>
);

// ━━━ SIDEBAR ━━━
const LIVENESS_STATUS_LABEL = {
  pending: 'Pendente',
  manual_review: 'Aguardando revisão humana',
  approved: 'Aprovada',
  rejected: 'Reprovada',
  expired: 'Expirada',
};

const cameraErrorMessage = (err) => {
  const name = err?.name || '';
  if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
    return 'O navegador bloqueou a abertura da camera antes de exibir a permissao. Isso nao significa que voce tenha negado; abra as configuracoes do site e libere Camera para o QServe.';
  }
  if (name === 'NotFoundError' || name === 'DevicesNotFoundError') {
    return 'Nenhuma camera foi encontrada neste aparelho.';
  }
  if (name === 'NotReadableError' || name === 'TrackStartError') {
    return 'A camera esta em uso por outro aplicativo. Feche outros apps e tente novamente.';
  }
  if (window.location.protocol !== 'https:' && window.location.hostname !== 'localhost') {
    return 'A camera so abre em conexao segura HTTPS.';
  }
  return err?.message || 'Nao foi possivel abrir a camera.';
};

export const LivenessCapture = ({ onComplete, compact = false }) => {
  const POLL_INTERVAL_MS = 260;
  const STABLE_FRAMES_REQUIRED = 4;
  const FACE_CENTER_TOLERANCE = 0.16;
  const MIN_FACE_WIDTH_RATIO = 0.12;
  const [challenge, setChallenge] = useState(null);
  const [status, setStatus] = useState('idle');
  const [stepIndex, setStepIndex] = useState(0);
  const [error, setError] = useState('');
  const [hint, setHint] = useState('Toque em iniciar e mantenha o rosto visivel.');
  const [metrics, setMetrics] = useState({ motion: 0, face: false, faceCount: 0 });
  const videoRef = useRef(null);
  const scanCanvasRef = useRef(null);
  const streamRef = useRef(null);
  const detectorRef = useRef(null);
  const lastFrameRef = useRef(null);
  const baselineRef = useRef(null);
  const completedRef = useRef([]);
  const startedAtRef = useRef(0);
  const stepStartedAtRef = useRef(0);
  const advancingRef = useRef(false);
  const startingRef = useRef(false);
  const submittingRef = useRef(false);
  const usedFaceDetectorRef = useRef(false);
  const singleFaceRef = useRef(true);
  const motionSumRef = useRef(0);
  const motionCountRef = useRef(0);
  const stableFrameCountRef = useRef(0);
  const fallbackSamplesRef = useRef(0);

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
  };

  useEffect(() => () => stopCamera(), []);

  useEffect(() => {
    stepStartedAtRef.current = Date.now();
  }, [stepIndex]);

  const resetRefs = () => {
    lastFrameRef.current = null;
    baselineRef.current = null;
    completedRef.current = [];
    advancingRef.current = false;
    submittingRef.current = false;
    usedFaceDetectorRef.current = false;
    singleFaceRef.current = true;
    motionSumRef.current = 0;
    motionCountRef.current = 0;
    stableFrameCountRef.current = 0;
    fallbackSamplesRef.current = 0;
  };

  const start = async () => {
    if (startingRef.current || submittingRef.current) return;
    setError('');
    if (!isMobileRegistrationDevice()) {
      setError(MOBILE_WORKER_REGISTRATION_MESSAGE);
      return;
    }
    if (!navigator.mediaDevices?.getUserMedia) {
      setError('Camera indisponivel neste navegador.');
      return;
    }
    startingRef.current = true;
    try {
      resetRefs();
      setStatus('starting');
      const stream = await requestUserCameraStream();
      streamRef.current = stream;
      const video = await waitForVideoElement(videoRef);
      await attachStreamToVideo(video, stream);
      const data = await api.liveness.start();
      if (!data?.challenge?.id || !data.challenge.token || !Array.isArray(data.challenge.actions) || data.challenge.actions.length === 0) {
        throw new Error('Nao foi possivel iniciar o desafio. Tente novamente.');
      }
      setChallenge(data.challenge);
      setStepIndex(0);
      startedAtRef.current = Date.now();
      stepStartedAtRef.current = Date.now();
      setStatus('running');
      setHint('Posicione o rosto no centro da camera.');
    } catch (err) {
      stopCamera();
      setStatus('idle');
      setError(cameraErrorMessage(err) || 'Nao foi possivel iniciar a prova de vida.');
    } finally {
      startingRef.current = false;
    }
  };

  const readMotion = (video) => {
    const canvas = scanCanvasRef.current;
    if (!canvas || !video?.videoWidth || !video?.videoHeight) return 0;
    const w = 96;
    const h = 72;
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0, w, h);
    const data = ctx.getImageData(0, 0, w, h).data;
    let motion = 0;
    let samples = 0;
    const prev = lastFrameRef.current;
    if (prev) {
      for (let i = 0; i < data.length; i += 16) {
        const diff =
          Math.abs(data[i] - prev[i]) +
          Math.abs(data[i + 1] - prev[i + 1]) +
          Math.abs(data[i + 2] - prev[i + 2]);
        motion += diff / 765;
        samples += 1;
      }
    }
    lastFrameRef.current = new Uint8ClampedArray(data);
    return samples ? motion / samples : 0;
  };

  const readFace = async (video) => {
    if (!window.FaceDetector || !video?.videoWidth || !video?.videoHeight) return null;
    try {
      if (!detectorRef.current) {
        detectorRef.current = new window.FaceDetector({ fastMode: true, maxDetectedFaces: 2 });
      }
      const faces = await detectorRef.current.detect(video);
      usedFaceDetectorRef.current = true;
      if (faces.length > 1) singleFaceRef.current = false;
      const face = faces[0];
      if (!face?.boundingBox) return { count: faces.length, found: false };
      const box = face.boundingBox;
      return {
        count: faces.length,
        found: faces.length === 1,
        centerX: (box.x + box.width / 2) / video.videoWidth,
        centerY: (box.y + box.height / 2) / video.videoHeight,
        widthRatio: box.width / video.videoWidth,
      };
    } catch (_) {
      return null;
    }
  };

  const submit = async () => {
    if (submittingRef.current || !challenge) return;
    submittingRef.current = true;
    setStatus('submitting');
    setHint('Enviando validacao...');
    try {
      const frame = await captureVideoFrame(videoRef.current);
      const motionAvg = motionCountRef.current ? motionSumRef.current / motionCountRef.current : 0;
      const signals = {
        challengeToken: challenge.token,
        clientPassed: true,
        actions: completedRef.current,
        totalElapsedMs: Date.now() - startedAtRef.current,
        usedFaceDetector: usedFaceDetectorRef.current,
        singleFace: singleFaceRef.current,
        motionScoreAvg: Number(motionAvg.toFixed(4)),
        viewport: { width: window.innerWidth, height: window.innerHeight },
        userAgent: navigator.userAgent,
      };
      const fd = new FormData();
      fd.append('clientPassed', 'true');
      fd.append('signals', JSON.stringify(signals));
      fd.append('frame', frame, `liveness_${Date.now()}.jpg`);
      const result = await api.liveness.submit(challenge.id, fd);
      stopCamera();
      const resultStatus = result?.liveness?.status;
      setStatus(resultStatus === 'manual_review' ? 'manual_review' : 'rejected');
      setHint(resultStatus === 'manual_review' ? 'Etapas registradas. Aguarde revisão humana da imagem.' : 'Prova de vida não concluída.');
      if (onComplete) onComplete(result);
    } catch (err) {
      stopCamera();
      setStatus('rejected');
      setError(err.message || 'Nao foi possivel concluir a prova de vida.');
    } finally {
      submittingRef.current = false;
    }
  };

  const advanceStep = (action, face, motion) => {
    if (advancingRef.current || submittingRef.current) return;
    advancingRef.current = true;
    completedRef.current = [
      ...completedRef.current,
      {
        action: action.key,
        passed: true,
        elapsedMs: Date.now() - stepStartedAtRef.current,
        motion: Number(motion.toFixed(4)),
        face: face ? {
          found: Boolean(face.found),
          centerX: Number((face.centerX || 0).toFixed(4)),
          centerY: Number((face.centerY || 0).toFixed(4)),
          widthRatio: Number((face.widthRatio || 0).toFixed(4)),
          count: face.count || 0,
        } : null,
        at: new Date().toISOString(),
      },
    ];

    if (stepIndex + 1 >= (challenge?.actions || []).length) {
      setHint('Perfeito. Capturando imagem...');
      submit();
      return;
    }
    setHint('Etapa validada. Prepare a proxima.');
    setTimeout(() => {
      setStepIndex((idx) => idx + 1);
      advancingRef.current = false;
    }, 650);
  };

  useEffect(() => {
    if (status !== 'running' || !challenge) return undefined;
    const timer = setInterval(async () => {
      const expiresAt = Date.parse(challenge.expiresAt);
      if (Number.isFinite(expiresAt) && Date.now() >= expiresAt) {
        stopCamera();
        setStatus('rejected');
        setError('O tempo da prova de vida expirou. Inicie novamente.');
        return;
      }
      const video = videoRef.current;
      if (!video || video.readyState < 2 || !video.videoWidth || !video.videoHeight) return;
      const action = challenge.actions?.[stepIndex];
      if (!action) return;

      const motion = readMotion(video);
      motionSumRef.current += motion;
      motionCountRef.current += 1;
      const face = await readFace(video);
      const elapsed = Date.now() - stepStartedAtRef.current;
      const baseline = baselineRef.current;
      const hasFace = face ? face.found : true;
      const faceCentered = Boolean(face?.found &&
        Math.abs(face.centerX - 0.5) <= FACE_CENTER_TOLERANCE &&
        Math.abs(face.centerY - 0.5) <= FACE_CENTER_TOLERANCE &&
        face.widthRatio >= MIN_FACE_WIDTH_RATIO);
      const qualityOk = face ? (face.count === 1 && faceCentered) : motionCountRef.current >= STABLE_FRAMES_REQUIRED;
      if (!face) fallbackSamplesRef.current += 1;
      const stable = qualityOk && (face ? faceCentered : fallbackSamplesRef.current >= STABLE_FRAMES_REQUIRED);
      stableFrameCountRef.current = stable ? stableFrameCountRef.current + 1 : 0;
      const stableNow = stableFrameCountRef.current >= STABLE_FRAMES_REQUIRED;

      setMetrics({ motion, face: hasFace, faceCount: face?.count || 0 });

      let passed = false;
      let nextHint = action.instruction || action.label;
      if (action.key === 'center_face') {
        passed = elapsed > 950 && stableNow;
        if (passed) {
          baselineRef.current = face?.found
            ? { centerX: face.centerX, centerY: face.centerY, widthRatio: face.widthRatio }
            : { centerX: 0.5, centerY: 0.5, widthRatio: 0.34 };
        }
      } else if (action.key === 'turn_left') {
        passed = elapsed > 950 && stableNow && (
          (face?.found && baseline && face.centerX < baseline.centerX - 0.055) ||
          motion > 0.05
        );
      } else if (action.key === 'turn_right') {
        passed = elapsed > 950 && stableNow && (
          (face?.found && baseline && face.centerX > baseline.centerX + 0.055) ||
          motion > 0.05
        );
      } else if (action.key === 'move_closer') {
        passed = elapsed > 950 && stableNow && (
          (face?.found && baseline && face.widthRatio > baseline.widthRatio * 1.1) ||
          motion > 0.055
        );
      } else if (action.key === 'look_up') {
        passed = elapsed > 950 && stableNow && (
          (face?.found && baseline && face.centerY < baseline.centerY - 0.045) ||
          motion > 0.045
        );
      }

      if (!hasFace) nextHint = 'Posicione seu rosto no centro.';
      else if (face?.count > 1) nextHint = 'Mantenha apenas um rosto visivel.';
      else if (face && !faceCentered) nextHint = 'Posicione seu rosto no centro.';
      else if (!stableNow) nextHint = 'Mantenha o rosto parado...';
      setHint(nextHint);
      if (passed) advanceStep(action, face, motion);
    }, POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [status, challenge, stepIndex]);

  const current = challenge?.actions?.[stepIndex];
  const progress = challenge?.actions?.length ? ((stepIndex + (status === 'approved' ? 1 : 0)) / challenge.actions.length) * 100 : 0;
  const statusColor = status === 'approved' ? C.accent : status === 'rejected' ? C.danger : C.info;

  return (
    <Card style={{ padding: compact ? 14 : 18, marginBottom: compact ? 12 : 18 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start', marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: compact ? 14 : 16, fontWeight: 800, color: C.text }}>Prova de vida</div>
          <div style={{ fontSize: 12, color: C.sub, marginTop: 3 }}>Camera frontal, desafio aleatorio e registro de auditoria.</div>
        </div>
        <Chip type={status === 'approved' ? 'approved' : status === 'rejected' ? 'rejected' : 'pending'} label={status === 'idle' ? 'Nao iniciada' : LIVENESS_STATUS_LABEL[status] || status} />
      </div>

      {error && <Alert type="danger">{error}</Alert>}

      {(status === 'running' || status === 'starting' || status === 'submitting') && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ position: 'relative', overflow: 'hidden', borderRadius: 12, border: `1px solid ${C.border}`, background: C.surface, aspectRatio: '3 / 4', maxHeight: compact ? 360 : 460 }}>
            <video ref={videoRef} autoPlay playsInline muted
              style={{ width: '100%', height: '100%', objectFit: 'cover', transform: 'scaleX(-1)', display: 'block' }} />
            <div style={{ position: 'absolute', inset: '12%', border: `2px solid ${statusColor}90`, borderRadius: '50%', pointerEvents: 'none' }} />
            <div style={{ position: 'absolute', left: 12, right: 12, bottom: 12, background: 'rgba(15,18,24,0.82)', border: `1px solid ${C.border}`, borderRadius: 10, padding: 12 }}>
              <div style={{ fontSize: 12, color: C.sub, marginBottom: 4 }}>Etapa {Math.min(stepIndex + 1, challenge?.actions?.length || 1)} de {challenge?.actions?.length || 1}</div>
              <div style={{ fontSize: 18, fontWeight: 900, color: C.text }}>{current?.label || 'Preparando...'}</div>
              <div style={{ fontSize: 12, color: C.sub, marginTop: 4 }}>{status === 'submitting' ? 'Enviando para validacao...' : hint}</div>
            </div>
          </div>
          <canvas ref={scanCanvasRef} style={{ display: 'none' }} />
          <div style={{ height: 6, borderRadius: 99, overflow: 'hidden', background: C.border, marginTop: 10 }}>
            <div style={{ width: `${Math.min(100, progress)}%`, height: '100%', background: C.accent, transition: 'width 0.2s' }} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginTop: 8, fontSize: 11, color: C.muted }}>
            <span>Face: {metrics.face ? 'ok' : 'aguardando'}</span>
            <span>Movimento: {(metrics.motion * 100).toFixed(1)}%</span>
          </div>
        </div>
      )}

      {status === 'approved' && <Alert type="success">Prova de vida aprovada e registrada.</Alert>}
      {status === 'manual_review' && <Alert type="info">Etapas registradas. A evidência aguarda revisão humana; nenhuma aprovação automática foi concedida.</Alert>}
      {status === 'rejected' && <Alert type="warn">A prova nao foi aprovada. Reposicione o rosto e tente novamente.</Alert>}

      {(status === 'idle' || status === 'rejected') && (
        <Btn type="button" variant="primary" full onClick={start}>Iniciar prova de vida</Btn>
      )}
      {status === 'starting' && <Btn type="button" variant="primary" full loading>Preparando camera</Btn>}
      {status === 'submitting' && <Btn type="button" variant="primary" full loading>Validando</Btn>}
    </Card>
  );
};

const navItems = {
  admin: [
    { id: 'partners',   label: 'Parceiros',        icon: '+' },
    { id: 'partnerperformance', label: 'Desempenho das Unidades', icon: '▥' },
    { id: 'dash',       label: 'Dashboard',       icon: '⬡' },
    { id: 'jobs',       label: 'Vagas',            icon: '◈' },
    { id: 'clients',    label: 'Clientes',         icon: '◎' },
    { id: 'workers',    label: 'Colaboradores',    icon: '◉' },
    { id: 'pricetable', label: 'Tabela de Preços', icon: '◆' },
    { id: 'finance',    label: 'Financeiro',       icon: '💰' },
    { id: 'withdrawals',label: 'Saques',           icon: '◀' },
    { id: 'promotions', label: 'Promoções',        icon: '🎯' },
    { id: 'accounting', label: 'Contabilidade',     icon: '📊' },
    { id: 'reviews',    label: 'Avaliações',       icon: '★' },
    { id: 'settings',   label: 'Configurações',    icon: '⊙' },
  ],
  client: [
    { id: 'request', label: 'Solicitar Serviço', icon: '✦' },
    { id: 'myjobs',  label: 'Minhas Vagas',      icon: '◈' },
    { id: 'profile', label: 'Perfil',            icon: '◎' },
  ],
  worker: [
    { id: 'available', label: 'Vagas Disponíveis', icon: '◈' },
    { id: 'mywork',    label: 'Meu Trabalho',      icon: '◉' },
    { id: 'wallet',    label: 'Carteira',          icon: '◆' },
    { id: 'documents', label: 'Meus Documentos',  icon: '📄' },
    { id: 'wprofile',  label: 'Meu Perfil',        icon: '◎' },
  ],
  partner: [
    { id: 'partnerdash', label: 'Minha Regiao', icon: '+' },
    { id: 'partnerjobs', label: 'Servicos',     icon: '#' },
    { id: 'partnerclients', label: 'Clientes', icon: '◎' },
    { id: 'partnerworkers', label: 'Colaboradores', icon: '@' },
  ],
};

const Sidebar = ({ view, setView, user, logout }) => {
  const items = navItems[user.role] || [];
  const rc = roleColor[user.role];
  const activeClass = user.role === 'admin' ? 'qs-nav-active-gold' : user.role === 'worker' ? 'qs-nav-active-info' : 'qs-nav-active';
  return (
    <>
      {/* Sidebar fixa */}
      <div className="qs-sidebar" style={{ width: 240, background: C.surface, borderRight: `1px solid ${C.border}`, display: 'flex', flexDirection: 'column', height: '100vh', position: 'fixed', top: 0, left: 0, zIndex: 100, overflow: 'hidden' }}>
        {/* Logo + info usuário */}
        <div className="qs-sidebar-header" style={{ padding: '20px 20px 16px', borderBottom: `1px solid ${C.border}`, flexShrink: 0 }}>
          <div className="qs-sidebar-brand" style={{ fontWeight: 900, fontSize: 22, letterSpacing: -0.5, marginBottom: 10 }}>
            <span style={{ color: rc }}>+</span><span style={{ color: C.text }}>Qserve</span>
          </div>
          <div className="qs-sidebar-user" style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
            <Avatar name={user.name} size={28} color={rc} src={user.profilePhoto} />
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.name}</div>
              <div style={{ fontSize: 10, color: C.sub, textTransform: 'capitalize' }}>{user.role}</div>
            </div>
          </div>
        </div>
        {/* Menu — rola internamente se houver muitos itens */}
        <nav className="qs-sidebar-nav" style={{ flex: 1, padding: '12px 0', overflowY: 'auto' }}>
          {items.map((item) => (
            <div
              key={item.id}
              className={`qs-nav-entry ${view === item.id ? activeClass : 'qs-nav-item'}`}
              onClick={() => setView(item.id)}
              style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 20px', cursor: 'pointer', fontSize: 14, fontWeight: 600, color: view === item.id ? undefined : C.sub, borderLeft: '3px solid transparent', transition: 'all 0.15s' }}
            >
              <span style={{ fontSize: 16 }}>{item.icon}</span>
              {item.label}
            </div>
          ))}
        </nav>
        {/* Botão Sair — fixo no rodapé */}
        <div className="qs-sidebar-footer" style={{ padding: '12px 16px', borderTop: `1px solid ${C.border}`, flexShrink: 0 }}>
          <button className="qs-sidebar-logout" onClick={logout} style={{ width: '100%', background: C.dangerDim, border: `1px solid ${C.danger}40`, color: C.danger, borderRadius: 8, padding: '10px 0', fontSize: 13, cursor: 'pointer', fontWeight: 700, fontFamily: C.font, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
            ⏻ Sair
          </button>
        </div>
      </div>
      {/* Espaçador para compensar a sidebar fixa */}
      <div className="qs-sidebar-spacer" style={{ width: 240, flexShrink: 0 }} />
    </>
  );
};

// ━━━ LOGIN / REGISTER ━━━
const LoginPage = ({ initialTab = 'login', onGoLanding, referralSlug = '', referralAudience = 'worker', workerReferralId = '' }) => {
  const { login, register, loginWithGoogleSession, refreshUser } = useAuth();
  const [mode, setMode] = useState(initialTab);
  const referralRole = referralAudience === 'client' ? 'client' : 'worker';
  const [form, setForm] = useState({ name: '', email: '', password: '', role: referralSlug ? referralRole : 'client', phone: '', cpf: '' });
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotMsg, setForgotMsg] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);

  // Worker-specific
  const [wForm, setWForm] = useState({ areas: [], yearsExperience: '', description: '', serviceRadius: '', certifications: '', cpf: '', consent: false,
    street: '', number: '', zip: '', city: '', state: '' });
  const [wDocs, setWDocs] = useState({});
  const [registered, setRegistered] = useState(false);
  const [awaitingLiveness, setAwaitingLiveness] = useState(false);
  const [referralPartner, setReferralPartner] = useState(null);
  const [referralLoading, setReferralLoading] = useState(false);
  const [conductTerm, setConductTerm] = useState(null);
  const [conductAccepted, setConductAccepted] = useState(false);
  const [showConductTerm, setShowConductTerm] = useState(false);
  const oauthHandledRef = useRef(false);

  useEffect(() => {
    api.terms.conduct()
      .then((response) => setConductTerm(response.term || null))
      .catch((err) => setError(err.message || 'Não foi possível carregar as normas de conduta.'));
  }, []);

  useEffect(() => {
    const slug = String(referralSlug || '').trim();
    if (!slug) return;

    setMode('register');
    setForm((f) => ({ ...f, role: referralRole }));
    setReferralLoading(true);
    setError('');

    api.partners.referral(slug)
      .then((data) => setReferralPartner(data.partner || null))
      .catch((err) => {
        setReferralPartner(null);
        setError(err.message || 'Parceiro de indicacao invalido ou inativo.');
      })
      .finally(() => setReferralLoading(false));
  }, [referralSlug, referralRole]);

  useEffect(() => {
    if (!workerReferralId) return;
    setMode('register');
    setForm((f) => ({ ...f, role: 'client' }));
  }, [workerReferralId]);

  useEffect(() => {
    if (oauthHandledRef.current) return;

    const params = new URLSearchParams(window.location.search);
    const session = params.get('oauth_session');
    const oauthError = params.get('oauth_error');
    if (!session && !oauthError) return;
    oauthHandledRef.current = true;

    const cleanUrl = `${window.location.origin}/login`;
    window.history.replaceState({}, '', cleanUrl);

    if (oauthError) {
      setError(oauthError === 'existing_account'
        ? 'Esta conta ja existe. Entre com seu login atual; a vinculacao com Google ainda nao esta disponivel.'
        : 'Nao foi possivel concluir o login com Google. Tente novamente.');
      return;
    }

    setLoading(true);
    setError('');
    loginWithGoogleSession(session)
      .catch((err) => setError(err.message || 'Sessao Google invalida ou expirada.'))
      .finally(() => setLoading(false));
  }, [loginWithGoogleSession]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setW = (k) => (e) => setWForm((f) => ({ ...f, [k]: e.target.value }));
  const toggleArea = (area) => setWForm((f) => ({
    ...f,
    areas: f.areas.includes(area) ? f.areas.filter((a) => a !== area) : [...f.areas, area],
  }));
  const setDoc = (type) => (e) => {
    if (type === 'selfie' && !isMobileRegistrationDevice()) {
      e.target.value = '';
      setError(MOBILE_WORKER_REGISTRATION_MESSAGE);
      return;
    }
    const file = e.target.files[0];
    if (file) setWDocs((d) => ({ ...d, [type]: file }));
  };

  const isWorker = form.role === 'worker' && mode === 'register';
  const workerMobileBlocked = isWorker && !isMobileRegistrationDevice();

  const handle = async (e) => {
    e.preventDefault();
    setError('');
    if (mode === 'register' && (!conductAccepted || !conductTerm?.version)) {
      return setError('Leia e aceite as normas de conduta para continuar.');
    }
    if (mode === 'register' && form.role === 'client') {
      if (!form.cpf || form.cpf.replace(/\D/g, '').length !== 11) return setError('Informe um CPF válido com 11 dígitos.');
    }
    if (mode === 'register' && referralSlug) {
      if (referralLoading) return setError('Aguarde a validacao do parceiro.');
      if (!referralPartner) return setError('Link de parceiro invalido ou inativo.');
    }
    if (isWorker) {
      if (workerMobileBlocked) return setError(MOBILE_WORKER_REGISTRATION_MESSAGE);
      if (!form.name || !form.email || !form.password || !form.phone) return setError('Preencha todos os dados pessoais.');
      if (!wForm.cpf.trim()) return setError('Informe seu CPF.');
      if (!wForm.consent) return setError('É necessário autorizar a consulta de antecedentes para continuar.');
      if (wForm.areas.length === 0) return setError('Selecione ao menos uma área de atuação.');
      if (!wForm.yearsExperience) return setError('Informe os anos de experiência.');
      if (!wForm.description.trim()) return setError('Adicione uma descrição profissional.');
      if (!wForm.serviceRadius) return setError('Informe o raio de atendimento em km.');
      if ((wForm.city.trim() || wForm.state.trim()) &&
          (!wForm.city.trim() || !/^[A-Za-z]{2}$/.test(wForm.state.trim()))) {
        return setError('Informe cidade e UF juntas para sua base de atendimento.');
      }
      if (wForm.zip && wForm.zip.replace(/\D/g, '').length !== 8) {
        return setError('O CEP da base deve ter 8 dígitos.');
      }
      const missing = WORKER_REGISTRATION_DOCUMENT_TYPES.find((dt) => !wDocs[dt.key]);
      if (missing) return setError(`Envie o documento obrigatório: ${missing.label}`);
    }
    setLoading(true);
    try {
      if (mode === 'login') {
        await login(form.email, form.password);
      } else if (form.role === 'client') {
        await register({
          name: form.name,
          email: form.email,
          password: form.password,
          role: 'client',
          phone: form.phone,
          cpf: form.cpf,
          partnerSlug: referralPartner?.slug || referralSlug || undefined,
          referredByWorkerId: workerReferralId || undefined,
          conductAccepted,
          conductTermVersion: conductTerm.version,
        });
      } else {
        // Worker: register → upload docs → success screen
        const workerForm = new FormData();
        workerForm.append('name', form.name);
        workerForm.append('email', form.email);
        workerForm.append('password', form.password);
        workerForm.append('phone', form.phone);
        workerForm.append('cpf', wForm.cpf);
        workerForm.append('backgroundCheckConsent', String(wForm.consent));
        workerForm.append('conductAccepted', String(conductAccepted));
        workerForm.append('conductTermVersion', conductTerm.version);
        workerForm.append('areas', JSON.stringify(wForm.areas));
        workerForm.append('yearsExperience', String(parseInt(wForm.yearsExperience) || 0));
        workerForm.append('description', wForm.description);
        workerForm.append('serviceRadius', String(parseInt(wForm.serviceRadius) || 0));
        ['street', 'number', 'zip', 'city', 'state'].forEach((field) => workerForm.append(field, wForm[field].trim()));
        workerForm.append('certifications', wForm.certifications || '');
        if (referralPartner?.slug || referralSlug) {
          workerForm.append('partnerSlug', referralPartner?.slug || referralSlug);
        }
        WORKER_REGISTRATION_DOCUMENT_TYPES.forEach((dt) => {
          workerForm.append(dt.key, wDocs[dt.key]);
        });
        const regData = await api.auth.registerWorker(workerForm);
        localStorage.setItem('qserve_token', regData.token);
        localStorage.setItem('qserve_user', JSON.stringify(regData.user));
        setAwaitingLiveness(true);
        setLoading(false);
        return;
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const enterApp = async () => {
    setLoading(true);
    try { await refreshUser(); } catch { /* ignore */ } finally { setLoading(false); }
  };

  const quickLogin = async (email, password) => {
    setLoading(true); setError('');
    try { await login(email, password); } catch (err) { setError(err.message); } finally { setLoading(false); }
  };

  const startGoogleLogin = async () => {
    setLoading(true);
    setError('');
    try {
      if (mode === 'register' && (!conductAccepted || !conductTerm?.version)) {
        throw new Error('Leia e aceite as normas de conduta para continuar.');
      }
      const status = await api.auth.googleStatus();
      if (!status.enabled) throw new Error('Login com Google ainda nao configurado no servidor.');
      window.location.assign(api.auth.googleStartUrl({ mode, role: form.role, partnerSlug: referralPartner?.slug || referralSlug, workerReferralId, conductAccepted, conductTermVersion: conductTerm?.version || '' }));
    } catch (err) {
      setError(err.message || 'Nao foi possivel iniciar login com Google.');
      setLoading(false);
    }
  };

  const demos = [
    { label: '⚡ Admin', email: 'admin@qserve.com', password: 'admin123', color: C.gold },
    { label: '👤 Cliente', email: 'maria@email.com', password: 'cliente123', color: C.accent },
    { label: '🔧 Worker', email: 'carlos@email.com', password: 'colab123', color: C.info },
  ];

  const handleForgot = async (e) => {
    e.preventDefault();
    setForgotLoading(true); setForgotMsg('');
    try {
      const res = await api.auth.forgotPassword(forgotEmail);
      setForgotMsg(res.message);
    } catch (err) { setForgotMsg(err.message); }
    finally { setForgotLoading(false); }
  };

  if (forgotMode) {
    return (
      <div style={{ display: 'flex', minHeight: '100vh', background: C.bg, alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ maxWidth: 420, width: '90%' }} className="qs-fade">
          <div style={{ fontWeight: 900, fontSize: 28, color: C.text, marginBottom: 8 }}>Redefinir senha</div>
          <p style={{ color: C.sub, fontSize: 14, marginBottom: 24 }}>Informe seu e-mail e enviaremos um link de redefinição.</p>
          {forgotMsg
            ? <><Alert type="success">{forgotMsg}</Alert><div style={{ marginTop: 16 }}><Btn variant="ghost" full onClick={() => { setForgotMode(false); setForgotMsg(''); setForgotEmail(''); }}>Voltar ao login</Btn></div></>
            : (
              <form onSubmit={handleForgot}>
                <FInput label="E-mail" type="email" placeholder="seu@email.com" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} icon="✉" />
                <Btn variant="primary" full loading={forgotLoading}>Enviar link</Btn>
                <div style={{ marginTop: 12 }}><Btn variant="ghost" full onClick={() => setForgotMode(false)}>Voltar</Btn></div>
              </form>
            )
          }
        </div>
      </div>
    );
  }

  // ── Tela de sucesso após cadastro de worker ──
  if (awaitingLiveness) {
    return (
      <div style={{ display: 'flex', minHeight: '100vh', background: C.bg, alignItems: 'center', justifyContent: 'center', padding: 18 }}>
        <div style={{ maxWidth: 460, width: '100%' }} className="qs-fade">
          <div style={{ fontWeight: 900, fontSize: 26, color: C.text, marginBottom: 8 }}>Finalize sua prova de vida</div>
          <p style={{ color: C.sub, fontSize: 14, lineHeight: 1.5, marginBottom: 16 }}>
            Seus dados e documentos foram recebidos. Agora conclua o desafio pela camera frontal para liberar a analise do administrador.
          </p>
          <LivenessCapture onComplete={(result) => {
            if (!['manual_review', 'approved'].includes(result?.liveness?.status)) return;
            setAwaitingLiveness(false);
            setRegistered(true);
          }} />
        </div>
      </div>
    );
  }

  if (registered) {
    return (
      <div style={{ display: 'flex', minHeight: '100vh', background: C.bg, alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ maxWidth: 460, width: '90%', textAlign: 'center' }} className="qs-fade">
          <div style={{ width: 80, height: 80, borderRadius: '50%', background: C.accentDim, border: `2px solid ${C.accent}50`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 38, margin: '0 auto 24px' }}>✓</div>
          <div style={{ fontWeight: 900, fontSize: 26, color: C.text, marginBottom: 10 }}>Cadastro enviado!</div>
          <p style={{ fontSize: 15, color: C.sub, lineHeight: 1.6, marginBottom: 28 }}>
            Seus documentos e sua prova de vida foram recebidos com sucesso.<br />
            Aguarde a aprovação do administrador para acessar as vagas disponíveis.
          </p>
          <Card style={{ marginBottom: 24, textAlign: 'left' }}>
            <div style={{ fontSize: 11, color: C.sub, fontWeight: 700, marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>Documentos enviados</div>
            {WORKER_REGISTRATION_DOCUMENT_TYPES.map((dt) => (
              <div key={dt.key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '9px 0', borderBottom: `1px solid ${C.border}` }}>
                <span style={{ color: C.text, fontSize: 13 }}>{dt.label}</span>
                <span style={{ color: C.accent, fontWeight: 700, fontSize: 12 }}>✓ Enviado</span>
              </div>
            ))}
          </Card>
          <Btn variant="primary" full loading={loading} onClick={enterApp}>Acessar minha conta</Btn>
        </div>
      </div>
    );
  }

  const SectionLabel = ({ children }) => (
    <div style={{ fontSize: 11, color: C.accent, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', margin: '20px 0 12px', paddingBottom: 6, borderBottom: `1px solid ${C.border}` }}>
      {children}
    </div>
  );

  const taStyle = { width: '100%', background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, padding: '10px 12px', color: C.text, fontSize: 14, fontFamily: C.font, resize: 'vertical', boxSizing: 'border-box' };

  return (
    <div className="qs-login-shell" style={{ display: 'flex', height: '100vh', overflow: 'hidden', background: C.bg }}>
      {/* Left panel */}
      <div className="qs-login-left" style={{ width: '40%', background: C.surface, borderRight: `1px solid ${C.border}`, display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '48px 40px', flexShrink: 0, overflowY: 'auto' }}>
        <div className="qs-login-intro" style={{ marginBottom: 40 }}>
          {onGoLanding && (
            <button onClick={onGoLanding} style={{ background: 'none', border: 'none', color: C.sub, fontSize: 13, cursor: 'pointer', fontFamily: C.font, padding: 0, marginBottom: 20, display: 'flex', alignItems: 'center', gap: 6 }}>
              ← Voltar ao início
            </button>
          )}
          <div className="qs-login-title" style={{ fontWeight: 900, fontSize: 36, letterSpacing: -1, marginBottom: 8 }}>
            <span style={{ color: C.accent }}>+</span><span style={{ color: C.text }}>Qserve</span>
          </div>
          <p style={{ color: C.sub, fontSize: 16, lineHeight: 1.5 }}>Conectamos você aos melhores profissionais de serviços da sua região.</p>
        </div>
        <div className="qs-login-features">
        {[
          { icon: '🔒', title: 'Pagamento Seguro', desc: 'Token de conclusão garante que o serviço foi realizado antes do repasse.' },
          { icon: '⭐', title: 'Profissionais Verificados', desc: 'Todos os colaboradores passam por processo de validação documental.' },
          { icon: '⚡', title: 'Acompanhamento em Tempo Real', desc: 'Monitore o status do seu serviço a qualquer momento.' },
        ].map((f) => (
          <div key={f.title} style={{ display: 'flex', gap: 14, marginBottom: 24 }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: C.accentDim, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>{f.icon}</div>
            <div><div style={{ fontWeight: 700, color: C.text, marginBottom: 3 }}>{f.title}</div><div style={{ fontSize: 13, color: C.sub, lineHeight: 1.4 }}>{f.desc}</div></div>
          </div>
        ))}
        </div>
      </div>
      {/* Right panel — scrollable para formulário longo */}
      <div className="qs-login-right" style={{ flex: 1, display: 'flex', alignItems: isWorker ? 'flex-start' : 'center', justifyContent: 'center', padding: '40px', overflowY: 'auto' }}>
        <div style={{ width: '100%', maxWidth: 420 }} className="qs-login-card qs-fade">
          <div style={{ display: 'flex', background: C.surface, borderRadius: 10, padding: 4, marginBottom: 28, border: `1px solid ${C.border}` }}>
            {['login', 'register'].map((m) => (
              <button key={m} onClick={() => setMode(m)} style={{ flex: 1, padding: '9px 0', borderRadius: 7, border: 'none', background: mode === m ? C.card : 'transparent', color: mode === m ? C.text : C.sub, fontWeight: 700, fontSize: 14, cursor: 'pointer', fontFamily: C.font, transition: 'all 0.15s' }}>
                {m === 'login' ? 'Entrar' : 'Cadastrar'}
              </button>
            ))}
          </div>
          {!isWorker && (
            <>
              <button type="button" onClick={startGoogleLogin} disabled={loading} style={{
                width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                background: '#fff', color: '#151922', border: `1px solid ${C.border}`, borderRadius: 10,
                padding: '11px 14px', fontWeight: 800, fontSize: 14, cursor: loading ? 'default' : 'pointer',
                fontFamily: C.font, marginBottom: 14, opacity: loading ? 0.7 : 1,
              }}>
                <span style={{ fontWeight: 900, color: '#4285F4' }}>G</span>
                {mode === 'login' ? 'Continuar com Google' : 'Cadastrar cliente com Google'}
              </button>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                <div style={{ flex: 1, height: 1, background: C.border }} />
                <span style={{ color: C.muted, fontSize: 11, fontWeight: 700 }}>OU</span>
                <div style={{ flex: 1, height: 1, background: C.border }} />
              </div>
            </>
          )}
          {error && <Alert type="danger">{error}</Alert>}
          {referralSlug && !error && (
            <Alert type={referralPartner ? 'success' : 'info'}>
              {referralLoading
                ? 'Validando parceiro de origem...'
                : referralPartner
                  ? `${referralAudience === 'client' ? 'Cliente captado por' : 'Cadastro vinculado a'} ${referralPartner.name}${referralPartner.city || referralPartner.state ? ` - ${[referralPartner.city, referralPartner.state].filter(Boolean).join('/')}` : ''}.`
                  : `Informe seus dados para concluir o cadastro de ${referralAudience === 'client' ? 'cliente' : 'colaborador'} indicado.`}
            </Alert>
          )}
          {workerReferralId && !error && (
            <Alert type="info">Cliente indicado por colaborador. Crie sua conta para solicitar servicos com seguranca pela plataforma.</Alert>
          )}
          {workerMobileBlocked && <Alert type="warn">{MOBILE_WORKER_REGISTRATION_MESSAGE}</Alert>}
          <form onSubmit={handle}>
            {mode === 'register' && (
              <>
                <FInput label="Nome completo" placeholder="Seu nome" value={form.name} onChange={set('name')} icon="👤" />
                <FInput label="Telefone" placeholder="(11) 99999-0000" value={form.phone} onChange={set('phone')} icon="📱" />
                {form.role === 'client' && (
                  <FInput label="CPF *" placeholder="000.000.000-00" value={form.cpf}
                    onChange={(e) => {
                      const d = e.target.value.replace(/\D/g, '').slice(0, 11);
                      const masked = d.length <= 3 ? d : d.length <= 6 ? `${d.slice(0,3)}.${d.slice(3)}` : d.length <= 9 ? `${d.slice(0,3)}.${d.slice(3,6)}.${d.slice(6)}` : `${d.slice(0,3)}.${d.slice(3,6)}.${d.slice(6,9)}-${d.slice(9)}`;
                      setForm((f) => ({ ...f, cpf: masked }));
                    }}
                    icon="🪪" />
                )}
                <div style={{ marginBottom: 14 }}>
                  <label style={{ display: 'block', fontSize: 12, color: C.sub, marginBottom: 5, fontWeight: 600 }}>Perfil</label>
                  <select value={form.role} onChange={set('role')} disabled={Boolean(referralSlug || workerReferralId)} style={{ width: '100%', background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, padding: '10px 12px', color: C.text, fontSize: 14, fontFamily: C.font, opacity: (referralSlug || workerReferralId) ? 0.75 : 1 }}>
                    <option value="client">Cliente</option>
                    <option value="worker">Colaborador</option>
                  </select>
                </div>

                {/* ── Campos exclusivos do Colaborador ── */}
                {isWorker && (
                  <>
                    <SectionLabel>Dados Pessoais Complementares</SectionLabel>
                    <FInput label="CPF *" placeholder="000.000.000-00" value={wForm.cpf} onChange={setW('cpf')} icon="🪪" />
                    <div style={{ marginBottom: 16, padding: '12px 14px', background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8 }}>
                      <label style={{ display: 'flex', gap: 10, cursor: 'pointer', alignItems: 'flex-start' }}>
                        <input type="checkbox" checked={wForm.consent} onChange={(e) => setWForm((f) => ({ ...f, consent: e.target.checked }))}
                          style={{ marginTop: 2, flexShrink: 0, accentColor: C.accent, width: 16, height: 16 }} />
                        <span style={{ fontSize: 13, color: C.sub, lineHeight: 1.5 }}>
                          Autorizo a plataforma a realizar consulta de antecedentes criminais em meu CPF, conforme exigido para cadastro de colaboradores.
                          <span style={{ color: C.danger }}> *</span>
                        </span>
                      </label>
                    </div>

                    <SectionLabel>Dados Profissionais</SectionLabel>

                    <div style={{ marginBottom: 14 }}>
                      <label style={{ display: 'block', fontSize: 12, color: C.sub, marginBottom: 8, fontWeight: 600 }}>
                        Áreas de atuação <span style={{ color: C.danger }}>*</span>
                      </label>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {AREAS.map((area) => (
                          <button key={area} type="button" onClick={() => toggleArea(area)} style={{
                            padding: '5px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600,
                            cursor: 'pointer', fontFamily: C.font, transition: 'all 0.15s',
                            background: wForm.areas.includes(area) ? C.infoDim : 'transparent',
                            color: wForm.areas.includes(area) ? C.info : C.sub,
                            border: `1px solid ${wForm.areas.includes(area) ? C.info : C.border}`,
                          }}>{area}</button>
                        ))}
                      </div>
                    </div>

                    <div className="qs-compact-row" style={{ display: 'flex', gap: 10 }}>
                      <div style={{ flex: 1 }}><FInput label="Anos de experiência *" type="number" placeholder="Ex: 3" value={wForm.yearsExperience} onChange={setW('yearsExperience')} /></div>
                      <div style={{ flex: 1 }}><FInput label="Raio de atendimento (km) *" type="number" placeholder="Ex: 20" value={wForm.serviceRadius} onChange={setW('serviceRadius')} /></div>
                    </div>

                    <SectionLabel>Base de atendimento</SectionLabel>
                    <FInput label="Rua" value={wForm.street} onChange={setW('street')} placeholder="Nome da rua" />
                    <div className="qs-compact-row" style={{ display: 'flex', gap: 10 }}>
                      <div style={{ flex: 1 }}><FInput label="Número" value={wForm.number} onChange={setW('number')} placeholder="123" /></div>
                      <div style={{ flex: 1 }}><FInput label="CEP" value={wForm.zip} onChange={setW('zip')} placeholder="00000-000" /></div>
                    </div>
                    <div className="qs-compact-row" style={{ display: 'flex', gap: 10 }}>
                      <div style={{ flex: 2 }}><FInput label="Cidade" value={wForm.city} onChange={setW('city')} placeholder="Sua cidade" /></div>
                      <div style={{ flex: 1 }}><FInput label="UF" value={wForm.state} onChange={(e) => setWForm((f) => ({ ...f, state: e.target.value.toUpperCase() }))} placeholder="SP" /></div>
                    </div>
                    <p style={{ fontSize: 12, color: C.sub, marginBottom: 16 }}>A base é opcional no cadastro. Para ver vagas presenciais, informe cidade e UF no perfil. Rua, número e CEP permitem calcular o raio quando houver geocodificação. Seu endereço completo não aparece para clientes.</p>

                    <div style={{ marginBottom: 14 }}>
                      <label style={{ display: 'block', fontSize: 12, color: C.sub, marginBottom: 5, fontWeight: 600 }}>
                        Descrição profissional <span style={{ color: C.danger }}>*</span>
                      </label>
                      <textarea value={wForm.description} onChange={setW('description')} rows={3}
                        placeholder="Descreva sua experiência e diferenciais..." style={taStyle} />
                    </div>

                    <div style={{ marginBottom: 14 }}>
                      <label style={{ display: 'block', fontSize: 12, color: C.sub, marginBottom: 5, fontWeight: 600 }}>Certificações (opcional)</label>
                      <textarea value={wForm.certifications} onChange={setW('certifications')} rows={2}
                        placeholder="Ex: NR10, CREA, cursos realizados..." style={taStyle} />
                    </div>

                    <SectionLabel>Documentos Obrigatórios</SectionLabel>
                    <Alert type="info">Envie RG, CPF e comprovante de endereco. A selfie sera capturada automaticamente na prova de vida pela camera frontal.</Alert>

                    {WORKER_REGISTRATION_DOCUMENT_TYPES.map((dt) => (
                      <div key={dt.key} style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        padding: '10px 12px', borderRadius: 8, marginBottom: 8,
                        background: wDocs[dt.key] ? C.accentDim : C.surface,
                        border: `1px solid ${wDocs[dt.key] ? C.accent + '50' : C.border}`,
                      }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: wDocs[dt.key] ? C.accent : C.text }}>{dt.label}</div>
                          <div style={{ fontSize: 11, marginTop: 2, color: wDocs[dt.key] ? C.accent : C.muted }}>
                            {wDocs[dt.key] ? `✓ ${wDocs[dt.key].name}` : 'Não enviado'}
                          </div>
                        </div>
                        <label style={{
                          background: wDocs[dt.key] ? C.accentDim : C.infoDim,
                          color: wDocs[dt.key] ? C.accent : C.info,
                          border: `1px solid ${wDocs[dt.key] ? C.accent + '40' : C.info + '40'}`,
                          borderRadius: 8, padding: '6px 14px', fontSize: 12, fontWeight: 700,
                          cursor: 'pointer', fontFamily: C.font, flexShrink: 0, marginLeft: 10,
                        }}>
                          {wDocs[dt.key] ? '↑ Trocar' : '↑ Enviar'}
                          <input
                            type="file"
                            accept="image/*,.pdf"
                            style={{ display: 'none' }}
                            onChange={setDoc(dt.key)}
                          />
                        </label>
                      </div>
                    ))}
                  </>
                )}
              </>
            )}
            <FInput label="E-mail" type="email" placeholder="seu@email.com" value={form.email} onChange={set('email')} icon="✉" />
            <FInput label="Senha" type={showPass ? 'text' : 'password'} placeholder="••••••••" value={form.password} onChange={set('password')} icon="🔑"
              extra={<button type="button" onClick={() => setShowPass(!showPass)} style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: C.sub, cursor: 'pointer', fontSize: 16 }}>{showPass ? '🙈' : '👁'}</button>}
            />
            {mode === 'register' && (
              <div style={{ marginBottom: 16, padding: '12px 14px', background: C.surface, border: `1px solid ${conductAccepted ? C.accent + '70' : C.border}`, borderRadius: 8 }}>
                <label style={{ display: 'flex', gap: 10, cursor: 'pointer', alignItems: 'flex-start' }}>
                  <input type="checkbox" checked={conductAccepted} onChange={(event) => setConductAccepted(event.target.checked)} style={{ marginTop: 2, flexShrink: 0, accentColor: C.accent, width: 16, height: 16 }} />
                  <span style={{ fontSize: 13, color: C.sub, lineHeight: 1.5 }}>
                    Li e aceito as normas de conduta da QServe. O comprovante será arquivado e enviado automaticamente ao meu e-mail. <span style={{ color: C.danger }}>*</span>
                  </span>
                </label>
                <button type="button" onClick={() => setShowConductTerm((value) => !value)} style={{ marginTop: 8, padding: 0, background: 'none', border: 'none', color: C.accent, cursor: 'pointer', fontSize: 12, fontWeight: 700, fontFamily: C.font }}>
                  {showConductTerm ? 'Ocultar normas' : `Ler normas completas${conductTerm?.version ? ` - versão ${conductTerm.version}` : ''}`}
                </button>
                {showConductTerm && (
                  <div style={{ marginTop: 10, maxHeight: 220, overflowY: 'auto', padding: 12, borderRadius: 8, background: C.bg, border: `1px solid ${C.border}` }}>
                    <div style={{ color: C.text, fontWeight: 800, fontSize: 13, marginBottom: 8 }}>{conductTerm?.title || 'Carregando normas...'}</div>
                    {(conductTerm?.content || '').split('\n').filter(Boolean).map((line) => <div key={line} style={{ color: C.sub, fontSize: 12, lineHeight: 1.55, marginBottom: 6 }}>• {line}</div>)}
                  </div>
                )}
              </div>
            )}
            <Btn variant="primary" full loading={loading} disabled={workerMobileBlocked}>{mode === 'login' ? 'Entrar na plataforma' : isWorker ? 'Enviar cadastro' : 'Criar conta'}</Btn>
          </form>
          {mode === 'login' && (
            <div style={{ textAlign: 'center', marginTop: 16 }}>
              <button type="button" onClick={() => setForgotMode(true)} style={{ background: 'none', border: 'none', color: C.accent, fontSize: 13, cursor: 'pointer', fontFamily: C.font, textDecoration: 'underline' }}>
                Esqueceu sua senha?
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ━━━ ADMIN SCREENS ━━━

const ResetPasswordPage = ({ onGoLogin }) => {
  const token = new URLSearchParams(window.location.search).get('token') || '';
  const [form, setForm] = useState({ password: '', confirm: '' });
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (key) => (event) => setForm((prev) => ({ ...prev, [key]: event.target.value }));

  const submit = async (event) => {
    event.preventDefault();
    setMessage('');
    setError('');
    if (!token) return setError('Link de redefinicao invalido ou incompleto.');
    if (form.password.length < 8) return setError('A nova senha deve ter pelo menos 8 caracteres.');
    if (form.password !== form.confirm) return setError('As senhas informadas nao coincidem.');

    setLoading(true);
    try {
      const response = await api.auth.resetPassword(token, form.password);
      setDone(true);
      setMessage(response?.message || 'Senha redefinida com sucesso.');
    } catch (err) {
      setError(err.message || 'Nao foi possivel redefinir a senha.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: C.bg, alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ maxWidth: 420, width: '90%' }} className="qs-fade">
        <div style={{ fontWeight: 900, fontSize: 28, color: C.text, marginBottom: 8 }}>Nova senha</div>
        <p style={{ color: C.sub, fontSize: 14, marginBottom: 24 }}>Informe uma senha nova para recuperar o acesso.</p>
        {message && <Alert type="success">{message}</Alert>}
        {error && <Alert type="danger">{error}</Alert>}
        {done ? (
          <Btn variant="primary" full onClick={onGoLogin}>Voltar ao login</Btn>
        ) : (
          <form onSubmit={submit}>
            <FInput label="Nova senha" type="password" placeholder="minimo 8 caracteres" value={form.password} onChange={set('password')} />
            <FInput label="Confirmar senha" type="password" placeholder="repita a nova senha" value={form.confirm} onChange={set('confirm')} />
            <Btn variant="primary" full loading={loading}>Salvar nova senha</Btn>
            <div style={{ marginTop: 12 }}>
              <Btn type="button" variant="ghost" full onClick={onGoLogin}>Voltar ao login</Btn>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

const AdminDash = () => {
  const { data, loading } = useAdminDashboard();
  if (loading) return <Section title="Dashboard"><Spinner /></Section>;
  const d = data || {};
  const m = d.metrics || {};
  const fmt = (n) => n != null ? Number(n).toLocaleString('pt-BR', { minimumFractionDigits: 2 }) : '0,00';

  const metrics = (
    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
      <Metric label="Vagas Abertas" value={m.openJobs || 0} color={C.accent} icon="◈" />
      <Metric label="Concluídas" value={m.doneJobs || 0} color={C.gold} icon="✓" />
      <Metric label="Workers Pendentes" value={m.pendingWorkers || 0} color={C.danger} icon="◉" />
      <Metric label="GMV Total" value={`R$ ${fmt(m.gmv)}`} color={C.gold} icon="◆" />
      <Metric label="Receita da Plataforma" value={`R$ ${fmt(m.revenue)}`} color={C.accent} icon="◆" />
      <Metric label="Comissão vigente" value={currentCommissionLabel(m.commissionRate)} color={C.info} icon="%" />
    </div>
  );

  return (
    <Section title="Dashboard" sub="Visão geral da plataforma" stickyContent={metrics}>
      {(m.pendingWorkers || 0) > 0 && <Alert type="warn">{m.pendingWorkers} colaborador(es) aguardando aprovação.</Alert>}
      {(m.pendingReleasePayments || 0) > 0 && <Alert type="warn">{m.pendingReleasePayments} pagamentos aguardando liberacao.</Alert>}
      <h3 style={{ color: C.text, fontWeight: 700, marginBottom: 12 }}>Vagas Recentes</h3>
      {(d.recentJobs || []).map((j) => (
        <Card key={j.id} style={{ marginBottom: 10 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <span style={{ fontWeight: 700, color: C.text }}>{j.category}</span>
              {j.subcategory && <span style={{ color: C.sub, fontSize: 13 }}> / {j.subcategory}</span>}
              <div style={{ fontSize: 12, color: C.sub, marginTop: 2 }}>
                Cliente: {j.client?.name || '—'} | Worker: {j.worker?.name || '—'}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <span style={{ fontWeight: 700, color: C.gold }}>R$ {Number(j.price).toFixed(2)}</span>
              <Chip type={j.status} />
            </div>
          </div>
        </Card>
      ))}
    </Section>
  );
};

const AdminJobs = () => {
  const [statusFilter, setStatusFilter] = useState('');
  const { data, loading, refetch } = useAdminJobs(statusFilter);
  const action = useAction();
  const statuses = ['', 'open', 'accepted', 'in_progress', 'done', 'cancelled'];

  const deleteJob = async (id) => {
    if (!window.confirm('Deseja excluir esta vaga concluída? Esta ação não pode ser desfeita.')) return;
    await action.run(() => api.admin.deleteJob(id));
    refetch();
  };

  const filters = (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
      {statuses.map((s) => (
        <button key={s} onClick={() => setStatusFilter(s)} style={{ padding: '6px 14px', borderRadius: 6, border: `1px solid ${statusFilter === s ? C.accent : C.border}`, background: statusFilter === s ? C.accentDim : 'transparent', color: statusFilter === s ? C.accent : C.sub, cursor: 'pointer', fontWeight: 600, fontSize: 13, fontFamily: C.font }}>
          {s === '' ? 'Todas' : chipLabels[s] || s}
        </button>
      ))}
    </div>
  );

  return (
    <Section title="Vagas" sub="Todas as vagas da plataforma" headerContent={filters}>
      {action.error && <Alert type="danger">{action.error}</Alert>}
      {loading ? <Spinner /> : (data?.jobs || []).map((j) => (
        <Card key={j.id} style={{ marginBottom: 10 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <span style={{ fontWeight: 700, color: C.text }}>{j.category}</span>
              {j.subcategory && <span style={{ color: C.sub }}> / {j.subcategory}</span>}
              <div style={{ fontSize: 12, color: C.sub, marginTop: 4 }}>
                Cliente: {j.client?.name} | Worker: {j.worker?.name || 'Sem worker'}
              </div>
              <div style={{ fontSize: 12, color: C.sub }}>{j.city}, {j.state}</div>
              {j.token && <div style={{ fontFamily: C.mono, fontSize: 13, color: j.tokenUsed ? C.muted : C.gold, marginTop: 4 }}>Token: {j.token} {j.tokenUsed ? '(usado)' : ''}</div>}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-end' }}>
              <span style={{ fontWeight: 800, color: C.gold, fontSize: 16 }}>{fmtMoney(j.price)}</span>
              <Chip type={j.status} />
              <Chip type={j.paymentMethod} />
              {j.status === 'done' && (
                <Btn variant="danger" small loading={action.loading} onClick={() => deleteJob(j.id)}>Excluir</Btn>
              )}
            </div>
          </div>
        </Card>
      ))}
    </Section>
  );
};

const AdminClients = () => {
  const { data, loading, refetch } = useAdminUsers('client');
  const action = useAction();
  const [selectedUser, setSelectedUser] = useState(null);
  const [resetResult, setResetResult] = useState(null);
  const [resetLoading, setResetLoading] = useState(false);

  const deactivate = async (id) => {
    if (!window.confirm('Desativar este usuário?')) return;
    await action.run(() => api.admin.deactivateUser(id));
    refetch();
  };

  const deleteUser = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir este cadastro? Esta ação não pode ser desfeita.')) return;
    await action.run(() => api.admin.deleteUser(id));
    refetch();
  };

  const handleResetPassword = async (id) => {
    if (!window.confirm('Redefinir senha deste usuário?')) return;
    setResetLoading(true); setResetResult(null);
    try {
      const res = await api.admin.resetUserPassword(id);
      setResetResult(res);
    } catch (err) { alert(err.message); }
    finally { setResetLoading(false); }
  };

  const maskCpf = (cpf) => {
    if (!cpf) return '—';
    const d = cpf.replace(/\D/g, '');
    if (d.length !== 11) return cpf;
    return `${d.slice(0,3)}.${d.slice(3,6)}.${d.slice(6,9)}-${d.slice(9)}`;
  };

  return (
    <Section title="Clientes" sub="Gestão de clientes cadastrados">
      {action.error && <Alert type="danger">{action.error}</Alert>}
      {resetResult && (
        <Alert type="warn">
          <strong>Senha temporária gerada para {resetResult.userEmail}:</strong>
          <span style={{ fontFamily: C.mono, color: C.gold, fontWeight: 700, marginLeft: 8 }}>{resetResult.tempPassword}</span>
          <button onClick={() => setResetResult(null)} style={{ marginLeft: 12, background: 'none', border: 'none', color: C.sub, cursor: 'pointer' }}>✕</button>
        </Alert>
      )}
      {loading ? <Spinner /> : (data?.users || []).map((u) => (
        <Card key={u.id} style={{ marginBottom: 10 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
              <Avatar name={u.name} color={C.accent} />
              <div>
                <div style={{ fontWeight: 700, color: C.text }}>{u.name}</div>
                <div style={{ fontSize: 12, color: C.sub }}>{u.email}</div>
                <div style={{ fontSize: 12, color: C.sub }}>CPF: {maskCpf(u.cpf)} | Tel: {u.phone || 'Não informado'}</div>
                <div style={{ fontSize: 12, color: C.sub }}>Vagas: {u.clientJobs || 0} | Gasto: R$ {Number(u.totalSpent || 0).toFixed(2)}</div>
                {u.lastLoginAt && <div style={{ fontSize: 11, color: C.muted }}>Último acesso: {new Date(u.lastLoginAt).toLocaleString('pt-BR')}</div>}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              <Chip type={u.active ? 'active' : 'cancelled'} label={u.active ? 'Ativo' : 'Inativo'} />
              <Btn variant="gold" small loading={resetLoading} onClick={() => handleResetPassword(u.id)}>🔑 Senha</Btn>
              {u.active && <Btn variant="danger" small onClick={() => deactivate(u.id)}>Desativar</Btn>}
              <Btn variant="danger" small onClick={() => deleteUser(u.id)}>Excluir</Btn>
            </div>
          </div>
        </Card>
      ))}
    </Section>
  );
};

// ── Worker Photo (circular, lazy-loaded from selfie doc) ──
const resolveDocumentUrl = (doc) => {
  if (!doc) return null;
  if (doc.url) return doc.url;
  return resolveAssetUrl(doc.filePath);
};

const WorkerPhoto = ({ workerId, name, size = 42, color = C.info, profilePhoto }) => {
  const [photoSource, setPhotoSource] = useState(resolveAssetUrl(profilePhoto));
  const photoUrl = useProtectedAssetUrl(photoSource);
  useEffect(() => {
    const direct = resolveAssetUrl(profilePhoto);
    if (direct) {
      setPhotoSource(direct);
      return;
    }
    api.documents.adminList(workerId)
      .then(({ documents }) => {
        const selfie = (documents || []).find((d) => d.documentType === 'selfie');
        setPhotoSource(resolveDocumentUrl(selfie));
      })
      .catch(() => {});
  }, [workerId, profilePhoto]);

  if (photoUrl) {
    return (
      <img src={photoUrl} alt={name} onError={() => setPhotoSource(null)}
        style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover',
          border: `2px solid ${color}40`, flexShrink: 0 }} />
    );
  }
  return <Avatar name={name} size={size} color={color} />;
};

// ── Worker Docs Modal ──
const WorkerDocsModal = ({ worker, onClose }) => {
  const { data, loading, refetch } = useFetch(() => api.documents.adminList(worker.id), [worker.id]);
  const action = useAction();
  const [lightboxUrl, setLightboxUrl] = useState(null);

  const updateStatus = async (docId, status) => {
    await action.run(() => api.documents.updateStatus(docId, status));
    refetch();
  };

  const statusColor = { pending: C.gold, approved: C.accent, rejected: C.danger };
  const statusLabel = { pending: 'Em análise', approved: 'Aprovado', rejected: 'Rejeitado' };

  return (
    <>
    {lightboxUrl && (
      <div
        onClick={() => setLightboxUrl(null)}
        style={{ position: 'fixed', inset: 0, background: '#000000F0', zIndex: 2000,
          display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'zoom-out' }}>
        <img src={lightboxUrl} alt="Documento" onClick={(e) => e.stopPropagation()}
          style={{ maxWidth: '90vw', maxHeight: '90vh', objectFit: 'contain', borderRadius: 8, boxShadow: '0 0 60px #000' }} />
        <span onClick={() => setLightboxUrl(null)}
          style={{ position: 'fixed', top: 20, right: 24, color: '#fff', fontSize: 32,
            cursor: 'pointer', fontWeight: 700, lineHeight: 1, userSelect: 'none', textShadow: '0 2px 8px #000' }}>✕</span>
      </div>
    )}
    <Modal title={`Documentos — ${worker.name}`} onClose={onClose}>
      {action.error && <Alert type="danger">{action.error}</Alert>}
      {loading ? <Spinner /> : DOCUMENT_TYPES.map((dt) => {
        const doc = (data?.documents || []).find((d) => d.documentType === dt.key);
        const docUrl = resolveDocumentUrl(doc);
        const isPdf = !!doc?.isPdf || (docUrl || '').toLowerCase().endsWith('.pdf');
        return (
          <div key={dt.key} style={{ paddingBottom: 14, marginBottom: 14, borderBottom: `1px solid ${C.border}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontWeight: 600, color: C.text, fontSize: 14 }}>{dt.label}</div>
                {doc ? (
                  <>
                    <div style={{ fontSize: 12, marginTop: 3, color: statusColor[doc.status] }}>
                      {statusLabel[doc.status]} · {new Date(doc.uploadedAt).toLocaleDateString('pt-BR')}
                    </div>
                    {docUrl && (
                      <div style={{ marginTop: 6, marginBottom: 6 }}>
                        <Chip label={doc.status === 'pending' ? 'PENDENTE' : doc.status === 'approved' ? 'APROVADO' : 'REPROVADO'} type={doc.status} />
                      </div>
                    )}
                    {docUrl && <ProtectedAsset source={docUrl}>{(viewUrl) => viewUrl && (<>
                      <div style={{
                        width: 150, height: 100, marginTop: 8, borderRadius: 8, overflow: 'hidden',
                        border: `1px solid ${C.border}`, background: C.surface,
                      }}>
                        {isPdf ? (
                          <iframe title={`${dt.label}-preview`} src={viewUrl} style={{ width: '100%', height: '100%', border: 'none', background: '#fff' }} />
                        ) : (
                          <img src={viewUrl} alt={dt.label} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        )}
                      </div>
                      <a href={viewUrl} target="_blank" rel="noopener noreferrer"
                        style={{ fontSize: 11, color: C.info, textDecoration: 'none', fontWeight: 600, display: 'inline-block', marginTop: 4 }}>
                        📄 Visualizar ↗
                      </a>
                      <div style={{ marginTop: 6 }}>
                        <Btn
                          variant="ghost"
                          small
                          onClick={() => isPdf
                            ? window.open(viewUrl, '_blank', 'noopener,noreferrer')
                            : setLightboxUrl(viewUrl)
                          }
                        >
                          {isPdf ? 'Abrir PDF' : 'Ampliar'}
                        </Btn>
                      </div>
                    </>)}</ProtectedAsset>}
                  </>
                ) : (
                  <div style={{ fontSize: 12, color: C.muted, marginTop: 3 }}>Não enviado</div>
                )}
              </div>
              {doc && doc.status !== 'approved' && (
                <div style={{ display: 'flex', gap: 6 }}>
                  <Btn variant="primary" small loading={action.loading} onClick={() => updateStatus(doc.id, 'approved')}>Aprovar</Btn>
                  {doc.status === 'pending' && <Btn variant="danger" small loading={action.loading} onClick={() => updateStatus(doc.id, 'rejected')}>Rejeitar</Btn>}
                </div>
              )}
              {doc && doc.status === 'approved' && (
                <Btn variant="danger" small loading={action.loading} onClick={() => updateStatus(doc.id, 'rejected')}>Reprovar</Btn>
              )}
            </div>
          </div>
        );
      })}
    </Modal>
    </>
  );
};

// ── Worker Profile Modal (ficha completa) ──
const workerRegistrationComplete = (worker) => Boolean(worker?.workerRegistration?.complete);

const workerRegistrationPendingText = (worker) => {
  const reg = worker?.workerRegistration || {};
  const pending = [];
  if (Array.isArray(reg.missingDocuments) && reg.missingDocuments.length > 0) {
    pending.push(`Faltam: ${reg.missingDocuments.map(documentTypeLabel).join(', ')}`);
  }
  if (Array.isArray(reg.rejectedDocuments) && reg.rejectedDocuments.length > 0) {
    pending.push(`Reprovados: ${reg.rejectedDocuments.map(documentTypeLabel).join(', ')}`);
  }
  if (!reg.livenessApproved) pending.push('Prova de vida pendente');
  return pending.join(' | ');
};

const WorkerProfileModal = ({ worker, onClose, onSetStatus, onReview, actionLoading }) => {
  const { data, loading } = useFetch(() => api.documents.adminList(worker.id), [worker.id]);
  const { data: livenessData, loading: livenessLoading } = useFetch(() => api.liveness.adminStatus(worker.id), [worker.id]);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewError, setReviewError] = useState('');
  const selfieDoc = (data?.documents || []).find((d) => d.documentType === 'selfie');
  const selfieSource = resolveAssetUrl(worker.profilePhoto) || resolveDocumentUrl(selfieDoc);
  const selfieUrl = useProtectedAssetUrl(selfieSource);
  const [photoErr, setPhotoErr] = useState(false);
  const dsColor = { pending: C.gold, approved: C.accent, rejected: C.danger };
  const dsLabel = { pending: 'CADASTRO EM ANÁLISE', approved: 'CADASTRO APROVADO', rejected: 'CADASTRO REPROVADO' };
  const latestLiveness = livenessData?.checks?.[0];
  const evidenceUrl = useProtectedAssetUrl(latestLiveness?.evidenceUrl);
  const livenessNeedsReview = latestLiveness && (latestLiveness.status === 'manual_review' || (latestLiveness.status === 'approved' && !latestLiveness.reviewedAt));
  const reviewLiveness = async (decision) => {
    if (!window.confirm('Você revisou visualmente a imagem e deseja registrar esta decisão?')) return;
    setReviewLoading(true); setReviewError('');
    try {
      await api.liveness.review(worker.id, latestLiveness.id, decision);
      if (onReview) await onReview();
      onClose();
    } catch (err) { setReviewError(err.message); }
    finally { setReviewLoading(false); }
  };

  // Background check state
  const [bgStatus, setBgStatus] = useState(worker.backgroundCheckStatus || 'not_checked');
  const [bgNotes, setBgNotes] = useState(worker.backgroundCheckNotes || '');
  const [bgLoading, setBgLoading] = useState(false);
  const [bgMsg, setBgMsg] = useState('');
  const bgColor = { not_checked: C.sub, pending: C.gold, approved: C.accent, rejected: C.danger };
  const bgLabel = { not_checked: 'Não consultado', pending: 'Pendente', approved: 'Aprovado', rejected: 'Reprovado' };

  const saveBackgroundCheck = async () => {
    setBgLoading(true); setBgMsg('');
    try {
      await api.admin.updateBackgroundCheck(worker.id, bgStatus, bgNotes);
      setBgMsg('Atualizado com sucesso.');
    } catch (err) { setBgMsg(err.message); }
    finally { setBgLoading(false); }
  };

  return (
    <Modal title={`Ficha — ${worker.name}`} onClose={onClose}>
      {/* Foto de perfil */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 20 }}>
        {loading ? <Spinner size={72} /> : (selfieUrl && !photoErr) ? (
          <img src={selfieUrl} alt={worker.name} onError={() => setPhotoErr(true)}
            style={{ width: 120, height: 120, borderRadius: '50%', objectFit: 'cover',
              border: `3px solid ${C.info}60`, flexShrink: 0 }} />
        ) : (
          <Avatar name={worker.name} size={120} color={C.info} />
        )}
        <div style={{ fontWeight: 800, fontSize: 18, color: C.text, marginTop: 12 }}>{worker.name}</div>
        <div style={{ fontSize: 13, color: C.sub }}>{worker.email}</div>
        <div style={{ marginTop: 6 }}><Stars rating={worker.workerRating} /></div>
        <div style={{ marginTop: 8 }}>
          <span style={{ fontWeight: 800, fontSize: 12, color: dsColor[worker.docStatus] || C.sub }}>
            ● {dsLabel[worker.docStatus] || 'CADASTRO EM ANÁLISE'}
          </span>
        </div>
      </div>
      <Divider />
      {/* Dados */}
      {[
        { label: 'Telefone', value: worker.phone || 'Não informado' },
        { label: 'Serviços realizados', value: worker.totalJobs || 0 },
        { label: 'Total ganho', value: `R$ ${Number(worker.totalEarned || 0).toFixed(2)}` },
        { label: 'Saldo atual', value: `R$ ${Number(worker.balance || 0).toFixed(2)}` },
        { label: 'Anos de experiência', value: worker.yearsExperience ? `${worker.yearsExperience} anos` : '—' },
        { label: 'Raio de atendimento', value: worker.serviceRadius ? `${worker.serviceRadius} km` : '—' },
      ].map((r) => (
        <div key={r.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '9px 0', borderBottom: `1px solid ${C.border}` }}>
          <span style={{ color: C.sub, fontSize: 13 }}>{r.label}</span>
          <span style={{ fontWeight: 700, color: C.text, fontSize: 13 }}>{r.value}</span>
        </div>
      ))}
      {/* Áreas */}
      {(worker.areas || []).length > 0 && (
        <div style={{ marginTop: 14 }}>
          <div style={{ fontSize: 12, color: C.sub, fontWeight: 600, marginBottom: 8 }}>Áreas de atuação</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {worker.areas.map((a) => (
              <span key={a} style={{ background: C.infoDim, color: C.info, border: `1px solid ${C.info}30`, borderRadius: 6, padding: '3px 10px', fontSize: 12, fontWeight: 600 }}>{a}</span>
            ))}
          </div>
        </div>
      )}
      {/* Certificações */}
      {worker.certifications && (
        <div style={{ marginTop: 14 }}>
          <div style={{ fontSize: 12, color: C.sub, fontWeight: 600, marginBottom: 4 }}>Certificações</div>
          <div style={{ fontSize: 13, color: C.text }}>{worker.certifications}</div>
        </div>
      )}
      {/* Descrição */}
      {worker.description && (
        <div style={{ marginTop: 14 }}>
          <div style={{ fontSize: 12, color: C.sub, fontWeight: 600, marginBottom: 4 }}>Descrição profissional</div>
          <div style={{ fontSize: 13, color: C.text, lineHeight: 1.5 }}>{worker.description}</div>
        </div>
      )}
      <Divider />
      <div style={{ marginBottom: 4 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 10 }}>Prova de vida</div>
        {livenessLoading ? (
          <Spinner size={18} />
        ) : latestLiveness ? (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: `1px solid ${C.border}` }}>
              <span style={{ fontSize: 13, color: C.sub }}>Status</span>
              <span style={{ fontWeight: 700, fontSize: 13, color: latestLiveness.status === 'approved' && latestLiveness.reviewedAt ? C.accent : latestLiveness.status === 'rejected' ? C.danger : C.gold }}>
                {livenessNeedsReview ? 'Revisão humana necessária' : LIVENESS_STATUS_LABEL[latestLiveness.status] || latestLiveness.status}
              </span>
            </div>
            {latestLiveness.completedAt && (
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: `1px solid ${C.border}` }}>
                <span style={{ fontSize: 13, color: C.sub }}>Concluida em</span>
                <span style={{ fontWeight: 600, fontSize: 13, color: C.text }}>{new Date(latestLiveness.completedAt).toLocaleString('pt-BR')}</span>
              </div>
            )}
            {latestLiveness.failureReason && (
              <div style={{ fontSize: 12, color: C.danger, marginTop: 8 }}>{latestLiveness.failureReason}</div>
            )}
            {evidenceUrl && (
              <img src={evidenceUrl} alt="Prova de vida"
                style={{ width: '100%', maxHeight: 220, objectFit: 'cover', borderRadius: 10, border: `1px solid ${C.border}`, marginTop: 10 }} />
            )}
            {livenessNeedsReview && latestLiveness.evidenceUrl && (
              <div style={{ marginTop: 10 }}>
                <Alert type="warn">Os sinais do dispositivo não comprovam vida ou identidade. Examine a imagem antes de decidir.</Alert>
                {reviewError && <Alert type="danger">{reviewError}</Alert>}
                <div style={{ display: 'flex', gap: 8 }}>
                  <Btn variant="primary" loading={reviewLoading} onClick={() => reviewLiveness('approved')}>Aprovar evidência</Btn>
                  <Btn variant="danger" loading={reviewLoading} onClick={() => reviewLiveness('rejected')}>Rejeitar evidência</Btn>
                </div>
              </div>
            )}
          </>
        ) : (
          <Alert type="warn">Prova de vida ainda nao realizada.</Alert>
        )}
      </div>
      <Divider />
      {/* Verificação de Antecedentes */}
      <div style={{ marginBottom: 4 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 10 }}>Verificação de Antecedentes</div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: `1px solid ${C.border}` }}>
          <span style={{ fontSize: 13, color: C.sub }}>Status</span>
          <span style={{ fontWeight: 700, fontSize: 13, color: bgColor[worker.backgroundCheckStatus || 'not_checked'] }}>
            {bgLabel[worker.backgroundCheckStatus || 'not_checked']}
          </span>
        </div>
        {worker.backgroundCheckDate && (
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: `1px solid ${C.border}` }}>
            <span style={{ fontSize: 13, color: C.sub }}>Última consulta</span>
            <span style={{ fontWeight: 600, fontSize: 13, color: C.text }}>{new Date(worker.backgroundCheckDate).toLocaleDateString('pt-BR')}</span>
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: `1px solid ${C.border}` }}>
          <span style={{ fontSize: 13, color: C.sub }}>Consentimento</span>
          <span style={{ fontWeight: 700, fontSize: 13, color: worker.backgroundCheckConsent ? C.accent : C.danger }}>
            {worker.backgroundCheckConsent ? '✓ Autorizado' : '✕ Não autorizado'}
          </span>
        </div>
        {worker.backgroundCheckNotes && (
          <div style={{ marginTop: 8, fontSize: 13, color: C.text }}><span style={{ color: C.sub, fontWeight: 600 }}>Notas: </span>{worker.backgroundCheckNotes}</div>
        )}
        <div style={{ marginTop: 12 }}>
          <Btn variant="ghost" full onClick={() => setBgMsg('Integração com serviço de antecedentes em desenvolvimento.')}>
            Consultar Antecedentes Externos
          </Btn>
        </div>
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 12, color: C.sub, fontWeight: 600, marginBottom: 6 }}>Atualizar manualmente</div>
          <select value={bgStatus} onChange={(e) => setBgStatus(e.target.value)}
            style={{ width: '100%', background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, padding: '9px 12px', color: C.text, fontSize: 14, fontFamily: C.font, marginBottom: 8 }}>
            <option value="not_checked">Não consultado</option>
            <option value="pending">Pendente</option>
            <option value="approved">Aprovado</option>
            <option value="rejected">Reprovado</option>
          </select>
          <textarea value={bgNotes} onChange={(e) => setBgNotes(e.target.value)} rows={2}
            placeholder="Observações sobre a consulta..."
            style={{ width: '100%', background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, padding: '10px 12px', color: C.text, fontSize: 13, fontFamily: C.font, resize: 'vertical', boxSizing: 'border-box', marginBottom: 8 }} />
          {bgMsg && <Alert type={bgMsg.includes('sucesso') ? 'info' : 'danger'}>{bgMsg}</Alert>}
          <Btn variant="outline" full loading={bgLoading} onClick={saveBackgroundCheck}>Salvar verificação</Btn>
        </div>
      </div>
      <Divider />
      {/* Ações */}
      {!workerRegistrationComplete(worker) && (
        <Alert type="warn">{workerRegistrationPendingText(worker) || 'Cadastro documental pendente.'}</Alert>
      )}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 4 }}>
        {worker.docStatus !== 'approved' && (
          <Btn variant="primary" disabled={!workerRegistrationComplete(worker)} loading={actionLoading} onClick={() => onSetStatus(worker.id, 'approved')}>✓ Aprovar</Btn>
        )}
        {worker.docStatus !== 'rejected' && (
          <Btn variant="danger" loading={actionLoading} onClick={() => onSetStatus(worker.id, 'rejected')}>✕ Reprovar</Btn>
        )}
        <Btn variant="ghost" onClick={onClose}>Fechar</Btn>
      </div>
    </Modal>
  );
};

const AdminWorkers = () => {
  const { data, loading, refetch } = useAdminUsers('worker');
  const action = useAction();
  const activeRequestRef = useRef(false);
  const [docsWorker, setDocsWorker] = useState(null);
  const [profileWorker, setProfileWorker] = useState(null);
  const [workerSearch, setWorkerSearch] = useState('');
  const [workerStatusFilter, setWorkerStatusFilter] = useState('');
  const [workerPartnerFilter, setWorkerPartnerFilter] = useState('');
  const [workerDateFrom, setWorkerDateFrom] = useState('');
  const [workerDateTo, setWorkerDateTo] = useState('');

  const setStatus = async (id, status) => {
    await action.run(() => api.admin.setWorkerStatus(id, status));
    refetch();
  };

  const setActive = async (id, active) => {
    if (activeRequestRef.current) return;
    if (!window.confirm(active ? 'Reativar este colaborador?' : 'Desativar este colaborador? O histórico será preservado.')) return;
    activeRequestRef.current = true;
    try {
      await action.run(() => active ? api.admin.activateUser(id) : api.admin.deactivateUser(id));
      await refetch();
    } catch (_) { /* useAction displays the API error */ }
    finally { activeRequestRef.current = false; }
  };

  const deleteWorker = async (id) => {
    if (activeRequestRef.current) return;
    if (!window.confirm('Excluir este colaborador do painel? O acesso será removido, mas o histórico de serviços e pagamentos será preservado.')) return;

    activeRequestRef.current = true;
    try {
      await action.run(() => api.admin.deleteUser(id));
      await refetch();
    } catch (_) { /* useAction displays the API error */ }
    finally { activeRequestRef.current = false; }
  };
  const docStatusColor = { pending: C.gold, approved: C.accent, rejected: C.danger };
  const docStatusLabel = { pending: 'CADASTRO EM ANÁLISE', approved: 'CADASTRO APROVADO', rejected: 'CADASTRO REPROVADO' };
  const workers = data?.users || [];
  const partnerOptions = Array.from(new Map(
    workers
      .filter((worker) => worker.partner?.id)
      .map((worker) => [worker.partner.id, worker.partner])
  ).values());
  const filteredWorkers = workers.filter((worker) => {
    const query = serviceKey(workerSearch);
    const joined = serviceKey([
      worker.name,
      worker.email,
      worker.phone,
      worker.cpf,
      worker.partner?.name,
      worker.partner?.city,
      worker.partner?.state,
      ...(worker.skills || []),
      ...(worker.areas || []),
    ].filter(Boolean).join(' '));
    if (query && !joined.includes(query)) return false;
    if (workerStatusFilter && worker.docStatus !== workerStatusFilter) return false;
    if (workerPartnerFilter && worker.partner?.id !== workerPartnerFilter) return false;
    const joinedAt = toDateInputValue(worker.createdAt);
    if (workerDateFrom && joinedAt && joinedAt < workerDateFrom) return false;
    if (workerDateTo && joinedAt && joinedAt > workerDateTo) return false;
    return true;
  });

  const workerFilters = (
    <Card style={{ marginBottom: 0 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10 }}>
        <FInput label="Buscar" placeholder="Nome, email, telefone, CPF, area..." value={workerSearch} onChange={(e) => setWorkerSearch(e.target.value)} />
        <div style={{ marginBottom: 14 }}>
          <label style={{ display: 'block', fontSize: 12, color: C.sub, marginBottom: 5, fontWeight: 600 }}>Status</label>
          <select value={workerStatusFilter} onChange={(e) => setWorkerStatusFilter(e.target.value)} style={{ width: '100%', background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, color: C.text, padding: '10px 12px', fontFamily: C.font, fontSize: 14 }}>
            <option value="">Todos</option>
            <option value="pending">Pendentes</option>
            <option value="approved">Aprovados</option>
            <option value="rejected">Reprovados</option>
          </select>
        </div>
        <div style={{ marginBottom: 14 }}>
          <label style={{ display: 'block', fontSize: 12, color: C.sub, marginBottom: 5, fontWeight: 600 }}>Parceiro</label>
          <select value={workerPartnerFilter} onChange={(e) => setWorkerPartnerFilter(e.target.value)} style={{ width: '100%', background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, color: C.text, padding: '10px 12px', fontFamily: C.font, fontSize: 14 }}>
            <option value="">Todos</option>
            {partnerOptions.map((partner) => (
              <option key={partner.id} value={partner.id}>{partner.name} - {[partner.city, partner.state].filter(Boolean).join('/')}</option>
            ))}
          </select>
        </div>
        <FInput label="Entrada de" type="date" value={workerDateFrom} onChange={(e) => setWorkerDateFrom(e.target.value)} />
        <FInput label="Entrada ate" type="date" value={workerDateTo} onChange={(e) => setWorkerDateTo(e.target.value)} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ color: C.sub, fontSize: 12, fontWeight: 700 }}>
          Exibindo {filteredWorkers.length} de {workers.length} colaborador(es)
        </div>
        <Btn variant="ghost" small onClick={() => { setWorkerSearch(''); setWorkerStatusFilter(''); setWorkerPartnerFilter(''); setWorkerDateFrom(''); setWorkerDateTo(''); }}>
          Limpar filtros
        </Btn>
      </div>
    </Card>
  );

  return (
    <div className="qs-section qs-admin-workers-panel" style={FIXED_SECTION_WRAPPER_STYLE}>
      <div className="qs-section-header qs-admin-workers-fixed-header" style={FIXED_SECTION_HEADER_STYLE}>
        <div className="qs-section-inner" style={{ maxWidth: 960, margin: '0 auto' }}>
          <h2 className="qs-section-title" style={{ fontSize: 22, fontWeight: 800, color: C.text, marginBottom: 4 }}>Colaboradores</h2>
          <p className="qs-section-subtitle" style={{ fontSize: 14, color: C.sub, marginTop: 2 }}>Gestão e aprovação de workers</p>
          <div style={{ marginTop: 14 }}>{workerFilters}</div>
        </div>
      </div>
      {docsWorker && <WorkerDocsModal worker={docsWorker} onClose={() => setDocsWorker(null)} />}
      {profileWorker && (
        <WorkerProfileModal
          worker={profileWorker}
          onClose={() => setProfileWorker(null)}
          onSetStatus={async (id, status) => { await setStatus(id, status); setProfileWorker(null); }}
          onReview={refetch}
          actionLoading={action.loading}
        />
      )}
      <div className="qs-section-content qs-admin-workers-scroll" style={FIXED_SECTION_CONTENT_STYLE}>
        <div className="qs-section-inner" style={{ maxWidth: 960, margin: '0 auto' }}>
          {action.error && <Alert type="danger">{action.error}</Alert>}
          {loading ? <Spinner /> : filteredWorkers.length === 0 ? (
            <Alert type="info">Nenhum colaborador encontrado para os filtros selecionados.</Alert>
          ) : filteredWorkers.map((u) => (
            <Card key={u.id} style={{ marginBottom: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ display: 'flex', gap: 12 }}>
                  <WorkerPhoto workerId={u.id} name={u.name} size={48} color={C.info} profilePhoto={u.profilePhoto} />
                  <div>
                    <div style={{ fontWeight: 700, color: C.text }}>{u.name}</div>
                    <div style={{ fontSize: 12, color: C.sub }}>{u.email}</div>
                    <div style={{ fontSize: 12, color: C.info, marginTop: 3, fontWeight: 700 }}>
                      Entrada: {fmtDate(u.createdAt)}
                    </div>
                    {u.partner && (
                      <div style={{ fontSize: 12, color: C.purple, marginTop: 3, fontWeight: 700 }}>
                        Origem: {u.partner.name}{u.partner.city || u.partner.state ? ` - ${[u.partner.city, u.partner.state].filter(Boolean).join('/')}` : ''}
                      </div>
                    )}
                    <div style={{ marginTop: 4 }}><Stars rating={u.workerRating} /></div>
                    <div style={{ fontSize: 12, color: C.sub, marginTop: 4 }}>
                      Vagas: {u.totalJobs || 0} | Ganho: R$ {Number(u.totalEarned || 0).toFixed(2)}
                    </div>
                    {!workerRegistrationComplete(u) && (
                      <div style={{ fontSize: 12, color: C.gold, marginTop: 4, maxWidth: 520, lineHeight: 1.35 }}>
                        Pendencias: {workerRegistrationPendingText(u) || 'cadastro documental pendente'}
                      </div>
                    )}
                    {(u.skills || []).length > 0 && (
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 6 }}>
                        {u.skills.map((s) => <span key={s} style={{ background: C.infoDim, color: C.info, border: `1px solid ${C.info}30`, borderRadius: 4, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>{s}</span>)}
                      </div>
                    )}
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-end' }}>
                  <span style={{ fontWeight: 800, fontSize: 12, color: docStatusColor[u.docStatus] || C.sub }}>
                    ● {docStatusLabel[u.docStatus] || 'CADASTRO EM ANÁLISE'}
                  </span>
                  <span style={{ fontWeight: 700, fontSize: 12, color: u.livenessStatus === 'approved' ? C.accent : u.livenessStatus === 'rejected' ? C.danger : C.gold }}>
                    Prova de vida: {LIVENESS_STATUS_LABEL[u.livenessStatus] || 'Pendente'}
                  </span>
                  <Chip type={u.active ? 'active' : 'cancelled'} label={u.active ? 'Ativo' : 'Inativo'} />
                  {u.balance > 0 && (
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 11, color: C.sub }}>Saldo</div>
                      <div style={{ fontWeight: 800, color: C.gold }}>R$ {Number(u.balance).toFixed(2)}</div>
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    <Btn variant="ghost" small onClick={() => setProfileWorker(u)}>Ver Perfil</Btn>
                    <Btn variant="ghost" small onClick={() => setDocsWorker(u)}>Ver Docs</Btn>
                    {u.docStatus !== 'approved' && <Btn variant="primary" small disabled={!workerRegistrationComplete(u)} loading={action.loading} onClick={() => setStatus(u.id, 'approved')}>Aprovar</Btn>}
                    {u.docStatus !== 'rejected' && <Btn variant="danger" small loading={action.loading} onClick={() => setStatus(u.id, 'rejected')}>Reprovar</Btn>}
                    <Btn variant={u.active ? 'danger' : 'primary'} small disabled={action.loading} loading={action.loading} onClick={() => setActive(u.id, !u.active)}>{u.active ? 'Desativar' : 'Reativar'}</Btn>
                    <Btn variant="danger" small disabled={action.loading} loading={action.loading} onClick={() => deleteWorker(u.id)}>Excluir</Btn>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};

const partnerStatusLabel = { active: 'Ativo', suspended: 'Suspenso', prospect: 'Prospeccao' };

const partnerFormDefaults = () => ({
  name: '',
  ownerName: '',
  ownerEmail: '',
  phone: '',
  zip: '',
  coverageZipPrefixes: '',
  city: '',
  state: '',
  regionLabel: '',
  commissionShareRate: '35',
  monthlyFee: '0',
  notes: '',
  createLogin: true,
});

const formatPartnerRate = (rate) => {
  const raw = Number(rate);
  if (!Number.isFinite(raw)) return '0%';
  const percent = raw <= 1 ? raw * 100 : raw;
  return `${String(Number(percent.toFixed(2))).replace('.', ',')}%`;
};

const formatOptionalRate = (rate) => rate == null ? 'nao informado' : formatPartnerRate(rate);

const normalizeRate = (value, fallback = 0) => {
  const raw = Number(value);
  if (!Number.isFinite(raw)) return fallback;
  const rate = raw > 1 ? raw / 100 : raw;
  return Math.max(0, Math.min(1, rate));
};

const financialPolicyFromUser = financialPolicySnapshot;
const jobCommissionRate = jobCommissionRateSnapshot;
const jobWorkerPayoutRate = jobWorkerPayoutRateSnapshot;

const partnerToEditForm = (partner) => ({
  name: partner.name || '',
  ownerName: partner.ownerName || '',
  ownerEmail: partner.ownerEmail || '',
  phone: partner.phone || '',
  zip: partner.zip || '',
  coverageZipPrefixes: Array.isArray(partner.coverageZipPrefixes) ? partner.coverageZipPrefixes.join(', ') : '',
  city: partner.city || '',
  state: partner.state || '',
  regionLabel: partner.regionLabel || '',
  commissionShareRate: String(Number(((Number(partner.commissionShareRate) || 0) <= 1
    ? (Number(partner.commissionShareRate) || 0) * 100
    : Number(partner.commissionShareRate) || 0).toFixed(2))),
  monthlyFee: String(Number(partner.monthlyFee || 0)),
  status: partner.status || 'active',
  notes: partner.notes || '',
});

const AdminPartnerPerformance = () => {
  const { data, loading, refetch } = useFetch(() => api.admin.partners(), []);
  const [selectedPartnerId, setSelectedPartnerId] = useState('all');
  const [lastUpdatedAt, setLastUpdatedAt] = useState(new Date());

  useEffect(() => {
    const timer = window.setInterval(() => refetch(), 30000);
    return () => window.clearInterval(timer);
  }, [refetch]);

  useEffect(() => {
    if (data) setLastUpdatedAt(new Date());
  }, [data]);

  const partners = data?.partners || [];
  const selectedPartner = selectedPartnerId === 'all'
    ? null
    : partners.find((partner) => partner.id === selectedPartnerId) || null;
  const sumMetrics = (rows) => rows.reduce((acc, partner) => {
    const metrics = partner.metrics || {};
    [
      'totalJobs', 'doneJobs', 'openJobs', 'activeJobs', 'inProductionJobs', 'cancelledJobs',
      'originatedJobs', 'referredWorkers', 'pendingReferredWorkers', 'approvedReferredWorkers', 'referredClients',
    ].forEach((key) => { acc[key] = Number(acc[key] || 0) + Number(metrics[key] || 0); });
    return acc;
  }, {});
  const metrics = selectedPartner?.metrics || sumMetrics(partners);
  const totalJobs = Number(metrics.totalJobs || 0);
  const doneJobs = Number(metrics.doneJobs || 0);
  const cancelledJobs = Number(metrics.cancelledJobs || 0);
  const completionRate = totalJobs > 0 ? (doneJobs / totalJobs) * 100 : 0;
  const cancellationRate = totalJobs > 0 ? (cancelledJobs / totalJobs) * 100 : 0;
  const operationalBars = [
    ['Concluídos', doneJobs, C.accent],
    ['Em produção', Number(metrics.inProductionJobs || 0), C.gold],
    ['Abertos', Number(metrics.openJobs || 0), C.info],
    ['Cancelados', cancelledJobs, C.danger],
    ['Originados pela unidade', Number(metrics.originatedJobs || 0), C.purple],
  ];
  const insights = [
    completionRate < 50 && totalJobs > 0 ? 'Priorizar campanhas de recuperação e acompanhamento dos serviços ainda não concluídos.' : null,
    cancellationRate > 15 ? 'Revisar origem dos clientes e comunicação da oferta: a taxa de cancelamento está acima de 15%.' : null,
    Number(metrics.referredClients || 0) === 0 ? 'Ativar captação de clientes da unidade com QR Code e link público identificado.' : null,
    Number(metrics.approvedReferredWorkers || 0) < Number(metrics.pendingReferredWorkers || 0) ? 'Acelerar a validação de colaboradores indicados para ampliar a capacidade local.' : null,
    totalJobs === 0 ? 'A unidade ainda não possui volume operacional; iniciar campanha local de aquisição.' : null,
  ].filter(Boolean);

  return (
    <Section title="Desempenho das Unidades" sub="Dashboard operacional vivo para decisões de marketing, sem valores financeiros">
      <Card style={{ marginBottom: 16 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(220px, 1fr) auto auto', gap: 12, alignItems: 'end' }}>
          <div>
            <label style={{ display: 'block', color: C.sub, fontSize: 12, fontWeight: 700, marginBottom: 6 }}>Unidade analisada</label>
            <select value={selectedPartnerId} onChange={(event) => setSelectedPartnerId(event.target.value)} style={{ width: '100%', background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, padding: '10px 12px', color: C.text, fontFamily: C.font }}>
              <option value="all">Todas as unidades</option>
              {partners.map((partner) => <option key={partner.id} value={partner.id}>{partner.name} - {[partner.city, partner.state].filter(Boolean).join('/') || 'região geral'}</option>)}
            </select>
          </div>
          <Btn variant="ghost" onClick={refetch}>Atualizar agora</Btn>
          <div style={{ color: C.muted, fontSize: 11, paddingBottom: 10 }}>Atualização automática: 30 s<br />Última: {lastUpdatedAt.toLocaleTimeString('pt-BR')}</div>
        </div>
      </Card>

      {loading ? <Spinner /> : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10, marginBottom: 16 }}>
            {[
              ['Serviços recebidos', totalJobs, C.info],
              ['Concluídos', doneJobs, C.accent],
              ['Em produção', Number(metrics.inProductionJobs || 0), C.gold],
              ['Clientes captados', Number(metrics.referredClients || 0), C.purple],
              ['Colaboradores indicados', Number(metrics.referredWorkers || 0), C.info],
              ['Taxa de conclusão', `${completionRate.toFixed(1).replace('.', ',')}%`, C.accent],
            ].map(([label, value, color]) => (
              <Card key={label} style={{ margin: 0 }}>
                <div style={{ color: C.sub, fontSize: 11, fontWeight: 700 }}>{label}</div>
                <div style={{ color, fontSize: 24, fontWeight: 900, marginTop: 6 }}>{value}</div>
              </Card>
            ))}
          </div>

          <Card style={{ marginBottom: 16 }}>
            <div style={{ color: C.text, fontWeight: 900, marginBottom: 14 }}>Distribuição operacional</div>
            {operationalBars.map(([label, value, color]) => {
              const width = totalJobs > 0 ? Math.min(100, (Number(value) / totalJobs) * 100) : 0;
              return (
                <div key={label} style={{ marginBottom: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', color: C.sub, fontSize: 12, marginBottom: 5 }}><span>{label}</span><strong style={{ color }}>{value}</strong></div>
                  <div style={{ height: 10, background: C.surface, borderRadius: 999, overflow: 'hidden' }}><div style={{ width: `${width}%`, height: '100%', background: color, borderRadius: 999, transition: 'width .25s ease' }} /></div>
                </div>
              );
            })}
          </Card>

          <Card>
            <div style={{ color: C.text, fontWeight: 900, marginBottom: 10 }}>Leitura estratégica para marketing</div>
            {insights.length === 0
              ? <Alert type="success">A unidade apresenta equilíbrio operacional. Mantenha campanhas de aquisição e acompanhe a conversão.</Alert>
              : insights.map((insight) => <div key={insight} style={{ color: C.sub, fontSize: 13, lineHeight: 1.6, marginBottom: 7 }}>• {insight}</div>)}
          </Card>
        </>
      )}
    </Section>
  );
};

const AdminPartners = () => {
  const { data, loading, refetch } = useFetch(() => api.admin.partners(), []);
  const action = useAction();
  const [createdLogin, setCreatedLogin] = useState(null);
  const [assignMessage, setAssignMessage] = useState('');
  const [editingPartner, setEditingPartner] = useState(null);
  const [editForm, setEditForm] = useState(partnerFormDefaults());
  const [form, setForm] = useState(partnerFormDefaults());

  const set = (key) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const setEdit = (key) => (event) => {
    const value = event.target.value;
    setEditForm((prev) => ({ ...prev, [key]: value }));
  };

  const reset = () => setForm(partnerFormDefaults());

  const openEdit = (partner) => {
    setCreatedLogin(null);
    setAssignMessage('');
    setEditingPartner(partner);
    setEditForm(partnerToEditForm(partner));
  };

  const closeEdit = () => {
    setEditingPartner(null);
    setEditForm(partnerFormDefaults());
  };

  const create = async (event) => {
    event.preventDefault();
    setCreatedLogin(null);
    setAssignMessage('');
    try {
      const response = await action.run(() => api.admin.createPartner(form));
      if (response?.login) setCreatedLogin(response.login);
      if (response?.assignedJobs > 0) setAssignMessage(`${response.assignedJobs} servico(s) existente(s) vinculados a esta unidade.`);
      reset();
      refetch();
    } catch (_) {}
  };

  const updateStatus = async (partner, status) => {
    try {
      await action.run(() => api.admin.updatePartner(partner.id, { ...partner, status }));
      refetch();
    } catch (_) {}
  };

  const assignJobs = async (partner) => {
    setAssignMessage('');
    try {
      const response = await action.run(() => api.admin.assignPartnerJobs(partner.id));
      setAssignMessage(response.assignedJobs > 0
        ? `${response.assignedJobs} servico(s) vinculados a ${partner.name}.`
        : `Nenhum servico pendente encontrado para ${partner.city || partner.state}.`);
      refetch();
    } catch (_) {}
  };

  const referralLinkFor = (partner, audience = 'colaborador') => `${window.location.origin}/parceiro/${partner.slug}/${audience}`;
  const partnerAccessLinkFor = (partner) => `${window.location.origin}/parceiro/${partner.slug}/acesso`;

  const copyPartnerAccessLink = async (partner) => {
    const link = partnerAccessLinkFor(partner);
    setAssignMessage('');
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(link);
        setAssignMessage(`Link de acesso do parceiro ${partner.name} copiado.`);
      } else {
        window.prompt('Copie o link de acesso do parceiro:', link);
        setAssignMessage(`Link de acesso do parceiro ${partner.name} exibido para copia.`);
      }
    } catch (_) {
      window.prompt('Copie o link de acesso do parceiro:', link);
      setAssignMessage(`Link de acesso do parceiro ${partner.name} exibido para copia.`);
    }
  };

  const showLoginResult = (response, fallbackEmail) => {
    setCreatedLogin({
      email: response?.email || response?.userEmail || response?.user?.email || fallbackEmail || '',
      tempPassword: response?.tempPassword || '',
    });
  };

  const createPartnerAccess = async (partner) => {
    setCreatedLogin(null);
    setAssignMessage('');
    const email = String(partner.ownerEmail || '').trim().toLowerCase();
    if (!email) {
      setAssignMessage(`Informe o e-mail do responsavel de ${partner.name} antes de criar o acesso.`);
      return;
    }
    try {
      const response = await action.run(() => api.admin.createPartnerUser(partner.id, {
        name: partner.ownerName || partner.name,
        email,
        phone: partner.phone || '',
      }));
      showLoginResult(response, email);
      setAssignMessage(`Usuario de acesso criado para ${partner.name}.`);
      refetch();
    } catch (_) {}
  };

  const resetPartnerAccess = async (partner, user) => {
    if (!window.confirm(`Redefinir senha de ${user.email}?`)) return;
    setCreatedLogin(null);
    setAssignMessage('');
    try {
      const response = await action.run(() => api.admin.resetUserPassword(user.id));
      showLoginResult(response, user.email);
      setAssignMessage(`Senha temporaria redefinida para ${partner.name}.`);
      refetch();
    } catch (_) {}
  };

  const activatePartnerAccess = async (partner, user) => {
    setAssignMessage('');
    try {
      await action.run(() => api.admin.activateUser(user.id));
      setAssignMessage(`Acesso de ${user.email} reativado para ${partner.name}.`);
      refetch();
    } catch (_) {}
  };

  const copyReferralLink = async (partner, audience = 'colaborador') => {
    const link = referralLinkFor(partner, audience);
    const label = audience === 'cliente' ? 'cliente' : 'colaborador';
    setAssignMessage('');
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(link);
        setAssignMessage(`Link de cadastro de ${label} de ${partner.name} copiado.`);
      } else {
        window.prompt('Copie o link de cadastro do parceiro:', link);
        setAssignMessage(`Link de cadastro de ${label} de ${partner.name} exibido para copia.`);
      }
    } catch (_) {
      window.prompt('Copie o link de cadastro do parceiro:', link);
      setAssignMessage(`Link de cadastro de ${label} de ${partner.name} exibido para copia.`);
    }
  };

  const deletePartner = async (partner) => {
    const ok = window.confirm(`Excluir ${partner.name}? A exclusao so sera permitida se nao houver usuarios, servicos, saques ou regras de preco vinculados. Para preservar historico, prefira suspender o parceiro.`);
    if (!ok) return;
    setAssignMessage('');
    try {
      const response = await action.run(() => api.admin.deletePartner(partner.id));
      setAssignMessage(response?.message || `${partner.name} excluido com sucesso.`);
      refetch();
    } catch (_) {}
  };

  const saveEdit = async (event) => {
    event.preventDefault();
    if (!editingPartner) return;
    setAssignMessage('');
    try {
      await action.run(() => api.admin.updatePartner(editingPartner.id, editForm));
      setAssignMessage(`Regras de ${editForm.name || editingPartner.name} atualizadas com sucesso.`);
      closeEdit();
      refetch();
    } catch (_) {}
  };

  const partners = data?.partners || [];
  return (
    <Section title="Parceiros Regionais" sub="Cadastro de unidades franqueadas por cidade ou UF">
      {action.error && <Alert type="danger">{action.error}</Alert>}
      {createdLogin && (
        <Alert type="warn">
          Login criado: <strong>{createdLogin.email}</strong>
          <span style={{ marginLeft: 8 }}>Senha temporaria:</span>
          <span style={{ fontFamily: C.mono, color: C.gold, fontWeight: 800, marginLeft: 6 }}>{createdLogin.tempPassword}</span>
        </Alert>
      )}
      {assignMessage && <Alert type="info">{assignMessage}</Alert>}

      <Card style={{ marginBottom: 18 }}>
        <form onSubmit={create}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
            <FInput label="Nome da unidade" placeholder="QServe Marica" value={form.name} onChange={set('name')} />
            <FInput label="Responsavel" placeholder="Nome do franqueado" value={form.ownerName} onChange={set('ownerName')} />
            <FInput label="E-mail do responsavel" placeholder="email@dominio.com" value={form.ownerEmail} onChange={set('ownerEmail')} />
            <FInput label="Telefone" placeholder="(21) 99999-9999" value={form.phone} onChange={set('phone')} />
            <FInput label="CEP base" placeholder="24900-000" value={form.zip} onChange={set('zip')} />
            <FInput label="Prefixos de CEP" placeholder="249, 2490, 24900" value={form.coverageZipPrefixes} onChange={set('coverageZipPrefixes')} />
            <FInput label="Cidade" placeholder="Marica" value={form.city} onChange={set('city')} />
            <FInput label="UF" placeholder="RJ" value={form.state} onChange={set('state')} />
            <FInput label="Regiao comercial" placeholder="Regiao dos Lagos" value={form.regionLabel} onChange={set('regionLabel')} />
            <FInput label="% da comissao" type="number" placeholder="35" value={form.commissionShareRate} onChange={set('commissionShareRate')} />
            <FInput label="Mensalidade" type="number" placeholder="0" value={form.monthlyFee} onChange={set('monthlyFee')} />
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 12, color: C.sub, marginBottom: 5, fontWeight: 600 }}>Observacoes</label>
            <textarea
              value={form.notes}
              onChange={set('notes')}
              placeholder="Contrato, raio de atendimento, regras comerciais..."
              style={{ width: '100%', minHeight: 72, resize: 'vertical', background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, color: C.text, padding: '10px 12px', fontFamily: C.font }}
            />
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, color: C.text, fontSize: 13, fontWeight: 600 }}>
            <input type="checkbox" checked={form.createLogin} onChange={set('createLogin')} />
            Criar usuario de acesso para o parceiro
          </label>
          <Btn variant="primary" loading={action.loading}>Cadastrar parceiro</Btn>
        </form>
      </Card>

      {loading ? <Spinner /> : partners.length === 0 ? (
        <Alert type="info">Nenhum parceiro cadastrado ainda.</Alert>
      ) : partners.map((partner) => {
        const metrics = partner.metrics || {};
        const partnerUsers = Array.isArray(partner.users) ? partner.users : [];
        return (
          <Card key={partner.id} style={{ marginBottom: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
              <div style={{ minWidth: 260 }}>
                <div style={{ fontWeight: 800, color: C.text, fontSize: 16 }}>{partner.name}</div>
                <div style={{ color: C.sub, fontSize: 12, marginTop: 3 }}>
                  {partner.city || 'UF inteira'}, {partner.state} {partner.regionLabel ? `| ${partner.regionLabel}` : ''}
                </div>
                <div style={{ color: C.sub, fontSize: 12, marginTop: 3 }}>
                  Responsavel: {partner.ownerName || '-'} | {partner.ownerEmail || '-'}
                </div>
                <div style={{ color: C.sub, fontSize: 12, marginTop: 3 }}>
                  Comissao: {formatPartnerRate(partner.commissionShareRate)} | Mensalidade: {fmtMoney(partner.monthlyFee || 0)}
                </div>
                <div style={{ color: C.gold, fontSize: 12, marginTop: 3, fontWeight: 700, wordBreak: 'break-all' }}>
                  Parceiro: {partnerAccessLinkFor(partner)}
                </div>
                <div style={{ color: C.purple, fontSize: 12, marginTop: 3, fontWeight: 700, wordBreak: 'break-all' }}>
                  Cliente: {referralLinkFor(partner, 'cliente')}
                </div>
                <div style={{ color: C.info, fontSize: 12, marginTop: 3, fontWeight: 700, wordBreak: 'break-all' }}>
                  Colaborador: {referralLinkFor(partner, 'colaborador')}
                </div>
                {(partner.zip || (Array.isArray(partner.coverageZipPrefixes) && partner.coverageZipPrefixes.length > 0)) && (
                  <div style={{ color: C.sub, fontSize: 12, marginTop: 3 }}>
                    CEPs: {[partner.zip, ...(partner.coverageZipPrefixes || [])].filter(Boolean).join(', ')}
                  </div>
                )}
                <div style={{ marginTop: 8 }}>
                  <Chip type={partner.status === 'active' ? 'active' : partner.status === 'suspended' ? 'cancelled' : 'pending'} label={partnerStatusLabel[partner.status] || partner.status} />
                </div>
                <div style={{ marginTop: 10, border: `1px solid ${C.border}`, borderRadius: 8, padding: 10, background: C.surface }}>
                  <div style={{ color: C.text, fontSize: 12, fontWeight: 900, marginBottom: 6 }}>Acesso do parceiro</div>
                  {partnerUsers.length === 0 ? (
                    <>
                      <div style={{ color: C.gold, fontSize: 12, marginBottom: 8 }}>Nenhum usuario partner vinculado a esta unidade.</div>
                      <Btn variant="primary" small loading={action.loading} onClick={() => createPartnerAccess(partner)}>Criar acesso</Btn>
                    </>
                  ) : partnerUsers.map((user) => (
                    <div key={user.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'flex-start', flexWrap: 'wrap', marginBottom: 8 }}>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ color: C.text, fontSize: 12, fontWeight: 800, wordBreak: 'break-all' }}>{user.email}</div>
                        <div style={{ color: C.sub, fontSize: 11, marginTop: 2 }}>{user.name || 'Sem nome'} | {user.active ? 'Ativo' : 'Inativo'}</div>
                        <div style={{ color: C.sub, fontSize: 11, marginTop: 2 }}>
                          Ultimo login: {user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleString('pt-BR') : 'nunca logou'}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                        <Btn variant="gold" small loading={action.loading} onClick={() => resetPartnerAccess(partner, user)}>Senha</Btn>
                        {!user.active && <Btn variant="primary" small loading={action.loading} onClick={() => activatePartnerAccess(partner, user)}>Reativar</Btn>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(86px, 1fr))', gap: 8, flex: 1, minWidth: 360 }}>
                {[
                  ['Servicos', metrics.totalJobs || 0, C.info],
                  ['Originados', metrics.originatedJobs || 0, C.purple],
                  ['Concluidos', metrics.doneJobs || 0, C.accent],
                  ['Em producao', metrics.inProductionJobs || 0, C.gold],
                  ['Cancelados', metrics.cancelledJobs || 0, C.danger],
                  ['Conclusao', `${(Number(metrics.totalJobs || 0) > 0 ? (Number(metrics.doneJobs || 0) / Number(metrics.totalJobs || 0)) * 100 : 0).toFixed(1).replace('.', ',')}%`, C.accent],
                  ['Clientes', metrics.referredClients || 0, C.accent],
                  ['Colab.', metrics.referredWorkers || 0, C.purple],
                  ['Pendentes', metrics.pendingReferredWorkers || 0, C.gold],
                ].map(([label, value, color]) => (
                  <div key={label} style={{ border: `1px solid ${C.border}`, borderRadius: 8, padding: 10, background: C.surface }}>
                    <div style={{ color: C.sub, fontSize: 11, fontWeight: 700 }}>{label}</div>
                    <div style={{ color, fontSize: 16, fontWeight: 900, marginTop: 4 }}>{value}</div>
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', width: '100%', flexWrap: 'wrap' }}>
                <Btn variant="ghost" small loading={action.loading} onClick={() => copyReferralLink(partner, 'cliente')}>Link cliente</Btn>
                <Btn variant="ghost" small loading={action.loading} onClick={() => copyReferralLink(partner, 'colaborador')}>Link colaborador</Btn>
                <Btn variant="ghost" small loading={action.loading} onClick={() => copyPartnerAccessLink(partner)}>Link parceiro</Btn>
                <Btn variant="gold" small loading={action.loading} onClick={() => openEdit(partner)}>Editar regras</Btn>
                <Btn variant="ghost" small loading={action.loading} onClick={() => assignJobs(partner)}>Vincular vagas da regiao</Btn>
                {partner.status === 'active' ? (
                  <Btn variant="danger" small loading={action.loading} onClick={() => updateStatus(partner, 'suspended')}>Suspender</Btn>
                ) : (
                  <Btn variant="primary" small loading={action.loading} onClick={() => updateStatus(partner, 'active')}>Reativar</Btn>
                )}
                <Btn variant="danger" small loading={action.loading} onClick={() => deletePartner(partner)}>Excluir</Btn>
              </div>
            </div>
          </Card>
        );
      })}

      {editingPartner && (
        <Modal title={`Editar parceiro - ${editingPartner.name}`} onClose={closeEdit}>
          <form onSubmit={saveEdit}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 12 }}>
              <FInput label="Nome da unidade" value={editForm.name} onChange={setEdit('name')} />
              <FInput label="Responsavel" value={editForm.ownerName} onChange={setEdit('ownerName')} />
              <FInput label="E-mail do responsavel" value={editForm.ownerEmail} onChange={setEdit('ownerEmail')} />
              <FInput label="Telefone" value={editForm.phone} onChange={setEdit('phone')} />
              <FInput label="CEP base" value={editForm.zip} onChange={setEdit('zip')} />
              <FInput label="Prefixos de CEP" value={editForm.coverageZipPrefixes} onChange={setEdit('coverageZipPrefixes')} />
              <FInput label="Cidade" value={editForm.city} onChange={setEdit('city')} />
              <FInput label="UF" value={editForm.state} onChange={setEdit('state')} />
              <FInput label="Regiao comercial" value={editForm.regionLabel} onChange={setEdit('regionLabel')} />
              <FInput label="% da comissao" type="number" value={editForm.commissionShareRate} onChange={setEdit('commissionShareRate')} />
              <FInput label="Mensalidade" type="number" value={editForm.monthlyFee} onChange={setEdit('monthlyFee')} />
              <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', fontSize: 12, color: C.sub, marginBottom: 5, fontWeight: 600 }}>Status da unidade</label>
                <select
                  value={editForm.status}
                  onChange={setEdit('status')}
                  style={{ width: '100%', background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, color: C.text, padding: '10px 12px', fontFamily: C.font, fontSize: 14 }}
                >
                  <option value="active">Ativo</option>
                  <option value="suspended">Suspenso</option>
                  <option value="prospect">Prospeccao</option>
                </select>
              </div>
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 12, color: C.sub, marginBottom: 5, fontWeight: 600 }}>Observacoes e regras especificas</label>
              <textarea
                value={editForm.notes}
                onChange={setEdit('notes')}
                placeholder="Regras comerciais, raio de atendimento, contrato, excecoes..."
                style={{ width: '100%', minHeight: 96, resize: 'vertical', background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, color: C.text, padding: '10px 12px', fontFamily: C.font }}
              />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, flexWrap: 'wrap' }}>
              <Btn type="button" variant="ghost" onClick={closeEdit}>Cancelar</Btn>
              <Btn variant="primary" loading={action.loading}>Salvar alteracoes</Btn>
            </div>
          </form>
        </Modal>
      )}
    </Section>
  );
};

const PartnerDash = () => {
  const { data, loading, error, refetch } = useFetch(() => api.partner.dashboard(), []);
  const { data: wdData, loading: wdLoading, error: wdError, refetch: refetchWithdrawals } = useFetch(() => api.partner.withdrawals(), []);
  const action = useAction();
  const [withdrawModal, setWithdrawModal] = useState(false);
  const [withdrawForm, setWithdrawForm] = useState({ amountDigits: '0', pixType: 'cpf', pixKey: '' });

  const requestWithdrawal = async () => {
    const amount = parseMoney(withdrawForm.amountDigits);
    try {
      await action.run(() => api.partner.requestWithdrawal({
        amount,
        pixKey: withdrawForm.pixKey,
        pixType: withdrawForm.pixType,
      }));
      setWithdrawModal(false);
      setWithdrawForm({ amountDigits: '0', pixType: 'cpf', pixKey: '' });
      refetch();
      refetchWithdrawals();
    } catch (_) {}
  };

  if (loading) return <Section title="Minha Regiao"><Spinner /></Section>;
  if (error) return <Section title="Minha Regiao"><Alert type="danger">{error}</Alert></Section>;

  const partner = data?.partner || {};
  const m = data?.metrics || {};
  const financial = wdData?.financial || data?.financial || m;
  const withdrawals = wdData?.withdrawals || [];
  const clientLink = partner.slug ? `${window.location.origin}/parceiro/${partner.slug}/cliente` : '';
  const copyClientLink = async () => {
    if (!clientLink) return;
    try {
      if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(clientLink);
      else window.prompt('Copie o link de clientes:', clientLink);
    } catch (_) {
      window.prompt('Copie o link de clientes:', clientLink);
    }
  };
  const metrics = (
    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
      <Metric label="Servicos" value={m.totalJobs || 0} color={C.info} icon="#" />
      <Metric label="Ativos" value={m.activeJobs || 0} color={C.accent} icon="+" />
      <Metric label="Concluidos" value={m.doneJobs || 0} color={C.gold} icon="ok" />
      <Metric label="Clientes" value={m.referredClients || 0} color={C.accent} icon="C" />
      <Metric label="Colaboradores" value={m.referredWorkers || 0} color={C.purple} icon="@" />
      <Metric label="Saldo saque" value={fmtMoney(financial.availableBalance || 0)} color={C.purple} icon="$" />
    </div>
  );

  return (
    <Section title={partner.name || 'Minha Regiao'} sub={`${partner.city || 'UF inteira'}, ${partner.state || ''}`} stickyContent={metrics}>
      {withdrawModal && (
        <Modal title="Solicitar saque da unidade" onClose={() => setWithdrawModal(false)}>
          {action.error && <Alert type="danger">{action.error}</Alert>}
          <Alert type="info">Saldo disponivel para saque: {fmtMoney(financial.availableBalance || 0)}</Alert>
          <MoneyInput label="Valor do saque" digits={withdrawForm.amountDigits} onChange={(digits) => setWithdrawForm((f) => ({ ...f, amountDigits: digits }))} />
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 12, color: C.sub, marginBottom: 5, fontWeight: 600 }}>Tipo da chave PIX</label>
            <select value={withdrawForm.pixType} onChange={(e) => setWithdrawForm((f) => ({ ...f, pixType: e.target.value }))} style={{ width: '100%', background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, color: C.text, padding: '10px 12px', fontFamily: C.font, fontSize: 14 }}>
              {PIX_TYPES.map((type) => <option key={type.value} value={type.value}>{type.label}</option>)}
            </select>
          </div>
          <FInput label="Chave PIX" placeholder="Informe a chave que recebera o repasse" value={withdrawForm.pixKey} onChange={(e) => setWithdrawForm((f) => ({ ...f, pixKey: e.target.value }))} />
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
            <Btn variant="ghost" onClick={() => setWithdrawModal(false)}>Cancelar</Btn>
            <Btn
              variant="primary"
              loading={action.loading}
              disabled={parseMoney(withdrawForm.amountDigits) <= 0 || !withdrawForm.pixKey.trim()}
              onClick={requestWithdrawal}
            >
              Enviar solicitacao
            </Btn>
          </div>
        </Modal>
      )}

      {wdError && <Alert type="danger">{wdError}</Alert>}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 14, marginBottom: 18 }}>
        <Card>
          <div style={{ color: C.text, fontWeight: 900, marginBottom: 10 }}>Repasse da unidade</div>
          <div style={{ display: 'grid', gap: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', color: C.sub, fontSize: 13 }}><span>Receita acumulada</span><strong style={{ color: C.purple }}>{fmtMoney(financial.partnerRevenue || 0)}</strong></div>
            <div style={{ display: 'flex', justifyContent: 'space-between', color: C.sub, fontSize: 13 }}><span>Pendente/processando</span><strong style={{ color: C.gold }}>{fmtMoney(financial.pendingWithdrawals || 0)}</strong></div>
            <div style={{ display: 'flex', justifyContent: 'space-between', color: C.sub, fontSize: 13 }}><span>Ja pago</span><strong style={{ color: C.accent }}>{fmtMoney(financial.paidWithdrawals || 0)}</strong></div>
            <div style={{ display: 'flex', justifyContent: 'space-between', color: C.text, fontSize: 15, borderTop: `1px solid ${C.border}`, paddingTop: 8 }}><span>Disponivel</span><strong style={{ color: C.gold }}>{fmtMoney(financial.availableBalance || 0)}</strong></div>
          </div>
          <div style={{ marginTop: 14 }}>
            <Btn variant="gold" full onClick={() => setWithdrawModal(true)} disabled={(financial.availableBalance || 0) <= 0}>Solicitar saque</Btn>
          </div>
        </Card>

        <Card>
          <div style={{ color: C.text, fontWeight: 900, marginBottom: 10 }}>Entrada de clientes</div>
          {clientLink ? (
            <div style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap' }}>
              <div style={{ background: '#fff', padding: 8, borderRadius: 8, lineHeight: 0 }}>
                <QRCodeCanvas value={clientLink} size={112} />
              </div>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ color: C.sub, fontSize: 12, marginBottom: 8, wordBreak: 'break-all' }}>{clientLink}</div>
                <Btn variant="ghost" small onClick={copyClientLink}>Copiar link</Btn>
              </div>
            </div>
          ) : (
            <Alert type="warn">Slug da unidade indisponivel para gerar QR Code.</Alert>
          )}
        </Card>
      </div>

      <h3 style={{ color: C.text, fontWeight: 700, marginBottom: 12 }}>Historico de saques</h3>
      {wdLoading ? <Spinner /> : withdrawals.length === 0 ? (
        <Alert type="info">Nenhum saque solicitado por esta unidade ainda.</Alert>
      ) : withdrawals.slice(0, 6).map((withdrawal) => (
        <Card key={withdrawal.id} style={{ marginBottom: 10 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <div>
              <div style={{ color: C.text, fontWeight: 800 }}>{fmtMoney(withdrawal.amount)}</div>
              <div style={{ color: C.sub, fontSize: 12 }}>Solicitado em {fmtDate(withdrawal.requestedAt)}</div>
              <div style={{ color: C.sub, fontSize: 12 }}>PIX: {withdrawal.pixType ? `${PIX_TYPES.find((type) => type.value === withdrawal.pixType)?.label || withdrawal.pixType} - ` : ''}{withdrawal.pixKey}</div>
            </div>
            <Chip type={withdrawal.status} label={{ pending: 'Pendente', approved: 'Processando', paid: 'Pago', rejected: 'Rejeitado' }[withdrawal.status] || withdrawal.status} />
          </div>
        </Card>
      ))}

      <h3 style={{ color: C.text, fontWeight: 700, margin: '20px 0 12px' }}>Servicos recentes</h3>
      {(data?.recentJobs || []).length === 0 ? <Alert type="info">Nenhum servico atribuido a esta unidade ainda.</Alert> : (data?.recentJobs || []).map((job) => (
        <Card key={job.id} style={{ marginBottom: 10 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
            <div>
              <div style={{ color: C.text, fontWeight: 800 }}>{job.category} / {job.subcategory}</div>
              <div style={{ color: C.sub, fontSize: 12 }}>Cliente: {job.client?.name || '-'} | Worker: {job.worker?.name || 'Sem worker'}</div>
              <div style={{ color: C.sub, fontSize: 12 }}>{job.city}, {job.state}</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ color: C.gold, fontWeight: 900 }}>{fmtMoney(job.price)}</div>
              <div style={{ color: C.purple, fontSize: 12, fontWeight: 700 }}>Parceiro: {fmtMoney(job.partnerCommission || 0)}</div>
              <div style={{ marginTop: 4 }}><Chip type={job.status} /></div>
            </div>
          </div>
        </Card>
      ))}
    </Section>
  );
};

const PartnerJobs = () => {
  const [statusFilter, setStatusFilter] = useState('');
  const { data, loading, error } = useFetch(() => api.partner.jobs(statusFilter), [statusFilter]);
  const statuses = ['', 'open', 'accepted', 'in_progress', 'done', 'cancelled'];
  const filters = (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
      {statuses.map((status) => (
        <button
          key={status || 'all'}
          onClick={() => setStatusFilter(status)}
          style={{ padding: '6px 14px', borderRadius: 6, border: `1px solid ${statusFilter === status ? C.purple : C.border}`, background: statusFilter === status ? `${C.purple}20` : 'transparent', color: statusFilter === status ? C.purple : C.sub, cursor: 'pointer', fontWeight: 600, fontSize: 13, fontFamily: C.font }}
        >
          {status === '' ? 'Todas' : chipLabels[status] || status}
        </button>
      ))}
    </div>
  );

  return (
    <Section title="Servicos da Regiao" sub="Operacao e receita da unidade parceira" headerContent={filters}>
      {error ? <Alert type="danger">{error}</Alert> : loading ? <Spinner /> : (data?.jobs || []).length === 0 ? <Alert type="info">Nenhum servico encontrado para este filtro.</Alert> : (data?.jobs || []).map((job) => (
        <Card key={job.id} style={{ marginBottom: 10 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <div>
              <div style={{ color: C.text, fontWeight: 800 }}>{job.category} / {job.subcategory}</div>
              <div style={{ color: C.sub, fontSize: 12, marginTop: 3 }}>Cliente: {job.client?.name || '-'} | Worker: {job.worker?.name || 'Sem worker'}</div>
              <div style={{ color: C.sub, fontSize: 12 }}>{job.street} {job.number} - {job.city}, {job.state}</div>
              {job.scheduledDate && <div style={{ color: C.sub, fontSize: 12 }}>Data: {new Date(job.scheduledDate).toLocaleDateString('pt-BR')}</div>}
            </div>
            <div style={{ textAlign: 'right', minWidth: 180 }}>
              <div style={{ color: C.gold, fontWeight: 900, fontSize: 17 }}>{fmtMoney(job.price)}</div>
              <div style={{ color: C.purple, fontSize: 12, fontWeight: 700 }}>Parceiro: {fmtMoney(job.partnerCommission || 0)}</div>
              <div style={{ marginTop: 5 }}><Chip type={job.status} /></div>
            </div>
          </div>
        </Card>
      ))}
    </Section>
  );
};

const PartnerClients = () => {
  const { data, loading, error } = useFetch(() => api.partner.clients(), []);
  const clients = data?.clients || [];
  const totalSpent = clients.reduce((sum, client) => sum + (Number(client.totalSpent) || 0), 0);
  const activeClients = clients.filter((client) => (Number(client.clientJobs) || 0) > 0).length;
  const metrics = (
    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
      <Metric label="Clientes" value={clients.length} color={C.accent} icon="◎" />
      <Metric label="Com servicos" value={activeClients} color={C.info} icon="#" />
      <Metric label="Gasto total" value={fmtMoney(totalSpent)} color={C.gold} icon="$" />
    </div>
  );

  return (
    <Section title="Clientes Captados" sub="Clientes cadastrados pelo link ou QR Code da unidade" stickyContent={metrics}>
      {error ? <Alert type="danger">{error}</Alert> : loading ? <Spinner /> : clients.length === 0 ? (
        <Alert type="info">Nenhum cliente captado por link de parceiro ainda.</Alert>
      ) : clients.map((client) => (
        <Card key={client.id} style={{ marginBottom: 10 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <div>
              <div style={{ color: C.text, fontWeight: 800 }}>{client.name}</div>
              <div style={{ color: C.sub, fontSize: 12, marginTop: 3 }}>{client.email}</div>
              <div style={{ color: C.sub, fontSize: 12 }}>{client.phone || 'Telefone nao informado'}</div>
              {client.lastLoginAt && <div style={{ color: C.sub, fontSize: 12 }}>Ultimo acesso: {new Date(client.lastLoginAt).toLocaleDateString('pt-BR')}</div>}
            </div>
            <div style={{ textAlign: 'right', minWidth: 160 }}>
              <div style={{ color: C.info, fontWeight: 900 }}>{client.clientJobs || 0} servico(s)</div>
              <div style={{ color: C.gold, fontSize: 12, fontWeight: 700 }}>{fmtMoney(client.totalSpent || 0)}</div>
              <div style={{ color: C.sub, fontSize: 12, marginTop: 4 }}>Desde {new Date(client.createdAt).toLocaleDateString('pt-BR')}</div>
            </div>
          </div>
        </Card>
      ))}
    </Section>
  );
};

const PartnerWorkers = () => {
  const [statusFilter, setStatusFilter] = useState('');
  const { data, loading, error } = useFetch(() => api.partner.workers(), []);
  const workers = data?.workers || [];
  const statusColor = { pending: C.gold, approved: C.accent, rejected: C.danger };
  const statusLabel = { pending: 'Em analise', approved: 'Aprovado', rejected: 'Reprovado' };
  const filteredWorkers = statusFilter ? workers.filter((worker) => worker.docStatus === statusFilter) : workers;
  const displayAreas = (worker) => {
    if (Array.isArray(worker.areas) && worker.areas.length > 0) return worker.areas;
    if (Array.isArray(worker.skills)) return worker.skills;
    return [];
  };
  const counts = workers.reduce((acc, worker) => {
    acc[worker.docStatus] = (acc[worker.docStatus] || 0) + 1;
    return acc;
  }, {});
  const metrics = (
    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
      <Metric label="Total" value={workers.length} color={C.purple} icon="@" />
      <Metric label="Em analise" value={counts.pending || 0} color={C.gold} icon="!" />
      <Metric label="Aprovados" value={counts.approved || 0} color={C.accent} icon="ok" />
      <Metric label="Reprovados" value={counts.rejected || 0} color={C.danger} icon="x" />
    </div>
  );
  const filters = (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
      {[
        ['', 'Todos'],
        ['pending', 'Em analise'],
        ['approved', 'Aprovados'],
        ['rejected', 'Reprovados'],
      ].map(([value, label]) => (
        <button
          key={value || 'all'}
          onClick={() => setStatusFilter(value)}
          style={{ padding: '6px 14px', borderRadius: 6, border: `1px solid ${statusFilter === value ? C.purple : C.border}`, background: statusFilter === value ? `${C.purple}20` : 'transparent', color: statusFilter === value ? C.purple : C.sub, cursor: 'pointer', fontWeight: 600, fontSize: 13, fontFamily: C.font }}
        >
          {label}
        </button>
      ))}
    </div>
  );

  return (
    <Section title="Meus Colaboradores" sub="Acompanhamento dos cadastros indicados pela unidade parceira" stickyContent={metrics} headerContent={filters}>
      <Alert type="info">A aprovacao, reprovacao e auditoria documental continuam exclusivas do administrador.</Alert>
      {error ? <Alert type="danger">{error}</Alert> : loading ? <Spinner /> : filteredWorkers.length === 0 ? (
        <Alert type="info">Nenhum colaborador encontrado para este filtro.</Alert>
      ) : filteredWorkers.map((worker) => (
        <Card key={worker.id} style={{ marginBottom: 10 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 14, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', gap: 12, minWidth: 260, flex: 1 }}>
              <WorkerPhoto workerId={worker.id} name={worker.name} size={46} color={C.purple} profilePhoto={worker.profilePhoto} />
              <div style={{ minWidth: 0 }}>
                <div style={{ color: C.text, fontWeight: 900 }}>{worker.name}</div>
                <div style={{ color: C.sub, fontSize: 12, marginTop: 2 }}>{worker.email || '-'}</div>
                {worker.phone && <div style={{ color: C.sub, fontSize: 12, marginTop: 2 }}>Telefone: {worker.phone}</div>}
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 6 }}>
                  <Stars rating={worker.workerRating || 0} />
                  <span style={{ color: C.sub, fontSize: 12 }}>{worker.workerRatingCount || 0} avaliacao(oes)</span>
                </div>
                {displayAreas(worker).length > 0 && (
                  <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginTop: 8 }}>
                    {displayAreas(worker).slice(0, 6).map((area) => (
                      <span key={area} style={{ background: `${C.purple}18`, color: C.purple, border: `1px solid ${C.purple}35`, borderRadius: 4, padding: '2px 8px', fontSize: 11, fontWeight: 700 }}>{area}</span>
                    ))}
                  </div>
                )}
                {worker.description && (
                  <div style={{ color: C.sub, fontSize: 12, lineHeight: 1.45, marginTop: 8, maxWidth: 620 }}>
                    {worker.description}
                  </div>
                )}
              </div>
            </div>
            <div style={{ minWidth: 180, textAlign: 'right' }}>
              <div style={{ color: statusColor[worker.docStatus] || C.sub, fontWeight: 900, fontSize: 13 }}>
                {statusLabel[worker.docStatus] || worker.docStatus || 'Em analise'}
              </div>
              <div style={{ color: C.sub, fontSize: 12, marginTop: 6 }}>Servicos: {worker.totalJobs || 0}</div>
              <div style={{ color: C.sub, fontSize: 12, marginTop: 3 }}>Experiencia: {worker.yearsExperience || 0} ano(s)</div>
              <div style={{ color: C.sub, fontSize: 12, marginTop: 3 }}>Raio: {worker.serviceRadius || 0} km</div>
              <div style={{ color: C.sub, fontSize: 12, marginTop: 8 }}>
                Cadastro: {worker.createdAt ? new Date(worker.createdAt).toLocaleDateString('pt-BR') : '-'}
              </div>
            </div>
          </div>
        </Card>
      ))}
    </Section>
  );
};

// eslint-disable-next-line no-unused-vars
const LegacyAdminPriceTable = () => {
  const { data, loading, refetch } = useAdminPriceTable();
  const action = useAction();
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({ category: '', subcategory: '', basePriceDigits: '0', unit: 'fixo' });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    try {
      const payload = { category: form.category, subcategory: form.subcategory, basePrice: parseMoney(form.basePriceDigits), unit: form.unit };
      if (editItem) { await action.run(() => api.priceTable.update(editItem.id, payload)); }
      else { await action.run(() => api.priceTable.create(payload)); }
      setShowForm(false); setEditItem(null); setForm({ category: '', subcategory: '', basePriceDigits: '0', unit: 'fixo' });
      refetch();
    } catch (_) {}
  };

  const remove = async (id) => {
    if (!window.confirm('Excluir este preço?')) return;
    await action.run(() => api.priceTable.remove(id));
    refetch();
  };

  const startEdit = (item) => {
    setEditItem(item);
    setForm({ category: item.category, subcategory: item.subcategory, basePriceDigits: String(Math.round(parseFloat(item.basePrice) * 100)), unit: item.unit });
    setShowForm(true);
  };

  return (
    <Section title="Tabela de Preços" sub="Gerencie os preços sugeridos por categoria"
      headerContent={<Btn variant="primary" onClick={() => { setEditItem(null); setForm({ category: '', subcategory: '', basePriceDigits: '0', unit: 'fixo' }); setShowForm(true); }}>+ Novo Preço</Btn>}
    >
      {action.error && <Alert type="danger">{action.error}</Alert>}
      {showForm && (
        <Card style={{ marginBottom: 20, maxWidth: 500 }}>
          <div style={{ fontWeight: 700, color: C.text, marginBottom: 14 }}>{editItem ? 'Editar Preço' : 'Novo Preço'}</div>
          <div style={{ display: 'flex', gap: 10 }}>
            <div style={{ flex: 1 }}><FInput label="Categoria" placeholder="ex: Limpeza" value={form.category} onChange={set('category')} /></div>
            <div style={{ flex: 1 }}><FInput label="Subcategoria" placeholder="ex: Limpeza Regular" value={form.subcategory} onChange={set('subcategory')} /></div>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <div style={{ flex: 1 }}><MoneyInput label="Preço base (R$)" digits={form.basePriceDigits} onChange={(d) => setForm((f) => ({ ...f, basePriceDigits: d }))} /></div>
            <div style={{ flex: 1, marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 12, color: C.sub, marginBottom: 5, fontWeight: 600 }}>Unidade</label>
              <select value={form.unit} onChange={set('unit')} style={{ width: '100%', background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, padding: '10px 12px', color: C.text, fontSize: 14, fontFamily: C.font }}>
                <option value="fixo">Fixo</option>
                <option value="hora">Por hora</option>
                <option value="m2">Por m²</option>
              </select>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Btn variant="primary" loading={action.loading} onClick={save}>Salvar</Btn>
            <Btn variant="ghost" onClick={() => { setShowForm(false); setEditItem(null); }}>Cancelar</Btn>
          </div>
        </Card>
      )}
      {loading ? <Spinner /> : (data?.prices || []).map((p) => (
        <Card key={p.id} style={{ marginBottom: 8 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <span style={{ fontWeight: 700, color: C.text }}>{p.category}</span>
              <span style={{ color: C.sub }}> / {p.subcategory}</span>
              <div style={{ fontSize: 12, color: C.sub, marginTop: 2, display: 'flex', gap: 8 }}>
                <Chip type={p.unit} />
                {!p.active && <span style={{ color: C.danger }}>Inativo</span>}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <span style={{ fontWeight: 800, color: C.gold, fontSize: 16 }}>{fmtMoney(p.basePrice)}</span>
              <Btn variant="ghost" small onClick={() => startEdit(p)}>Editar</Btn>
              <Btn variant="danger" small onClick={() => remove(p.id)}>Excluir</Btn>
            </div>
          </div>
        </Card>
      ))}
    </Section>
  );
};

const priceFormDefaults = () => ({
  category: '',
  subcategory: '',
  basePriceDigits: '0',
  unit: 'fixo',
  scope: 'global',
  state: '',
  city: '',
  partnerId: '',
  active: true,
});

const AdminPriceTable = () => {
  const { data, loading, refetch } = useAdminPriceTable();
  const { data: partnersData } = useFetch(() => api.admin.partners(), []);
  const action = useAction();
  const partners = partnersData?.partners || [];
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState(priceFormDefaults());
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  const resetForm = () => {
    setEditItem(null);
    setForm(priceFormDefaults());
  };

  const save = async () => {
    try {
      const payload = {
        category: form.category,
        subcategory: form.subcategory,
        basePrice: parseMoney(form.basePriceDigits),
        unit: form.unit,
        scope: form.scope,
        state: form.state,
        city: form.city,
        partnerId: form.partnerId || null,
        active: form.active,
      };
      if (editItem) { await action.run(() => api.priceTable.update(editItem.id, payload)); }
      else { await action.run(() => api.priceTable.create(payload)); }
      setShowForm(false); resetForm(); refetch();
    } catch (_) {}
  };

  const remove = async (id) => {
    if (!window.confirm('Excluir este preco?')) return;
    await action.run(() => api.priceTable.remove(id));
    refetch();
  };

  const startEdit = (item) => {
    setEditItem(item);
    setForm({
      category: item.category || '',
      subcategory: item.subcategory || '',
      basePriceDigits: String(Math.round(parseFloat(item.basePrice || 0) * 100)),
      unit: item.unit || 'fixo',
      scope: item.scope || 'global',
      state: item.state || '',
      city: item.city || '',
      partnerId: item.partnerId || '',
      active: item.active !== false,
    });
    setShowForm(true);
  };

  const openCreate = () => { resetForm(); setShowForm(true); };
  const setScope = (e) => {
    const scope = e.target.value;
    setForm((f) => ({
      ...f,
      scope,
      city: scope === 'city' ? f.city : '',
      partnerId: scope === 'partner' ? f.partnerId : '',
    }));
  };

  return (
    <Section title="Tabela de Precos" sub="Gerencie precos globais, por UF, por cidade ou por parceiro"
      headerContent={<Btn variant="primary" onClick={openCreate}>+ Novo Preco</Btn>}
    >
      {action.error && <Alert type="danger">{action.error}</Alert>}
      {showForm && (
        <Card style={{ marginBottom: 20, maxWidth: 760 }}>
          <div style={{ fontWeight: 700, color: C.text, marginBottom: 14 }}>{editItem ? 'Editar Preco' : 'Novo Preco'}</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }}>
            <FInput label="Categoria" placeholder="ex: Limpeza" value={form.category} onChange={set('category')} />
            <FInput label="Subcategoria" placeholder="ex: Limpeza Regular" value={form.subcategory} onChange={set('subcategory')} />
            <MoneyInput label="Preco base (R$)" digits={form.basePriceDigits} onChange={(d) => setForm((f) => ({ ...f, basePriceDigits: d }))} />
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 12, color: C.sub, marginBottom: 5, fontWeight: 600 }}>Unidade</label>
              <select value={form.unit} onChange={set('unit')} style={{ width: '100%', background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, padding: '10px 12px', color: C.text, fontSize: 14, fontFamily: C.font }}>
                <option value="fixo">Fixo</option>
                <option value="hora">Por hora</option>
                <option value="m2">Por m2</option>
              </select>
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 12, color: C.sub, marginBottom: 5, fontWeight: 600 }}>Abrangencia</label>
              <select value={form.scope} onChange={setScope} style={{ width: '100%', background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, padding: '10px 12px', color: C.text, fontSize: 14, fontFamily: C.font }}>
                <option value="global">Global</option>
                <option value="state">UF</option>
                <option value="city">Cidade/UF</option>
                <option value="partner">Parceiro especifico</option>
              </select>
            </div>
            {(form.scope === 'state' || form.scope === 'city') && (
              <FInput label="UF" placeholder="MS" value={form.state} onChange={(e) => setForm((f) => ({ ...f, state: e.target.value.toUpperCase() }))} />
            )}
            {form.scope === 'city' && (
              <FInput label="Cidade" placeholder="Campo Grande" value={form.city} onChange={set('city')} />
            )}
            {form.scope === 'partner' && (
              <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', fontSize: 12, color: C.sub, marginBottom: 5, fontWeight: 600 }}>Parceiro</label>
                <select value={form.partnerId} onChange={set('partnerId')} style={{ width: '100%', background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, padding: '10px 12px', color: C.text, fontSize: 14, fontFamily: C.font }}>
                  <option value="">Selecione</option>
                  {partners.map((partner) => (
                    <option key={partner.id} value={partner.id}>
                      {partner.name} - {partner.city || 'UF inteira'}/{partner.state || '--'}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, color: C.text, fontSize: 13, fontWeight: 600 }}>
            <input type="checkbox" checked={form.active} onChange={set('active')} />
            Regra ativa
          </label>
          <div style={{ display: 'flex', gap: 8 }}>
            <Btn variant="primary" loading={action.loading} onClick={save}>Salvar</Btn>
            <Btn variant="ghost" onClick={() => { setShowForm(false); resetForm(); }}>Cancelar</Btn>
          </div>
        </Card>
      )}
      {loading ? <Spinner /> : (data?.prices || []).map((p) => (
        <Card key={p.id} style={{ marginBottom: 8 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <div>
              <span style={{ fontWeight: 700, color: C.text }}>{p.category}</span>
              <span style={{ color: C.sub }}> / {p.subcategory}</span>
              <div style={{ fontSize: 12, color: C.sub, marginTop: 5, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <Chip type={p.unit} />
                <span>Regra: {p.scopeLabel || priceScopeLabel(p, partners)}</span>
                {!p.active && <span style={{ color: C.danger }}>Inativo</span>}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ fontWeight: 800, color: C.gold, fontSize: 16 }}>{fmtMoney(p.basePrice)}</span>
              <Btn variant="ghost" small onClick={() => startEdit(p)}>Editar</Btn>
              <Btn variant="danger" small onClick={() => remove(p.id)}>Excluir</Btn>
            </div>
          </div>
        </Card>
      ))}
    </Section>
  );
};

const AdminFinance = () => {
  const { data: dash, loading: dl } = useAdminDashboard();
  const { data: jobs, loading: jl } = useAdminJobs('done');
  const [testingAsaas, setTestingAsaas] = useState(false);
  const [asaasResult, setAsaasResult] = useState(null);

  const testAsaas = async () => {
    setTestingAsaas(true);
    setAsaasResult(null);
    try {
      const response = await api.payments.testConnection();
      const balanceText = response.balance != null ? ` Saldo: R$ ${Number(response.balance).toFixed(2)}` : '';
      setAsaasResult({ ok: true, msg: response.message || `Asaas conectado.${balanceText}` });
    } catch (e) {
      setAsaasResult({ ok: false, msg: e.message || 'Erro na conexão' });
    } finally {
      setTestingAsaas(false);
    }
  };

  if (dl || jl) return <Section title="Financeiro"><Spinner /></Section>;
  const m = dash?.metrics || {};
  const fmt = (n) => Number(n || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 });

  const metrics = (
    <div>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <Metric label="GMV Total" value={`R$ ${fmt(m.gmv)}`} color={C.gold} icon="◆" />
        <Metric label="Receita da Plataforma" value={`R$ ${fmt(m.revenue)}`} color={C.accent} icon="◆" />
        <Metric label="Comissão vigente" value={currentCommissionLabel(m.commissionRate)} color={C.info} icon="%" />
        <Metric label="Aguardando Liberação" value={m.pendingReleasePayments || 0} color={C.danger} icon="⏳" />
      </div>
      <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 12 }}>
        <button
          onClick={testAsaas}
          disabled={testingAsaas}
          style={{ padding: '6px 14px', borderRadius: 6, border: `1px solid ${C.accent}`, background: C.accentDim, color: C.accent, cursor: 'pointer', fontWeight: 700, fontSize: 13, fontFamily: C.font }}
        >
          {testingAsaas ? 'Testando...' : '⚡ Testar Asaas'}
        </button>
        {asaasResult && (
          <span style={{ fontSize: 13, color: asaasResult.ok ? C.accent : C.danger, fontWeight: 600 }}>
            {asaasResult.msg}
          </span>
        )}
      </div>
    </div>
  );

  return (
    <Section title="Financeiro" sub="Resumo financeiro da plataforma" stickyContent={metrics}>
      <h3 style={{ color: C.text, fontWeight: 700, marginBottom: 12 }}>Serviços Concluídos</h3>
      {(jobs?.jobs || []).map((j) => (
        <Card key={j.id} style={{ marginBottom: 10 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontWeight: 700, color: C.text }}>{j.category} / {j.subcategory}</div>
              <div style={{ fontSize: 12, color: C.sub }}>Cliente: {j.client?.name} | Worker: {j.worker?.name}</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontWeight: 800, color: C.gold }}>{fmtMoney(j.price)}</div>
              <div style={{ fontSize: 12, color: C.accent }}>Comissão: {fmtMoney(j.commission)}</div>
              <div style={{ fontSize: 12, color: C.info }}>Worker: {fmtMoney(j.workerPayout)}</div>
            </div>
          </div>
        </Card>
      ))}
    </Section>
  );
};

const AdminFinanceV2 = () => {
  const { data: dash, loading: dashLoading, refetch: refetchDash } = useAdminDashboard();
  const { data: jobs, loading: jobsLoading, refetch: refetchJobs } = useAdminJobs('done');
  const { data: acceptedJobs, loading: acceptedLoading, refetch: refetchAccepted } = useAdminJobs('accepted');
  const { data: activeJobs, loading: activeLoading, refetch: refetchActive } = useAdminJobs('in_progress');
  const { data: pendingData, loading: pendingLoading, refetch: refetchPending } = useFetch(() => api.payments.pendingRelease(), []);
  const action = useAction();
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [testingAsaas, setTestingAsaas] = useState(false);
  const [asaasResult, setAsaasResult] = useState(null);

  const loading = dashLoading || jobsLoading || pendingLoading || acceptedLoading || activeLoading;
  if (loading) return <Section title="Financeiro"><Spinner /></Section>;

  const m = dash?.metrics || {};
  const doneJobs = jobs?.jobs || [];
  const pendingReleaseJobs = pendingData?.jobs || dash?.pixPending || [];
  const releasedJobs = doneJobs.filter((j) =>
    j.disbursementPath === 'direct' && j.pagseguroTransferId && ['PROCESSANDO', 'LIBERADO'].includes(j.releaseStatus));
  const selectableJobs = [...(acceptedJobs?.jobs || []), ...(activeJobs?.jobs || [])]
    .filter((j) => j.paymentStatus === 'paid' && !j.disbursementPath && !j.tokenUsed);

  const fmt = (n) => Number(n || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const refreshAll = () => {
    refetchDash();
    refetchJobs();
    refetchPending();
    refetchAccepted();
    refetchActive();
  };

  const selectDirectPath = async (jobId) => {
    try {
      await action.run(() => api.payments.selectDisbursementPath(jobId, 'direct'));
      refreshAll();
    } catch (_) {}
  };

  const approveRelease = async (jobId) => {
    try {
      await action.run(() => api.payments.approveRelease(jobId));
      refreshAll();
    } catch (_) {}
  };

  const openReject = (jobId) => {
    setRejectingId(jobId);
    setRejectReason('');
    action.clear();
  };

  const rejectRelease = async () => {
    try {
      await action.run(() => api.payments.rejectRelease(rejectingId, rejectReason));
      setRejectingId(null);
      refreshAll();
    } catch (_) {}
  };

  const confirmTransfer = async (jobId) => {
    try {
      await action.run(() => api.payments.confirmTransfer(jobId));
      refreshAll();
    } catch (_) {}
  };

  const testAsaas = async () => {
    setTestingAsaas(true);
    setAsaasResult(null);
    try {
      const response = await api.payments.testConnection();
      const lastOrderText = response.lastOrderId ? ` Último pedido: ${response.lastOrderId}` : '';
      const balanceText = response.balance != null ? ` Saldo: R$ ${Number(response.balance).toFixed(2)}` : '';
      setAsaasResult({ ok: true, message: response.message || `Asaas conectado.${lastOrderText}${balanceText}` });
    } catch (err) {
      setAsaasResult({ ok: false, message: err.message });
    } finally {
      setTestingAsaas(false);
    }
  };

  const financeMetrics = (
    <div>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
        <Metric label="GMV Total" value={`R$ ${fmt(m.gmv)}`} color={C.gold} icon="◆" />
        <Metric label="Receita da Plataforma" value={`R$ ${fmt(m.revenue)}`} color={C.accent} icon="◆" />
        <Metric label="Comissão vigente" value={currentCommissionLabel(m.commissionRate)} color={C.info} icon="%" />
        <Metric label="Aguardando Liberação" value={pendingReleaseJobs.length} color={C.gold} icon="⏳" />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <button
          onClick={testAsaas}
          disabled={testingAsaas}
          style={{ padding: '6px 14px', borderRadius: 6, border: `1px solid ${C.accent}`, background: C.accentDim, color: C.accent, cursor: 'pointer', fontWeight: 700, fontSize: 13, fontFamily: C.font }}
        >
          {testingAsaas ? 'Testando...' : '⚡ Testar Asaas'}
        </button>
        {asaasResult && (
          <span style={{ fontSize: 13, color: asaasResult.ok ? C.accent : C.danger, fontWeight: 600 }}>
            {asaasResult.message}
          </span>
        )}
      </div>
    </div>
  );

  return (
    <Section title="Financeiro" sub="Resumo financeiro e liberações PIX" stickyContent={financeMetrics}>
      {rejectingId && (
        <Modal title="Rejeitar Liberação" onClose={() => setRejectingId(null)}>
          {action.error && <Alert type="danger">{action.error}</Alert>}
          <FInput
            label="Motivo da rejeição"
            placeholder="Informe o motivo da rejeição deste pagamento..."
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
          />
          <div style={{ display: 'flex', gap: 8 }}>
            <Btn variant="danger" loading={action.loading} onClick={rejectRelease}>Confirmar Rejeição</Btn>
            <Btn variant="ghost" onClick={() => setRejectingId(null)}>Cancelar</Btn>
          </div>
        </Modal>
      )}

      {action.error && !rejectingId && <Alert type="danger">{action.error}</Alert>}

      <h3 style={{ color: C.text, fontWeight: 700, marginBottom: 12 }}>Escolha de Repasse Antes da Conclusao</h3>
      <Alert type="info">Sem escolha administrativa, o repasse segue pela carteira. A transferencia direta deve ser escolhida antes de o worker concluir a vaga.</Alert>
      {selectableJobs.map((j) => (
        <Card key={`disbursement-${j.id}`} style={{ marginBottom: 10 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center' }}>
            <span>{j.category} / {j.subcategory} — {j.id}</span>
            <Btn variant="primary" small loading={action.loading} onClick={() => selectDirectPath(j.id)}>Selecionar transferencia direta</Btn>
          </div>
        </Card>
      ))}

      <h3 style={{ color: C.text, fontWeight: 700, marginBottom: 12 }}>Pagamentos Pendentes de Liberação</h3>
      {pendingReleaseJobs.length === 0 ? (
        <Alert type="info">Nao ha pagamentos pendentes de liberacao no momento.</Alert>
      ) : pendingReleaseJobs.map((j) => (
        <Card key={j.id} style={{ marginBottom: 10 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontWeight: 700, color: C.text }}>{j.category} / {j.subcategory}</div>
              <div style={{ fontSize: 12, color: C.sub }}>
                Cliente: {j.client?.name || '—'} | Worker: {j.worker?.name || '—'}
              </div>
              <div style={{ fontSize: 12, color: C.sub, marginTop: 2 }}>
                Status: <Chip type={j.releaseStatus || 'PENDENTE_LIBERACAO'} />
              </div>
              {j.releaseRejectedReason && <div style={{ fontSize: 12, color: C.danger, marginTop: 6 }}>Motivo rejeicao: {j.releaseRejectedReason}</div>}
            </div>
            <div style={{ textAlign: 'right', minWidth: 210 }}>
              <div style={{ fontWeight: 900, color: C.gold, fontSize: 18 }}>{fmtMoney(j.price)}</div>
              <div style={{ fontSize: 12, color: C.sub }}>Comissao ({formatOptionalRate(jobCommissionRate(j))}): {fmtMoney(j.commission)}</div>
              <div style={{ fontSize: 12, color: C.accent }}>Repasse worker ({formatOptionalRate(jobWorkerPayoutRate(j))}): {fmtMoney(j.workerPayout)}</div>
              <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', marginTop: 8, flexWrap: 'wrap' }}>
                {j.disbursementPath === 'direct' ? <>
                  <Btn variant="primary" small loading={action.loading} onClick={() => approveRelease(j.id)}>Aprovar Liberacao</Btn>
                  <Btn variant="danger" small onClick={() => openReject(j.id)}>Rejeitar</Btn>
                </> : <span>Repasse pela carteira; transferencia direta indisponivel.</span>}
              </div>
            </div>
          </div>
        </Card>
      ))}

      <h3 style={{ color: C.text, fontWeight: 700, margin: '20px 0 12px' }}>Transferencias para Confirmacao pelo Provedor</h3>
      {releasedJobs.length === 0 ? (
        <Alert type="info">Nenhuma transferencia em processamento com identificador do provedor.</Alert>
      ) : releasedJobs.map((j) => (
        <Card key={`released-${j.id}`} style={{ marginBottom: 10 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontWeight: 700, color: C.text }}>{j.category} / {j.subcategory}</div>
              <div style={{ fontSize: 12, color: C.sub }}>Worker: {j.worker?.name || '—'}</div>
              <div style={{ marginTop: 4 }}><Chip type={j.releaseStatus || 'LIBERADO'} /></div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontWeight: 800, color: C.accent }}>{fmtMoney(j.workerPayout)}</div>
              <Btn variant="gold" small loading={action.loading} onClick={() => confirmTransfer(j.id)}>Confirmar Transferencia</Btn>
            </div>
          </div>
        </Card>
      ))}

      <h3 style={{ color: C.text, fontWeight: 700, margin: '20px 0 12px' }}>Servicos Concluidos</h3>
      {doneJobs.map((j) => (
        <Card key={`done-${j.id}`} style={{ marginBottom: 10 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontWeight: 700, color: C.text }}>{j.category} / {j.subcategory}</div>
              <div style={{ fontSize: 12, color: C.sub }}>Cliente: {j.client?.name || '—'} | Worker: {j.worker?.name || '—'}</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontWeight: 800, color: C.gold }}>{fmtMoney(j.price)}</div>
              <div style={{ fontSize: 12, color: C.sub }}><Chip type={j.releaseStatus || 'AGUARDANDO_PAGAMENTO'} /></div>
            </div>
          </div>
        </Card>
      ))}
    </Section>
  );
};

const AdminWithdrawals = () => {
  const { data, loading, refetch } = useAdminWithdrawals();
  const action = useAction();
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  const approve = async (id) => {
    try {
      await action.run(() => api.withdrawals.approve(id));
      refetch();
    } catch (_) {}
  };

  const openReject = (id) => {
    setRejectingId(id);
    setRejectReason('');
    action.clear();
  };

  const confirmReject = async () => {
    try {
      await action.run(() => api.withdrawals.reject(rejectingId, rejectReason));
      setRejectingId(null);
      refetch();
    } catch (_) {}
  };

  const statusColor = { pending: C.gold, approved: C.info, paid: C.accent, rejected: C.danger };
  const statusLabel = { pending: 'Pendente', approved: 'Processando', paid: 'Pago', rejected: 'Rejeitado' };

  return (
    <Section title="Saques" sub="Solicitações de saque dos colaboradores">
      {rejectingId && (
        <Modal title="Rejeitar Solicitação" onClose={() => setRejectingId(null)}>
          {action.error && <Alert type="danger">{action.error}</Alert>}
          <FInput
            label="Motivo da rejeição (opcional)"
            placeholder="Ex: chave PIX inválida, dados incorretos..."
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
          />
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <Btn variant="danger" loading={action.loading} onClick={confirmReject}>Confirmar Rejeição</Btn>
            <Btn variant="ghost" onClick={() => setRejectingId(null)}>Cancelar</Btn>
          </div>
        </Modal>
      )}
      {action.error && !rejectingId && <Alert type="danger">{action.error}</Alert>}
      {loading ? <Spinner /> : (data?.withdrawals || []).length === 0 ? (
        <Alert type="info">Nenhuma solicitação de saque no momento.</Alert>
      ) : (data?.withdrawals || []).map((w) => {
        const isPartner = w.requesterType === 'partner';
        const requesterName = isPartner ? (w.partner?.name || w.worker?.name || 'Parceiro') : (w.worker?.name || 'Colaborador');
        const requesterEmail = w.worker?.email || '';
        return (
        <Card key={w.id} style={{ marginBottom: 10 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontWeight: 700, color: C.text }}>{requesterName}</div>
              <div style={{ fontSize: 12, color: isPartner ? C.purple : C.sub, fontWeight: isPartner ? 700 : 400 }}>
                {isPartner ? 'Parceiro regional' : 'Colaborador'}{requesterEmail ? ` | ${requesterEmail}` : ''}
              </div>
              {isPartner && w.partner && (
                <div style={{ fontSize: 12, color: C.sub, marginTop: 2 }}>
                  Unidade: {[w.partner.city, w.partner.state].filter(Boolean).join('/') || 'sem regiao'}
                </div>
              )}
              <div style={{ fontSize: 12, color: C.sub, marginTop: 2 }}>
                Chave PIX {w.pixType ? `(${PIX_TYPES.find(t => t.value === w.pixType)?.label || w.pixType})` : ''}:
              </div>
              <div style={{ fontFamily: C.mono, fontSize: 13, color: C.accent, fontWeight: 700, marginBottom: 2 }}>{w.pixKey}</div>
              <div style={{ fontSize: 12, color: C.sub }}>Solicitado em {new Date(w.requestedAt).toLocaleDateString('pt-BR')}</div>
              {w.rejectionReason && <div style={{ fontSize: 12, color: C.danger, marginTop: 4 }}>Motivo: {w.rejectionReason}</div>}
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontWeight: 900, color: C.gold, fontSize: 20, marginBottom: 6 }}>R$ {Number(w.amount).toFixed(2)}</div>
              <div style={{ fontWeight: 700, fontSize: 12, color: statusColor[w.status] || C.sub, marginBottom: 8 }}>
                {statusLabel[w.status] || w.status}
              </div>
              {['pending', 'approved'].includes(w.status) && (
                <>
                  <div style={{ fontSize: 11, color: C.gold, marginBottom: 6, textAlign: 'right' }}>
                    {w.status === 'approved'
                      ? 'Transferencia em processamento. Confirme quando o valor sair.'
                      : 'Faça o PIX manualmente e confirme abaixo'}
                  </div>
                  <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                    <Btn variant="primary" small loading={action.loading} onClick={() => approve(w.id)}>
                      {w.status === 'approved' ? 'Confirmar Pago' : 'Marcar como Pago'}
                    </Btn>
                    {w.status === 'pending' && <Btn variant="danger" small onClick={() => openReject(w.id)}>Rejeitar</Btn>}
                  </div>
                </>
              )}
            </div>
          </div>
        </Card>
      ); })}
    </Section>
  );
};

const AdminSettings = () => {
  const { data, loading, refetch } = useFetch(() => api.admin.getSettings(), []);
  const { data: conductData, loading: conductLoading } = useFetch(() => api.admin.conductAcceptances(), []);
  const { user } = useAuth();
  const [resetResult, setResetResult] = useState(null);
  const [resetLoading, setResetLoading] = useState(false);
  const [changePwForm, setChangePwForm] = useState({ oldPassword: '', newPassword: '', confirm: '' });
  const [changePwMsg, setChangePwMsg] = useState('');
  const [changePwLoading, setChangePwLoading] = useState(false);
  const [forgotMsg, setForgotMsg] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('config');
  const [commissionPercent, setCommissionPercent] = useState('');
  const [commissionSaving, setCommissionSaving] = useState(false);
  const [commissionMessage, setCommissionMessage] = useState('');
  const monitoringUrl = safeExternalUrl(process.env.REACT_APP_GRAFANA_URL);

  const handleResetUser = async (id, email) => {
    if (!window.confirm(`Redefinir senha de ${email}?`)) return;
    setResetLoading(id); setResetResult(null);
    try {
      const res = await api.admin.resetUserPassword(id);
      setResetResult(res);
    } catch (err) { alert(err.message); }
    finally { setResetLoading(false); }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setChangePwMsg('');
    if (changePwForm.newPassword !== changePwForm.confirm) { setChangePwMsg('As novas senhas não coincidem.'); return; }
    if (changePwForm.newPassword.length < 6) { setChangePwMsg('Nova senha deve ter ao menos 6 caracteres.'); return; }
    setChangePwLoading(true);
    try {
      const res = await api.auth.changePassword(changePwForm.oldPassword, changePwForm.newPassword);
      setChangePwMsg(res.message);
      setChangePwForm({ oldPassword: '', newPassword: '', confirm: '' });
    } catch (err) { setChangePwMsg(err.message); }
    finally { setChangePwLoading(false); }
  };

  const handleForgotAdmin = async () => {
    setForgotLoading(true); setForgotMsg('');
    try {
      const res = await api.auth.forgotPassword(user.email);
      setForgotMsg(res.message);
    } catch (err) { setForgotMsg(err.message); }
    finally { setForgotLoading(false); }
  };

  const s = data?.settings || {};
  const accessLog = data?.accessLog || [];

  useEffect(() => {
    if (s.commission != null) setCommissionPercent(String(s.commission));
  }, [s.commission]);

  const handleCommissionSave = async (event) => {
    event.preventDefault();
    const value = Number(commissionPercent);
    if (!Number.isFinite(value) || value < 0 || value > 100) {
      setCommissionMessage('Informe uma comissao entre 0 e 100%.');
      return;
    }
    setCommissionSaving(true);
    setCommissionMessage('');
    try {
      const response = await api.admin.updateSettings({ commissionPercent: value });
      setCommissionPercent(String(response.settings?.commission ?? value));
      setCommissionMessage(response.message || 'Comissao atualizada com sucesso.');
      refetch();
    } catch (err) {
      setCommissionMessage(err.message);
    } finally {
      setCommissionSaving(false);
    }
  };

  const maskCpf = (cpf) => {
    if (!cpf) return '—';
    const d = cpf.replace(/\D/g, '');
    if (d.length !== 11) return cpf;
    return `${d.slice(0,3)}.${d.slice(3,6)}.${d.slice(6,9)}-${d.slice(9)}`;
  };

  const tabs = [
    { id: 'config', label: '⊙ Configurações' },
    { id: 'conduct', label: '✓ Aceites de conduta' },
    { id: 'passwords', label: '🔑 Senhas' },
    { id: 'access', label: '◉ Acessos' },
  ];

  return (
    <Section title="Configurações" sub="Segurança, senhas e configurações do sistema">
      <div style={{ display: 'flex', background: C.surface, borderRadius: 10, padding: 4, marginBottom: 24, border: `1px solid ${C.border}`, width: 'fit-content' }}>
        {tabs.map((t) => (
          <button key={t.id} onClick={() => setActiveTab(t.id)} style={{ padding: '8px 20px', borderRadius: 7, border: 'none', background: activeTab === t.id ? C.card : 'transparent', color: activeTab === t.id ? C.text : C.sub, fontWeight: 700, fontSize: 13, cursor: 'pointer', fontFamily: C.font }}>
            {t.label}
          </button>
        ))}
      </div>

      {activeTab === 'config' && (
        <>
          <Card style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
            <div>
              <div style={{ color: C.text, fontWeight: 800 }}>Monitoramento central</div>
              <div style={{ color: C.sub, fontSize: 12, marginTop: 4 }}>Saude, producao, desempenho e alertas do QServe.</div>
            </div>
            {monitoringUrl
              ? <Btn variant="primary" onClick={() => window.open(monitoringUrl, '_blank', 'noopener,noreferrer')}>Abrir Grafana</Btn>
              : <span style={{ color: C.muted, fontSize: 12 }}>URL de monitoramento nao configurada.</span>}
          </Card>
          <Card style={{ marginBottom: 16 }}>
            <form onSubmit={handleCommissionSave}>
              <div style={{ color: C.text, fontWeight: 800, marginBottom: 6 }}>Variacao da comissao da plataforma</div>
              <div style={{ color: C.sub, fontSize: 12, marginBottom: 12 }}>
                Percentual aplicado a novos servicos e a futuras reprecificacoes. Servicos ja concluidos preservam os valores contabilizados.
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'minmax(220px, 1fr) 110px auto', gap: 12, alignItems: 'end' }}>
                <div style={{ marginBottom: 14 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', color: C.sub, fontSize: 12, fontWeight: 700, marginBottom: 8 }}>
                    <span>Escala móvel</span><strong style={{ color: C.gold }}>{Number(commissionPercent || 0).toFixed(1).replace('.', ',')}%</strong>
                  </div>
                  <input
                    aria-label="Comissão da plataforma"
                    type="range"
                    min="0"
                    max="100"
                    step="0.5"
                    value={commissionPercent || 0}
                    onChange={(event) => setCommissionPercent(event.target.value)}
                    style={{ width: '100%', accentColor: C.gold, cursor: 'pointer' }}
                  />
                  <div style={{ display: 'flex', justifyContent: 'space-between', color: C.muted, fontSize: 10, marginTop: 4 }}><span>0%</span><span>100%</span></div>
                </div>
                <FInput label="Valor exato (%)" type="number" min="0" max="100" step="0.1" inputMode="decimal" value={commissionPercent} onChange={(event) => setCommissionPercent(event.target.value)} />
                <Btn variant="gold" loading={commissionSaving} style={{ marginBottom: 14 }}>Aplicar comissão</Btn>
              </div>
              {commissionMessage && <Alert type={commissionMessage.toLowerCase().includes('sucesso') ? 'success' : 'danger'}>{commissionMessage}</Alert>}
            </form>
          </Card>
          {loading ? <Spinner /> : [
            { label: 'Comissão da plataforma', value: s.commission == null ? 'Não informado' : `${s.commission}%`, icon: '◆', color: C.gold },
            { label: 'Rate limit global', value: s.rateLimitGlobal || '—', icon: '⊙', color: C.info },
            { label: 'Rate limit login', value: s.rateLimitLogin || '—', icon: '🔑', color: C.danger },
            { label: 'JWT expiração', value: s.jwtExpiration || '7d', icon: '🔒', color: C.purple },
            { label: 'CORS permitidos', value: s.corsOrigins || '—', icon: '◎', color: C.sub },
            { label: 'Hash de senha', value: `bcrypt rounds ${s.bcryptRounds || 12}`, icon: '✦', color: C.accent },
            { label: 'Banco de dados', value: s.database || 'PostgreSQL', icon: '◈', color: C.accent },
          ].map((item) => (
            <Card key={item.label} style={{ marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <span style={{ color: item.color, fontSize: 18 }}>{item.icon}</span>
                <span style={{ color: C.sub, fontSize: 14 }}>{item.label}</span>
              </div>
              <span style={{ fontWeight: 700, color: item.color, fontFamily: C.mono, fontSize: 14 }}>{item.value}</span>
            </Card>
          ))}
        </>
      )}

      {activeTab === 'conduct' && (
        <>
          <Alert type="info">Cada registro preserva a versão e o conteúdo integral aceito, além da data, IP, navegador e situação do envio por e-mail.</Alert>
          {conductLoading ? <Spinner /> : (conductData?.acceptances || []).length === 0 ? (
            <Alert type="warn">Nenhum aceite foi arquivado até o momento.</Alert>
          ) : (conductData?.acceptances || []).map((acceptance) => (
            <Card key={acceptance.id} style={{ marginBottom: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 14, flexWrap: 'wrap' }}>
                <div>
                  <div style={{ color: C.text, fontWeight: 800 }}>{acceptance.user?.name || acceptance.recipient}</div>
                  <div style={{ color: C.sub, fontSize: 12, marginTop: 3 }}>{acceptance.user?.email || acceptance.recipient} · {acceptance.user?.role || 'usuário'}{acceptance.user?.partner?.name ? ` · ${acceptance.user.partner.name}` : ''}</div>
                  <div style={{ color: C.muted, fontSize: 11, marginTop: 4 }}>Versão {acceptance.termVersion} · {new Date(acceptance.acceptedAt).toLocaleString('pt-BR')} · IP {acceptance.ipAddress || 'não registrado'}</div>
                </div>
                <div style={{ color: acceptance.deliveryStatus === 'sent' ? C.accent : acceptance.deliveryStatus === 'failed' ? C.danger : C.gold, fontSize: 12, fontWeight: 800 }}>
                  E-mail: {acceptance.deliveryStatus === 'sent' ? 'enviado' : acceptance.deliveryStatus === 'failed' ? 'falhou' : 'pendente/não configurado'}
                </div>
              </div>
            </Card>
          ))}
        </>
      )}

      {activeTab === 'passwords' && (
        <>
          {resetResult && (
            <Alert type="warn">
              <strong>Senha temp. para {resetResult.userEmail}:</strong>
              <span style={{ fontFamily: C.mono, color: C.gold, fontWeight: 700, marginLeft: 8 }}>{resetResult.tempPassword}</span>
              <button onClick={() => setResetResult(null)} style={{ marginLeft: 12, background: 'none', border: 'none', color: C.sub, cursor: 'pointer' }}>✕</button>
            </Alert>
          )}

          <h3 style={{ color: C.text, fontWeight: 700, marginBottom: 12, fontSize: 15 }}>Alterar minha senha</h3>
          <Card style={{ marginBottom: 20 }}>
            <form onSubmit={handleChangePassword}>
              <FInput label="Senha atual" type="password" placeholder="••••••••" value={changePwForm.oldPassword}
                onChange={(e) => setChangePwForm((f) => ({ ...f, oldPassword: e.target.value }))} />
              <FInput label="Nova senha" type="password" placeholder="••••••••" value={changePwForm.newPassword}
                onChange={(e) => setChangePwForm((f) => ({ ...f, newPassword: e.target.value }))} />
              <FInput label="Confirmar nova senha" type="password" placeholder="••••••••" value={changePwForm.confirm}
                onChange={(e) => setChangePwForm((f) => ({ ...f, confirm: e.target.value }))} />
              {changePwMsg && <Alert type={changePwMsg.includes('sucesso') ? 'success' : 'danger'}>{changePwMsg}</Alert>}
              <Btn variant="primary" full loading={changePwLoading}>Alterar Senha</Btn>
            </form>
            <div style={{ marginTop: 12 }}>
              <Btn variant="ghost" full loading={forgotLoading} onClick={handleForgotAdmin}>Esqueci minha senha (enviar link por e-mail)</Btn>
              {forgotMsg && <div style={{ marginTop: 8 }}><Alert type="info">{forgotMsg}</Alert></div>}
            </div>
          </Card>

          <h3 style={{ color: C.text, fontWeight: 700, marginBottom: 16, fontSize: 15 }}>Senhas dos usuários</h3>
          <Alert type="info" style={{ marginBottom: 16 }}>
            A senha temporária aparece somente na resposta da redefinição. Depois de fechar o aviso, não poderá ser recuperada; gere outra se necessário.
          </Alert>
          {loading ? <Spinner /> : accessLog.map((u) => {
            return (
              <Card key={u.id} style={{ marginBottom: 8 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, color: C.text, fontSize: 14 }}>{u.name}</div>
                    <div style={{ fontSize: 12, color: C.sub }}>{u.email} · <span style={{ color: roleColor[u.role] || C.sub }}>{u.role}</span></div>
                    {u.lastLoginAt && <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>Último login: {new Date(u.lastLoginAt).toLocaleString('pt-BR')}</div>}
                  </div>
                  <Btn variant="gold" small loading={resetLoading === u.id} onClick={() => handleResetUser(u.id, u.email)}>🔑 Redefinir</Btn>
                </div>
              </Card>
            );
          })}
        </>
      )}

      {activeTab === 'access' && (
        <>
          <h3 style={{ color: C.text, fontWeight: 700, marginBottom: 12, fontSize: 15 }}>Log de acessos</h3>
          {loading ? <Spinner /> : accessLog.map((u) => (
            <Card key={u.id} style={{ marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <Avatar name={u.name} size={32} color={roleColor[u.role] || C.accent} src={u.profilePhoto} />
                <div>
                  <div style={{ fontWeight: 600, color: C.text, fontSize: 13 }}>{u.name}</div>
                  <div style={{ fontSize: 11, color: C.sub }}>{u.email}</div>
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <Chip type={u.active ? 'active' : 'cancelled'} label={u.active ? 'Ativo' : 'Inativo'} />
                <div style={{ fontSize: 11, color: u.lastLoginAt ? C.text : C.muted, marginTop: 4 }}>
                  {u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleString('pt-BR') : 'Nunca logou'}
                </div>
              </div>
            </Card>
          ))}
        </>
      )}
    </Section>
  );
};

// ━━━ CLIENT SCREENS ━━━

const defaultCategories = {
  'Diarista':    ['Limpeza regular'],
  'Limpeza':     ['Limpeza Regular', 'Pós-obra', 'Vidros', 'Fachada'],
  'Elétrica':    ['Instalação', 'Quadro Elétrico', 'Tomadas', 'Iluminação'],
  'Encanamento': ['Reparo de Vazamento', 'Instalação', 'Entupimento', 'Aquecedor'],
  'Jardinagem':  ['Corte de Grama', 'Podagem', 'Paisagismo', 'Irrigação'],
  'Pintura':     ['Interna', 'Externa', 'Textura', 'Grafiato'],
  'Reformas':    ['Montagem de Móveis', 'Revestimento', 'Piso', 'Drywall', 'Teto'],
  'Eventos':     ['Operador de Som', 'Operador de Iluminação', 'Técnico de Palco', 'Roadie', 'DJ', 'Fotógrafo de Eventos', 'Segurança'],
};

const catIcons = { Diarista: 'D', Limpeza: '🧹', Elétrica: '⚡', Encanamento: '🔧', Jardinagem: '🌿', Pintura: '🎨', Reformas: '🏗', Eventos: '🎤' };

const buildServiceCategories = (prices = []) => {
  const categoryMap = new Map();

  const addCategory = (category, subcategory) => {
    const categoryLabel = repairText(category).trim();
    const subcategoryLabel = repairText(subcategory).trim();
    const categoryKey = serviceKey(categoryLabel);
    const subcategoryKey = serviceKey(subcategoryLabel);
    if (!categoryKey || !subcategoryKey) return;

    if (!categoryMap.has(categoryKey)) {
      categoryMap.set(categoryKey, { label: categoryLabel, subcategories: new Map() });
    }

    const entry = categoryMap.get(categoryKey);
    if (!entry.subcategories.has(subcategoryKey)) {
      entry.subcategories.set(subcategoryKey, subcategoryLabel);
    }
  };

  Object.entries(defaultCategories).forEach(([category, subcategories]) => {
    subcategories.forEach((subcategory) => addCategory(category, subcategory));
  });

  prices.forEach((price) => {
    if (!price || price.active === false || priceScope(price) === 'partner') return;
    addCategory(price.category, price.subcategory);
  });

  return Array.from(categoryMap.values()).reduce((acc, entry) => {
    acc[entry.label] = Array.from(entry.subcategories.values());
    return acc;
  }, {});
};

const PricePreviewCard = ({ label, price, active, onPreview, onSelect }) => {
  const hasPrice = price && Number(price.basePrice) > 0;
  const handleClick = () => {
    const touchOnly = typeof window !== 'undefined'
      && window.matchMedia
      && window.matchMedia('(hover: none)').matches;
    if (touchOnly && !active) {
      onPreview();
      return;
    }
    onSelect();
  };
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onSelect();
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      className={`qs-price-card ${active ? 'qs-price-card-active' : ''}`}
      onMouseEnter={onPreview}
      onFocus={onPreview}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      title={hasPrice ? `${label}: ${fmtMoney(price.basePrice)} ${priceUnitLabel(price.unit)}` : `${label}: preco sob consulta`}
    >
      <div style={{ fontWeight: 800, color: C.text, minHeight: 42, display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1.25 }}>
        {label}
      </div>
      <div className="qs-price-hint" style={{ marginTop: 8, fontSize: 11, color: C.sub, fontWeight: 700, letterSpacing: 0.3, textTransform: 'uppercase' }}>
        Ver preco antes de contratar
      </div>
      <div className="qs-price-preview" aria-live="polite">
        {hasPrice ? (
          <>
            <div style={{ fontSize: 11, color: C.sub, marginBottom: 3 }}>A partir de</div>
            <div style={{ fontWeight: 900, color: C.gold, fontSize: 20 }}>{fmtMoney(price.basePrice)}</div>
            <div style={{ fontSize: 11, color: C.sub, marginTop: 3 }}>{priceUnitLabel(price.unit)}</div>
          </>
        ) : (
          <>
            <div style={{ fontWeight: 900, color: C.gold, fontSize: 15 }}>Sob consulta</div>
            <div style={{ fontSize: 11, color: C.sub, marginTop: 5 }}>O valor sera definido no orcamento.</div>
          </>
        )}
      </div>
    </div>
  );
};

const ClientRequest = () => {
  const creationAttempt = useRef(null);
  if (!creationAttempt.current) creationAttempt.current = createJobCreationAttempt(api.jobs.create);
  const [step, setStep] = useState(1);
  const [cat, setCat] = useState('');
  const [sub, setSub] = useState('');
  const [previewSub, setPreviewSub] = useState('');
  const [priceDigits, setPriceDigits] = useState('0');
  const [form, setForm] = useState({ scheduledDate: '', street: '', number: '', city: '', state: '', zip: '', notes: '', paymentMethod: 'pix', installments: 1, isQuote: false, serviceMode: 'onsite' });
  const [result, setResult] = useState(null);
  const [paymentResult, setPaymentResult] = useState(null);
  const action = useAction();
  const { data: ptData } = usePriceTable();
  const serviceCategories = useMemo(() => buildServiceCategories(ptData?.prices || []), [ptData]);
  const [routingPreview, setRoutingPreview] = useState({ loading: false, partner: null, message: '' });
  const priceRegion = useMemo(() => ({
    city: form.serviceMode === 'remote' ? '' : form.city,
    state: form.serviceMode === 'remote' ? '' : form.state,
    partnerId: form.serviceMode === 'remote' ? '' : routingPreview.partner?.id || '',
  }), [form.city, form.state, form.serviceMode, routingPreview.partner?.id]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  const selectSub = (s) => {
    setSub(s);
    const match = findServicePrice(ptData?.prices || [], cat, s, priceRegion);
    setPriceDigits(match ? String(Math.round(parseFloat(match.basePrice) * 100)) : '0');
    setStep(3);
  };

  useEffect(() => {
    if (form.serviceMode === 'remote') {
      setRoutingPreview({ loading: false, partner: null, message: '' });
      return undefined;
    }
    const zipDigits = String(form.zip || '').replace(/\D/g, '');
    const hasEnoughAddress = zipDigits.length >= 5 || (form.city.trim() && form.state.trim());
    if (!hasEnoughAddress) {
      setRoutingPreview({ loading: false, partner: null, message: '' });
      return undefined;
    }

    let cancelled = false;
    setRoutingPreview((current) => ({ ...current, loading: true }));
    const timer = setTimeout(() => {
      api.partners.resolveLocation({ zip: form.zip, city: form.city, state: form.state })
        .then((response) => {
          if (!cancelled) setRoutingPreview({ loading: false, partner: response.partner || null, message: response.message || '' });
        })
        .catch((err) => {
          if (!cancelled) setRoutingPreview({ loading: false, partner: null, message: err.message || 'Triagem indisponivel no momento.' });
        });
    }, 350);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [form.zip, form.city, form.state, form.serviceMode]);

  useEffect(() => {
    if (!cat || !sub) return;
    const match = findServicePrice(ptData?.prices || [], cat, sub, priceRegion);
    setPriceDigits(match ? String(Math.round(parseFloat(match.basePrice) * 100)) : '0');
  }, [cat, sub, priceRegion, ptData]);

  const selectedPrice = findServicePrice(ptData?.prices || [], cat, sub, priceRegion);
  const priceVal = parseMoney(priceDigits);
  const installSim = cardInstallments(priceVal);

  const submit = async () => {
    if (creationAttempt.current.isPending()) return;
    try {
      await action.run(async () => {
        const submitPrice = findServicePrice(ptData?.prices || [], cat, sub, priceRegion);
        const submitPriceVal = submitPrice ? parseFloat(submitPrice.basePrice) : 0;
        const res = await creationAttempt.current.submit({
          category: cat, subcategory: sub, isQuote: !submitPrice,
          scheduledDate: form.scheduledDate || null,
          address: form.serviceMode === 'remote' ? {} : { street: form.street, number: form.number, city: form.city, state: form.state, zip: form.zip },
          serviceMode: form.serviceMode,
          notes: form.notes, price: submitPriceVal, paymentMethod: form.paymentMethod, installments: form.installments,
        });
        setResult(res.job);
        setPaymentResult(res.payment || null);
      });
    } catch (_) {}
  };

  const [showPixModal, setShowPixModal] = useState(true);
  const safePaymentUrl = safeExternalUrl(paymentResult?.paymentUrl);

  if (result) return (
    <Section title="Vaga Criada!" sub="Sua solicitação foi enviada com sucesso">
      {!result.isQuote && result.paymentMethod === 'pix' && showPixModal && (
        <PixModal
          job={result}
          payment={paymentResult}
          onClose={() => setShowPixModal(false)}
          onPaid={(updatedJob, updatedPayment) => {
            if (updatedJob) setResult(updatedJob);
            if (updatedPayment) setPaymentResult(updatedPayment);
          }}
        />
      )}
      {result.isQuote && result.price <= 0 ? (
        <Alert type="info">Orcamento solicitado. Um colaborador podera aceitar a vaga e propor o valor, sem cobranca antecipada.</Alert>
      ) : result.paymentStatus === 'paid' ? (
        <Alert type="success">Status: PAGO. A vaga ja esta ABERTA para os colaboradores.</Alert>
      ) : paymentResult?.error ? (
        <Alert type="danger">
          Falha ao gerar cobranca no Asaas: {paymentResult.error}. A vaga ficou pendente e nao aparece para colaboradores ate o pagamento ser corrigido.
        </Alert>
      ) : (
        <Alert type="warn">
          {paymentResult?.mock
            ? 'Status: AGUARDANDO_PAGAMENTO. Como este ambiente esta em mock, use o botao de simular pagamento no modal PIX para abrir a vaga.'
            : 'Status: AGUARDANDO_PAGAMENTO. A vaga fica ABERTA somente depois da confirmacao do pagamento pelo Asaas.'}
        </Alert>
      )}
      
      <Card>
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontWeight: 700, color: C.text, fontSize: 16 }}>{result.category} / {result.subcategory}</div>
          <div style={{ color: C.sub, fontSize: 13, marginTop: 4 }}>{result.city}, {result.state}</div>
        </div>
        <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
          <div><div style={{ fontSize: 11, color: C.sub }}>Valor</div><div style={{ fontWeight: 800, color: C.gold, fontSize: 20 }}>{result.isQuote && result.price <= 0 ? 'A definir' : fmtMoney(result.price)}</div></div>
          {!result.isQuote && <div><div style={{ fontSize: 11, color: C.sub }}>Pagamento</div><div style={{ marginTop: 4 }}><Chip type={result.paymentMethod} /></div></div>}
        </div>
        {!result.isQuote && result.paymentMethod === 'pix' && (
          <div style={{ marginBottom: 16 }}>
            <Btn variant="primary" full onClick={() => setShowPixModal(true)}>Ver QR Code PIX</Btn>
          </div>
        )}
        {result.paymentMethod !== 'pix' && safePaymentUrl && result.paymentStatus !== 'paid' && (
          <div style={{ marginBottom: 16 }}>
            <Btn variant="primary" full onClick={() => window.open(safePaymentUrl, '_blank', 'noopener,noreferrer')}>
              Abrir pagamento no Asaas
            </Btn>
          </div>
        )}
        {paymentResult?.error && (
          <div style={{ marginBottom: 16 }}>
            <Btn variant="primary" full onClick={() => api.payments.createCharge(result.id).then(setPaymentResult).catch((e) => setPaymentResult({ error: e.message }))}>
              Tentar gerar cobranca novamente
            </Btn>
          </div>
        )}
        {result.paymentStatus === 'paid' && <TokenDisplay token={result.token} used={false} />}
        <div style={{ marginTop: 16 }}>
          <Btn variant="primary" onClick={() => { creationAttempt.current.cancel(); setResult(null); setStep(1); setCat(''); setSub(''); setPreviewSub(''); setPriceDigits('0'); }}>Solicitar outro serviço</Btn>
        </div>
      </Card>
    </Section>
  );

  return (
    <Section title="Solicitar Serviço" sub={`Etapa ${step} de 3`}>
      <div style={{ display: 'flex', gap: 0, marginBottom: 28 }}>
        {['Categoria', 'Subcategoria', 'Detalhes'].map((s, i) => (
          <div key={s} style={{ flex: 1, display: 'flex', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: step > i + 1 ? C.accent : step === i + 1 ? C.accent : C.border, color: step >= i + 1 ? '#000' : C.sub, fontWeight: 800, fontSize: 13 }}>
                {step > i + 1 ? '✓' : i + 1}
              </div>
              <span style={{ fontSize: 13, fontWeight: step === i + 1 ? 700 : 400, color: step === i + 1 ? C.text : C.sub }}>{s}</span>
            </div>
            {i < 2 && <div style={{ flex: 1, height: 1, background: step > i + 1 ? C.accent : C.border, margin: '0 12px' }} />}
          </div>
        ))}
      </div>

      {step === 1 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 12 }}>
          {Object.keys(serviceCategories).map((c) => (
            <Card key={c} onClick={() => { setCat(c); setSub(''); setPreviewSub(''); setPriceDigits('0'); setStep(2); }} style={{ textAlign: 'center', padding: 16 }}>
              <div style={{ fontSize: 24, marginBottom: 8 }}>{catIcons[c] || '🔨'}</div>
              <div style={{ fontWeight: 700, color: C.text, fontSize: 14 }}>{c}</div>
            </Card>
          ))}
        </div>
      )}

      {step === 2 && (
        <div>
          <button onClick={() => setStep(1)} style={{ background: 'none', border: 'none', color: C.accent, cursor: 'pointer', marginBottom: 16, fontFamily: C.font }}>← Voltar</button>
          <p style={{ color: C.sub, marginBottom: 16 }}>Categoria: <strong style={{ color: C.text }}>{cat}</strong></p>
          <p style={{ color: C.sub, marginBottom: 16, fontSize: 13 }}>
            No computador, passe o mouse sobre um servico para ver o preco. No celular, toque uma vez para visualizar e toque novamente para selecionar.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12 }}>
            {(serviceCategories[cat] || []).map((s) => {
              const price = findServicePrice(ptData?.prices || [], cat, s);
              return (
                <PricePreviewCard
                  key={s}
                  label={s}
                  price={price}
                  active={previewSub === s}
                  onPreview={() => setPreviewSub(s)}
                  onSelect={() => selectSub(s)}
                />
              );
            })}
          </div>
        </div>
      )}

      {step === 3 && (
        <div style={{ maxWidth: 560 }}>
          <button onClick={() => setStep(2)} style={{ background: 'none', border: 'none', color: C.accent, cursor: 'pointer', marginBottom: 16, fontFamily: C.font }}>← Voltar</button>
          <p style={{ color: C.sub, marginBottom: 16 }}><strong style={{ color: C.text }}>{cat}</strong> / {sub}</p>
          {action.error && <Alert type="danger">{action.error}</Alert>}
          <FInput label="Data desejada" type="date" value={form.scheduledDate} onChange={set('scheduledDate')} />
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 12, color: C.sub, marginBottom: 6 }}>Modalidade do serviço</label>
            <select value={form.serviceMode} onChange={set('serviceMode')} style={{ width: '100%', background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, padding: '10px 12px', color: C.text }}>
              <option value="onsite">Presencial</option>
              <option value="remote">Remoto — atendimento nacional</option>
            </select>
          </div>
          {form.serviceMode === 'onsite' && <>
          <FInput label="Rua" placeholder="Nome da rua" value={form.street} onChange={set('street')} />
          <div style={{ display: 'flex', gap: 10 }}>
            <div style={{ flex: 1 }}><FInput label="Número" placeholder="123" value={form.number} onChange={set('number')} /></div>
            <div style={{ flex: 1 }}><FInput label="CEP" placeholder="00000-000" value={form.zip} onChange={set('zip')} /></div>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <div style={{ flex: 2 }}><FInput label="Cidade" placeholder="São Paulo" value={form.city} onChange={set('city')} /></div>
            <div style={{ flex: 1 }}><FInput label="UF" placeholder="SP" value={form.state} onChange={set('state')} /></div>
          </div>

          {(routingPreview.loading || routingPreview.message) && (
            <Alert type={routingPreview.partner ? 'success' : 'info'}>
              {routingPreview.loading
                ? 'Localizando unidade responsavel por este endereco...'
                : routingPreview.partner
                  ? `Triagem regional: ${routingPreview.partner.name}${routingPreview.partner.city || routingPreview.partner.state ? ` - ${[routingPreview.partner.city, routingPreview.partner.state].filter(Boolean).join('/')}` : ''}.`
                  : 'Triagem regional: sem unidade especifica encontrada; a administracao direcionara a vaga.'}
            </Alert>
          )}
          </>}

          {selectedPrice ? (
            <>
              <MoneyInput label="Valor do serviço (R$)" digits={priceDigits} onChange={setPriceDigits} disabled />
              <div style={{ marginTop: -6, marginBottom: 14, color: C.sub, fontSize: 12, lineHeight: 1.45 }}>
                Regra aplicada: <strong style={{ color: C.text }}>{priceScopeLabel(selectedPrice)}</strong>. O servidor recalcula este valor pela tabela antes de gerar a cobranca.
              </div>
            </>
          ) : (
            <Alert type="info">
              Este servico e sob consulta. Nenhum valor sera cobrado agora; um colaborador aceitara a solicitacao e enviara o orcamento.
            </Alert>
          )}

          {/* Forma de pagamento */}
          {selectedPrice && <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 12, color: C.sub, marginBottom: 5, fontWeight: 600 }}>Forma de pagamento</label>
            <div style={{ display: 'flex', gap: 8 }}>
              {['pix', 'card', 'boleto'].map((m) => (
                <button key={m} onClick={() => setForm((f) => ({ ...f, paymentMethod: m, installments: 1 }))}
                  style={{ flex: 1, padding: '10px', borderRadius: 8, border: `2px solid ${form.paymentMethod === m ? C.accent : C.border}`, background: form.paymentMethod === m ? C.accentDim : C.surface, color: form.paymentMethod === m ? C.accent : C.sub, fontWeight: 700, cursor: 'pointer', fontFamily: C.font, fontSize: 13 }}>
                  {m === 'pix' ? '⚡ PIX' : m === 'card' ? '💳 Cartão' : '📄 Boleto'}
                </button>
              ))}
            </div>
          </div>}

          {/* Previa do total tabelado; o servidor confirma o valor final ao criar a cobranca. */}
          {selectedPrice && priceVal > 0 && (
            <Card style={{ marginBottom: 16, background: C.surface }}>
              <div style={{ fontWeight: 700, color: C.text, marginBottom: 12 }}>Prévia de Pagamento</div>
              {form.paymentMethod === 'pix' && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ color: C.sub }}>⚡ PIX — sem taxa</span>
                  <span style={{ fontWeight: 900, color: C.accent, fontSize: 18 }}>R$ {priceVal.toFixed(2)}</span>
                </div>
              )}
              {form.paymentMethod === 'boleto' && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ color: C.sub }}>📄 Boleto — sem juros</span>
                  <span style={{ fontWeight: 900, color: C.gold, fontSize: 18 }}>R$ {priceVal.toFixed(2)}</span>
                </div>
              )}
              {form.paymentMethod === 'card' && (
                <div>
                  <div style={{ fontSize: 12, color: C.sub, marginBottom: 8 }}>Escolha o número de parcelas:</div>
                  {installSim.map((inst) => (
                    <div
                      key={inst.n}
                      onClick={() => setForm((f) => ({ ...f, installments: inst.n }))}
                      style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', borderRadius: 8, marginBottom: 6, cursor: 'pointer', border: `2px solid ${form.installments === inst.n ? C.info : C.border}`, background: form.installments === inst.n ? C.infoDim : 'transparent' }}
                    >
                      <span style={{ fontWeight: 700, color: form.installments === inst.n ? C.info : C.sub }}>
                        {inst.n}x (sem acréscimo no valor do serviço)
                      </span>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontWeight: 900, color: form.installments === inst.n ? C.info : C.text }}>
                          {inst.n > 1 && inst.lastInstallment !== inst.installment
                            ? `${inst.n - 1}x R$ ${inst.installment.toFixed(2)} + 1x R$ ${inst.lastInstallment.toFixed(2)}`
                            : `${inst.n}x R$ ${inst.installment.toFixed(2)}`}
                        </div>
                        <div style={{ fontSize: 11, color: C.sub }}>Total: R$ {inst.total.toFixed(2)}</div>
                      </div>
                    </div>
                  ))}
                  <div style={{ fontSize: 11, color: C.sub, marginTop: 8 }}>
                    Prévia sem acréscimo do QServe. O total definitivo é confirmado pelo servidor na criação da cobrança.
                  </div>
                </div>
              )}
            </Card>
          )}

          <FInput label="Observações (opcional)" placeholder="Detalhes do serviço..." value={form.notes} onChange={set('notes')} />
          <Btn variant="primary" full loading={action.loading} disabled={action.loading} onClick={submit}>{selectedPrice ? 'Solicitar Serviço' : 'Solicitar Orcamento'}</Btn>
        </div>
      )}
    </Section>
  );
};

const StarRater = ({ onRate }) => {
  const [hover, setHover] = useState(0);
  const [selected, setSelected] = useState(0);
  const [comment, setComment] = useState('');
  const action = useAction();
  const submit = async () => { await action.run(() => onRate(selected, comment)); };
  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ display: 'flex', gap: 4, marginBottom: 8 }}>
        {[1, 2, 3, 4, 5].map((i) => (
          <span key={i} onClick={() => setSelected(i)} onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(0)}
            style={{ fontSize: 24, cursor: 'pointer', color: i <= (hover || selected) ? C.gold : C.muted, transition: 'color 0.1s' }}>★</span>
        ))}
      </div>
      {selected > 0 && (
        <>
          <FInput placeholder="Comentário (opcional)" value={comment} onChange={(e) => setComment(e.target.value)} />
          {action.error && <Alert type="danger">{action.error}</Alert>}
          {action.success && <Alert type="success">Avaliação enviada!</Alert>}
          {!action.success && <Btn variant="gold" small loading={action.loading} onClick={submit}>Enviar Avaliação</Btn>}
        </>
      )}
    </div>
  );
};

const ClientMyJobs = () => {
  const { data, loading, refetch } = useJobs();
  const [reviewed, setReviewed] = useState({});
  const [reviewJob, setReviewJob] = useState(null);
  const [pixJob, setPixJob] = useState(null);

  // Auto-abre modal de avaliação para a primeira vaga concluída sem avaliação
  useEffect(() => {
    if (!data?.jobs) return;
    const pending = data.jobs.find((j) => j.status === 'done' && !j.clientRatingScore && !reviewed[j.id]);
    if (pending && !reviewJob) setReviewJob(pending);
  }, [data]); // eslint-disable-line

  if (loading) return <Section title="Minhas Vagas"><Spinner /></Section>;
  return (
    <Section title="Minhas Vagas" sub="Acompanhe todos os seus serviços">
      {reviewJob && (
        <ReviewModal
          job={reviewJob}
          onClose={() => setReviewJob(null)}
          onDone={() => { setReviewed((r) => ({ ...r, [reviewJob.id]: true })); setReviewJob(null); refetch(); }}
        />
      )}
      {pixJob && (
        <PixModal
          job={pixJob}
          onClose={() => setPixJob(null)}
          onPaid={(updatedJob) => {
            if (updatedJob) setPixJob(updatedJob);
            refetch();
          }}
        />
      )}
      {(data?.jobs || []).length === 0 && <Alert type="info">Nenhuma vaga encontrada. Solicite um serviço!</Alert>}
      {(data?.jobs || []).map((j) => (
        <Card key={j.id} style={{ marginBottom: 14 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, color: C.text, fontSize: 15 }}>{j.category} / {j.subcategory}</div>
              <div style={{ fontSize: 12, color: C.sub, marginTop: 3 }}>
                {j.city}, {j.state} · {j.scheduledDate ? new Date(j.scheduledDate).toLocaleDateString('pt-BR') : 'Sem data'}
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                <Chip type={j.status} /><Chip type={j.paymentMethod} />
              </div>
              {j.isQuote && j.price > 0 && !['done', 'cancelled'].includes(j.status) && (
                <div style={{ marginTop: 10, background: C.accentDim, borderRadius: 8, padding: '10px 14px', border: `1px solid ${C.accent}40` }}>
                  <div style={{ fontSize: 12, color: C.sub, marginBottom: 2 }}>Orçamento proposto pelo colaborador:</div>
                  <div style={{ fontWeight: 800, color: C.accent, fontSize: 18 }}>{fmtMoney(j.price)}</div>
                  <div style={{ fontSize: 11, color: C.sub, marginTop: 4 }}>{j.paymentStatus === 'paid' ? 'Pagamento confirmado. Use o token somente ao concluir o servico.' : 'Efetue o pagamento para liberar o inicio do servico e o token de conclusao.'}</div>
                  {j.paymentStatus !== 'paid' && !j.pixCode && (
                    <div style={{ marginTop: 10 }}>
                      <Btn variant="primary" small onClick={() => setPixJob(j)}>Pagar orcamento</Btn>
                    </div>
                  )}
                </div>
              )}
              {j.isQuote && j.price <= 0 && !['done', 'cancelled'].includes(j.status) && (
                <div style={{ marginTop: 10, background: C.infoDim, borderRadius: 8, padding: '10px 14px', border: `1px solid ${C.info}40`, color: C.sub, fontSize: 12 }}>
                  Aguardando um colaborador aceitar a solicitacao e propor o valor do orcamento.
                </div>
              )}
              {j.pixCode && j.paymentMethod === 'pix' && j.paymentStatus !== 'paid' && (
                <div style={{ marginTop: 10 }}>
                  <Btn variant="primary" small onClick={() => setPixJob(j)}>⚡ Ver QR Code PIX</Btn>
                </div>
              )}
              {j.paymentStatus === 'paid' && j.token && !j.tokenUsed && !['cancelled', 'done'].includes(j.status) && (
                <div style={{ marginTop: 10 }}>
                  <p style={{ fontSize: 12, color: C.sub, marginBottom: 4 }}>Passe este token ao colaborador quando o serviço for concluído:</p>
                  <TokenDisplay token={j.token} used={false} />
                </div>
              )}
              {j.status === 'done' && !j.clientRatingScore && !reviewed[j.id] && (
                <div style={{ marginTop: 10 }}>
                  <Btn variant="gold" small onClick={() => setReviewJob(j)}>★ Avaliar serviço</Btn>
                </div>
              )}
              {j.status === 'done' && j.clientRatingScore && (
                <div style={{ marginTop: 8 }}>
                  <Stars rating={j.clientRatingScore} />
                  {j.clientRatingComment && <p style={{ fontSize: 13, color: C.sub, marginTop: 4 }}>"{j.clientRatingComment}"</p>}
                </div>
              )}
            </div>
            <div style={{ textAlign: 'right', marginLeft: 16 }}>
              <div style={{ fontWeight: 800, color: C.gold, fontSize: 18 }}>{j.isQuote && j.price <= 0 ? 'A definir' : fmtMoney(j.price)}</div>
            </div>
          </div>
        </Card>
      ))}
    </Section>
  );
};

const ClientProfile = () => {
  const { user, refreshUser } = useAuth();
  const action = useAction();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ name: user.name || '', phone: user.phone || '' });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    try {
      await action.run(() => api.auth.updateProfile(form));
      await refreshUser();
      setEditing(false);
    } catch (_) {}
  };

  return (
    <Section title="Meu Perfil" sub="Suas informações na plataforma">
      <Card style={{ maxWidth: 480 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
          <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
            <Avatar name={user.name} size={56} color={C.accent} src={user.profilePhoto} />
            <div>
              <div style={{ fontWeight: 800, fontSize: 18, color: C.text }}>{user.name}</div>
              <div style={{ color: C.sub, fontSize: 13 }}>{user.email}</div>
              <Chip type="active" />
            </div>
          </div>
          {!editing && <Btn variant="ghost" small onClick={() => setEditing(true)}>Editar</Btn>}
        </div>
        <Divider />
        {editing ? (
          <>
            {action.error && <Alert type="danger">{action.error}</Alert>}
            <FInput label="Nome" value={form.name} onChange={set('name')} />
            <FInput label="Telefone" placeholder="(11) 99999-0000" value={form.phone} onChange={set('phone')} />
            <div style={{ display: 'flex', gap: 8 }}>
              <Btn variant="primary" loading={action.loading} onClick={save}>Salvar</Btn>
              <Btn variant="ghost" onClick={() => { setEditing(false); setForm({ name: user.name || '', phone: user.phone || '' }); }}>Cancelar</Btn>
            </div>
          </>
        ) : (
          <>
            {[
              { label: 'Telefone', value: user.phone || 'Não informado' },
              { label: 'Vagas solicitadas', value: user.clientJobs || 0 },
              { label: 'Total gasto', value: `R$ ${Number(user.totalSpent || 0).toFixed(2)}` },
              { label: 'Avaliação média', value: user.clientRating ? `${user.clientRating} ★` : '—' },
            ].map((r) => (
              <div key={r.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: `1px solid ${C.border}` }}>
                <span style={{ color: C.sub, fontSize: 14 }}>{r.label}</span>
                <span style={{ fontWeight: 700, color: C.text }}>{r.value}</span>
              </div>
            ))}
          </>
        )}
      </Card>
    </Section>
  );
};

// ━━━ WORKER SCREENS ━━━

const WorkerAvailable = ({ setView }) => {
  const { user } = useAuth();
  const { data, loading, error: fetchError, refetch } = useJobs('open');
  const action = useAction();

  const accept = async (id) => {
    action.clear();
    try {
      await action.run(() => api.jobs.accept(id));
      refetch();
    } catch (_) {}
  };

  if (user.docStatus !== 'approved') {
    return (
      <Section title="Vagas Disponíveis" sub="Encontre serviços para realizar">
        <Alert type="warn">
          {user.docStatus === 'rejected'
            ? 'Seu cadastro foi reprovado. Entre em contato com o suporte para mais informações.'
            : 'Seu cadastro está em análise. Você poderá visualizar as vagas disponíveis após aprovação.'}
        </Alert>
        <Btn variant="ghost" onClick={() => setView('documents')}>Ver meus documentos</Btn>
      </Section>
    );
  }

  if (loading) return <Section title="Vagas Disponíveis"><Spinner /></Section>;

  const jobs = data?.jobs || [];

  return (
    <Section title="Vagas Disponíveis" sub="Encontre serviços para realizar">
      {fetchError && <Alert type="danger">Erro ao carregar vagas: {fetchError}</Alert>}
      {data?.geo?.status === 'worker_location_insufficient' &&
        <Alert type="warn">Informe sua cidade e UF em Meu Perfil para visualizar vagas presenciais. Vagas remotas continuam disponíveis.</Alert>}
      {action.error && <Alert type="danger">{action.error}</Alert>}
      {action.success && <Alert type="success">Vaga aceita com sucesso! Verifique em "Meu Trabalho".</Alert>}
      {!fetchError && jobs.length === 0 && <Alert type="info">Nenhuma vaga disponível no momento.</Alert>}
      {jobs.map((j) => (
        <Card key={j.id} style={{ marginBottom: 14 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, color: C.text, fontSize: 15 }}>{j.category}</div>
              {j.subcategory && <div style={{ color: C.sub, fontSize: 13 }}>{j.subcategory}</div>}
              <div style={{ fontSize: 12, color: C.sub, marginTop: 6 }}>
                {j.serviceMode === 'remote' ? '🌐 Remoto — todo o Brasil' : `📍 ${j.city}, ${j.state}`}
                {j.serviceMode !== 'remote' && j.distanceKm != null && ` · cerca de ${j.distanceKm} km`}
              </div>
              {j.locationStatus === 'municipality_fallback' && <div style={{ fontSize: 11, color: C.muted }}>Correspondência provisória por cidade/UF</div>}
              {j.scheduledDate && (
                <div style={{ fontSize: 12, color: C.sub }}>
                  📅 {new Date(j.scheduledDate).toLocaleDateString('pt-BR')}
                </div>
              )}
              <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                <Chip type={j.isQuote ? 'quote' : 'open'} label={j.isQuote ? 'Orçamento' : 'Preço fixo'} />
                {j.serviceMode === 'remote' && <Chip type="open" label="Remoto" />}
                {j.paymentMethod && <Chip type={j.paymentMethod} />}
              </div>
            </div>
            <div style={{ textAlign: 'right', flexShrink: 0 }}>
              <div style={{ fontSize: 11, color: C.sub }}>Valor do serviço</div>
              <div style={{ fontWeight: 900, color: C.accent, fontSize: 22, lineHeight: 1.1 }}>
                R$ {Number(j.workerPayout || 0).toFixed(2)}
              </div>
              {j.price > 0 && (
                <div style={{ fontSize: 11, color: C.sub }}>
                  Valor: R$ {Number(j.price).toFixed(2)} | Comissao ({formatOptionalRate(jobCommissionRate(j))}): -R$ {Number(j.commission || 0).toFixed(2)}
                </div>
              )}
              <div style={{ marginTop: 8 }}>
                <Btn variant="primary" small loading={action.loading} onClick={() => accept(j.id)}>
                  Aceitar vaga
                </Btn>
              </div>
            </div>
          </div>
        </Card>
      ))}
    </Section>
  );
};

const WorkerMyWork = () => {
  const { data, loading, refetch } = useJobs();
  const [tokenInputs, setTokenInputs] = useState({});
  const [quotePriceDigits, setQuotePriceDigits] = useState({});
  const [workTab, setWorkTab] = useState('active');
  const validateAction = useAction();
  const startAction = useAction();
  const quoteAction = useAction();

  const validate = async (jobId) => {
    const token = tokenInputs[jobId] || '';
    try {
      await validateAction.run(() => api.jobs.validateToken(jobId, token));
      refetch();
    } catch (_) {}
  };

  const startJob = async (jobId) => {
    try {
      await startAction.run(() => api.jobs.start(jobId));
      refetch();
    } catch (_) {}
  };

  const proposePrice = async (jobId) => {
    const price = parseMoney(quotePriceDigits[jobId] || '0');
    if (!price || price <= 0) return;
    try {
      await quoteAction.run(() => api.jobs.proposePrice(jobId, price));
      refetch();
    } catch (_) {}
  };

  if (loading) return <Section title="Meu Trabalho"><Spinner /></Section>;
  const allMyJobs = (data?.jobs || []).filter((j) => j.status !== 'open');
  const activeJobs = allMyJobs.filter((j) => ['accepted', 'in_progress'].includes(j.status));
  const completedJobs = allMyJobs.filter((j) => j.status === 'done');

  const renderJobCard = (j) => (
    <Card key={j.id} style={{ marginBottom: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, color: C.text, fontSize: 15 }}>{j.category}</div>
          {j.subcategory && <div style={{ color: C.sub, fontSize: 13 }}>{j.subcategory}</div>}
          {(j.street || j.city) && (
            <div style={{ fontSize: 12, color: C.sub, marginTop: 4 }}>
              📍 {[j.street, j.number, j.city, j.state].filter(Boolean).join(', ')}
            </div>
          )}
          {['accepted', 'in_progress'].includes(j.status) && (j.clientName || j.clientPhone) && (
            <div style={{ marginTop: 12, background: C.infoDim, border: `1px solid ${C.info}30`, borderRadius: 10, padding: '12px 14px' }}>
              {(j.street || j.city) && (
                <div style={{ marginBottom: 10 }}>
                  <div style={{ fontSize: 11, color: C.info, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 4 }}>📍 Endereço do serviço</div>
                  <div style={{ fontSize: 13, color: C.text, fontWeight: 600 }}>
                    {[j.street, j.number].filter(Boolean).join(', ')}{(j.city || j.state) ? ` — ${[j.city, j.state].filter(Boolean).join(', ')}` : ''}
                  </div>
                  {j.zip && <div style={{ fontSize: 12, color: C.sub, marginTop: 2 }}>CEP: {j.zip}</div>}
                </div>
              )}
              <div>
                <div style={{ fontSize: 11, color: C.info, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 4 }}>📞 Contato do cliente</div>
                {j.clientName && <div style={{ fontSize: 13, color: C.text, fontWeight: 600 }}>Nome: {j.clientName}</div>}
                {j.clientPhone && <div style={{ fontSize: 13, color: C.text, fontWeight: 600, marginTop: 2 }}>Telefone: {j.clientPhone}</div>}
              </div>
            </div>
          )}
          {j.scheduledDate && <div style={{ fontSize: 12, color: C.sub }}>📅 {new Date(j.scheduledDate).toLocaleDateString('pt-BR')}</div>}
          {j.notes && <div style={{ fontSize: 13, color: C.sub, marginTop: 6, fontStyle: 'italic' }}>"{j.notes}"</div>}
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}><Chip type={j.status} /></div>

          {j.status === 'accepted' && j.paymentStatus === 'paid' && (
            <div style={{ marginTop: 10 }}>
              <Btn variant="ghost" small loading={startAction.loading} onClick={() => startJob(j.id)}>
                Marcar como Em Andamento
              </Btn>
            </div>
          )}

          {j.isQuote && j.status === 'accepted' && j.paymentStatus !== 'paid' && !j.paymentChargeCreated && !j.tokenUsed && (
            <div style={{ marginTop: 12, background: C.surface, borderRadius: 8, padding: '12px 14px', border: `1px solid ${C.border}` }}>
              <div style={{ fontSize: 12, color: C.sub, fontWeight: 600, marginBottom: 8 }}>
                {j.price > 0 ? `Preço proposto: R$ ${Number(j.price).toFixed(2)}` : 'Defina o preço do orçamento:'}
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                <div style={{ flex: 1 }}>
                  <MoneyInput digits={quotePriceDigits[j.id] || '0'} onChange={(d) => setQuotePriceDigits((q) => ({ ...q, [j.id]: d }))} />
                </div>
                <div style={{ paddingBottom: 14 }}>
                  <Btn variant="primary" small loading={quoteAction.loading} disabled={parseMoney(quotePriceDigits[j.id] || '0') <= 0} onClick={() => proposePrice(j.id)}>
                    {j.price > 0 ? 'Atualizar' : 'Propor'}
                  </Btn>
                </div>
              </div>
            </div>
          )}

          {(j.status === 'accepted' || j.status === 'in_progress') && j.paymentStatus === 'paid' && !j.tokenUsed && (!j.isQuote || j.price > 0) && (
            <div style={{ marginTop: 12 }}>
              <p style={{ fontSize: 13, color: C.sub, marginBottom: 8, fontWeight: 600 }}>Insira o token do cliente para concluir:</p>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  className="qs-input"
                  placeholder="AB12CD34EF"
                  value={tokenInputs[j.id] || ''}
                  onChange={(e) => setTokenInputs((t) => ({ ...t, [j.id]: e.target.value }))}
                  style={{ flex: 1, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, padding: '8px 12px', color: C.text, fontFamily: C.mono, fontSize: 14 }}
                />
                <Btn variant="gold" small loading={validateAction.loading} onClick={() => validate(j.id)}>Validar</Btn>
              </div>
            </div>
          )}
          {j.isQuote && j.price <= 0 && ['accepted', 'in_progress'].includes(j.status) && (
            <div style={{ fontSize: 12, color: C.gold, marginTop: 8 }}>
              ⚠ Defina o preço acima para liberar a validação do token.
            </div>
          )}
          {j.isQuote && j.price > 0 && j.paymentStatus !== 'paid' && ['accepted', 'in_progress'].includes(j.status) && (
            <div style={{ fontSize: 12, color: C.gold, marginTop: 8 }}>
              Aguardando o cliente pagar o orcamento. O inicio e o token permanecem bloqueados.
            </div>
          )}

          {j.status === 'done' && j.clientRatingScore && (
            <div style={{ marginTop: 8 }}>
              <div style={{ fontSize: 12, color: C.sub, marginBottom: 2 }}>Avaliação do cliente:</div>
              <Stars rating={j.clientRatingScore} />
            </div>
          )}
        </div>
        <div style={{ textAlign: 'right', marginLeft: 16 }}>
          <div style={{ fontSize: 11, color: C.sub }}>Seu ganho</div>
          <div style={{ fontWeight: 900, color: j.status === 'done' ? C.gold : C.accent, fontSize: 20 }}>
            R$ {Number(j.workerPayout).toFixed(2)}
          </div>
          <div style={{ fontSize: 11, color: C.sub, lineHeight: 1.35, marginTop: 4 }}>
            Valor do servico: {fmtMoney(j.price || 0)}<br />
            Comissao ({formatOptionalRate(jobCommissionRate(j))}): -{fmtMoney(j.commission || 0)}<br />
            Voce recebe: {fmtMoney(j.workerPayout || 0)}
          </div>
          {j.status === 'done' && <div style={{ fontSize: 11, color: C.accent }}>✓ Pago</div>}
        </div>
      </div>
    </Card>
  );

  return (
    <Section title="Meu Trabalho" sub="Seus serviços">
      <Alert type="warn">
        Atenção: ao optar por receber antes do prazo de compensação do cartão, a operadora cobra taxa de deságio de aproximadamente 6% sobre o valor.
      </Alert>
      {validateAction.error && <Alert type="danger">{validateAction.error}</Alert>}
      {startAction.error && <Alert type="danger">{startAction.error}</Alert>}
      {quoteAction.error && <Alert type="danger">{quoteAction.error}</Alert>}
      {quoteAction.success && <Alert type="success">Preço proposto com sucesso!</Alert>}

      {/* Tab switcher */}
      <div style={{ display: 'flex', background: C.surface, borderRadius: 10, padding: 4, marginBottom: 20, border: `1px solid ${C.border}`, width: 'fit-content' }}>
        <button onClick={() => setWorkTab('active')} style={{ padding: '7px 18px', borderRadius: 7, border: 'none', background: workTab === 'active' ? C.card : 'transparent', color: workTab === 'active' ? C.text : C.sub, fontWeight: 700, fontSize: 13, cursor: 'pointer', fontFamily: C.font }}>
          ⚙️ Em andamento ({activeJobs.length})
        </button>
        <button onClick={() => setWorkTab('done')} style={{ padding: '7px 18px', borderRadius: 7, border: 'none', background: workTab === 'done' ? C.card : 'transparent', color: workTab === 'done' ? C.text : C.sub, fontWeight: 700, fontSize: 13, cursor: 'pointer', fontFamily: C.font }}>
          ✓ Concluídos ({completedJobs.length})
        </button>
      </div>

      {workTab === 'active' && (
        <>
          {activeJobs.length === 0 && <Alert type="info">Nenhuma vaga aceita ou em andamento.</Alert>}
          {activeJobs.map(renderJobCard)}
        </>
      )}
      {workTab === 'done' && (
        <>
          {completedJobs.length === 0 && <Alert type="info">Nenhuma vaga concluída ainda.</Alert>}
          {completedJobs.map(renderJobCard)}
        </>
      )}
    </Section>
  );
};

const PIX_TYPES = [
  { value: 'cpf',     label: 'CPF' },
  { value: 'cnpj',    label: 'CNPJ' },
  { value: 'email',   label: 'E-mail' },
  { value: 'phone',   label: 'Telefone' },
  { value: 'random',  label: 'Chave aleatória' },
];

const WorkerWallet = () => {
  const { user, refreshUser } = useAuth();
  const { data: wdData, loading: wdLoading, refetch: wdRefetch } = useMyWithdrawals();
  const action = useAction();
  const pixAction = useAction();

  // PIX key registration state
  const [pixType, setPixType] = useState('email');
  const [pixValue, setPixValue] = useState('');
  const [pixSaved, setPixSaved] = useState(false);

  // Withdrawal modal state
  const [showModal, setShowModal] = useState(false);
  const [amountDigits, setAmountDigits] = useState('0');
  const [formError, setFormError] = useState('');

  const hasPix = !!(user.pix && user.pix.trim());
  const policy = financialPolicyFromUser(user);
  const rateRows = [
    { label: 'Comissao aplicada', value: formatOptionalRate(policy.platformCommissionRate) },
    { label: 'Voce recebe', value: formatOptionalRate(policy.workerPayoutRate) },
  ];

  const savePix = async () => {
    if (!pixValue.trim()) return;
    setPixSaved(false);
    try {
      await pixAction.run(() => api.auth.updateProfile({ pix: pixValue.trim(), pixType }));
      await refreshUser();
      setPixSaved(true);
      setPixValue('');
    } catch (_) {}
  };

  const openModal = () => {
    setAmountDigits('0');
    setFormError('');
    action.clear();
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setFormError('');
    action.clear();
  };

  const submit = async () => {
    setFormError('');
    const amount = parseMoney(amountDigits);
    if (amount <= 0) return setFormError('Informe um valor válido.');
    if (amount > (user.balance || 0)) return setFormError('Saldo insuficiente.');
    try {
      await action.run(() => api.withdrawals.request({ amount, pixKey: user.pix, pixType: user.pixType || '' }));
      await refreshUser();
      closeModal();
      wdRefetch();
    } catch (_) {}
  };

  const statusColor = { pending: C.gold, paid: C.accent, rejected: C.danger };
  const statusLabel = { pending: 'Pendente', paid: 'Pago', rejected: 'Rejeitado' };

  return (
    <Section title="Carteira" sub="Seu saldo e histórico financeiro">
      {showModal && (
        <Modal title="Solicitar Saque via PIX" onClose={closeModal}>
          {formError && <Alert type="danger">{formError}</Alert>}
          {action.error && <Alert type="danger">{action.error}</Alert>}
          <div style={{ background: C.bg, borderRadius: 8, padding: '10px 14px', marginBottom: 16 }}>
            <div style={{ fontSize: 11, color: C.sub, marginBottom: 4 }}>Chave PIX de destino</div>
            {user.pixType && (
              <div style={{ fontSize: 11, color: C.sub, marginBottom: 2 }}>
                Tipo: <span style={{ color: C.text, fontWeight: 600 }}>{PIX_TYPES.find(t => t.value === user.pixType)?.label || user.pixType}</span>
              </div>
            )}
            <div style={{ fontWeight: 700, color: C.accent, fontFamily: C.mono, fontSize: 14 }}>{user.pix}</div>
          </div>
          <MoneyInput
            label={`Valor a sacar (disponível: R$ ${Number(user.balance || 0).toFixed(2)})`}
            digits={amountDigits}
            onChange={setAmountDigits}
          />
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <Btn variant="gold" loading={action.loading} onClick={submit}>Confirmar Saque</Btn>
            <Btn variant="ghost" onClick={closeModal}>Cancelar</Btn>
          </div>
        </Modal>
      )}

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 24 }}>
        <Metric label="Saldo Disponível" value={`R$ ${Number(user.balance || 0).toFixed(2)}`} color={C.gold} icon="◆" />
        <Metric label="Total Ganho" value={`R$ ${Number(user.totalEarned || 0).toFixed(2)}`} color={C.accent} icon="✓" />
        <Metric label="Serviços Feitos" value={user.totalJobs || 0} color={C.info} icon="◈" />
      </div>

      {/* PIX key management */}
      <Card style={{ maxWidth: 460, marginBottom: 20 }}>
        <div style={{ fontWeight: 700, color: C.text, marginBottom: 14 }}>Chave PIX para recebimento</div>
        {hasPix ? (
          <>
            <div style={{ padding: '10px 0', borderBottom: `1px solid ${C.border}`, marginBottom: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ color: C.sub, fontSize: 13 }}>
                  {user.pixType ? PIX_TYPES.find(t => t.value === user.pixType)?.label || user.pixType : 'Chave'} cadastrada
                </span>
                <span style={{ fontWeight: 700, color: C.accent, fontFamily: C.mono, fontSize: 13 }}>{user.pix}</span>
              </div>
            </div>
            <div style={{ fontSize: 12, color: C.sub, marginBottom: 10 }}>Para alterar, cadastre uma nova chave abaixo:</div>
          </>
        ) : (
          <Alert type="warn">Cadastre sua chave PIX para poder solicitar saques.</Alert>
        )}
        {pixSaved && <Alert type="success">Chave PIX salva com sucesso!</Alert>}
        {pixAction.error && <Alert type="danger">{pixAction.error}</Alert>}
        <div style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
          {PIX_TYPES.map((t) => (
            <button
              key={t.value}
              onClick={() => setPixType(t.value)}
              style={{
                padding: '5px 12px', borderRadius: 20, fontSize: 12, cursor: 'pointer', border: 'none',
                background: pixType === t.value ? C.accent : C.border,
                color: pixType === t.value ? C.bg : C.sub,
                fontWeight: pixType === t.value ? 700 : 400,
              }}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
          <div style={{ flex: 1 }}>
            <FInput
              label={`Chave PIX (${PIX_TYPES.find(t => t.value === pixType)?.label})`}
              placeholder={
                pixType === 'cpf' ? '000.000.000-00' :
                pixType === 'cnpj' ? '00.000.000/0000-00' :
                pixType === 'email' ? 'seu@email.com' :
                pixType === 'phone' ? '+55 (11) 99999-9999' :
                'Cole a chave aleatória aqui'
              }
              value={pixValue}
              onChange={(e) => { setPixValue(e.target.value); setPixSaved(false); }}
            />
          </div>
          <div style={{ paddingBottom: 16 }}>
            <Btn variant="primary" loading={pixAction.loading} onClick={savePix} disabled={!pixValue.trim()}>
              Salvar
            </Btn>
          </div>
        </div>
      </Card>

      {/* Withdrawal trigger */}
      <Card style={{ maxWidth: 460, marginBottom: 24 }}>
        <div style={{ fontWeight: 700, color: C.text, marginBottom: 12 }}>Solicitar saque</div>
        {rateRows.map((r) => (
          <div key={r.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: `1px solid ${C.border}` }}>
            <span style={{ color: C.sub, fontSize: 14 }}>{r.label}</span>
            <span style={{ fontWeight: 700, color: C.text }}>{r.value}</span>
          </div>
        ))}
        <div style={{ marginTop: 14 }}>
          {!hasPix && (
            <div style={{ fontSize: 13, color: C.gold, marginBottom: 10 }}>
              ⚠ Cadastre sua chave PIX acima para liberar saques.
            </div>
          )}
          <Btn
            variant="gold"
            onClick={openModal}
            disabled={!hasPix || (user.balance || 0) <= 0}
          >
            Solicitar Saque
          </Btn>
          {hasPix && (user.balance || 0) <= 0 && (
            <div style={{ fontSize: 12, color: C.sub, marginTop: 6 }}>Saldo insuficiente para saque.</div>
          )}
        </div>
      </Card>

      <h3 style={{ color: C.text, fontWeight: 700, marginBottom: 12 }}>Histórico de Saques</h3>
      {wdLoading ? <Spinner /> : (wdData?.withdrawals || []).length === 0 ? (
        <Alert type="info">Nenhuma solicitação de saque ainda.</Alert>
      ) : (wdData?.withdrawals || []).map((w) => (
        <Card key={w.id} style={{ marginBottom: 8 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: 12, color: C.sub }}>
                PIX {w.pixType ? `(${PIX_TYPES.find(t => t.value === w.pixType)?.label || w.pixType})` : ''}: <span style={{ fontFamily: C.mono, color: C.text }}>{w.pixKey}</span>
              </div>
              <div style={{ fontSize: 12, color: C.sub }}>{new Date(w.requestedAt).toLocaleDateString('pt-BR')}</div>
              {w.rejectionReason && (
                <div style={{ fontSize: 12, color: C.danger, marginTop: 2 }}>Motivo: {w.rejectionReason}</div>
              )}
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontWeight: 800, color: C.gold }}>R$ {Number(w.amount).toFixed(2)}</div>
              <div style={{ fontSize: 12, fontWeight: 700, color: statusColor[w.status] || C.sub }}>
                {statusLabel[w.status] || w.status}
              </div>
            </div>
          </div>
        </Card>
      ))}
    </Section>
  );
};

const WorkerDocuments = () => {
  const { data, loading, refetch } = useMyDocuments();
  const { data: livenessData, loading: livenessLoading, refetch: refetchLiveness } = useFetch(() => api.liveness.status(), []);
  const [uploading, setUploading] = useState('');
  const [uploadError, setUploadError] = useState('');

  const handleUpload = async (docType, file) => {
    if (!file) return;
    if (docType === 'selfie' && !isMobileRegistrationDevice()) {
      setUploadError(MOBILE_WORKER_REGISTRATION_MESSAGE);
      return;
    }
    setUploading(docType);
    setUploadError('');
    try {
      const formData = new FormData();
      formData.append('documentType', docType);
      formData.append('file', file);
      await api.documents.upload(formData);
      refetch();
    } catch (err) {
      setUploadError(err.message);
    } finally {
      setUploading('');
    }
  };

  const getDoc = (type) => (data?.documents || []).find((d) => d.documentType === type);
  const statusColor = { pending: C.gold, approved: C.accent, rejected: C.danger };
  const latestLiveness = livenessData?.latest;
  const livenessApproved = Boolean(livenessData?.approved);
  const livenessCard = (
    <Card style={{ marginBottom: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start', marginBottom: livenessApproved ? 0 : 12 }}>
        <div>
          <div style={{ fontWeight: 800, color: C.text }}>Prova de vida ativa</div>
          <div style={{ fontSize: 12, color: C.sub, marginTop: 3 }}>
            {livenessApproved
              ? `Aprovada em ${new Date((livenessData?.approvedCheck || latestLiveness)?.completedAt || Date.now()).toLocaleDateString('pt-BR')}.`
              : latestLiveness
                ? `Ultima tentativa: ${LIVENESS_STATUS_LABEL[latestLiveness.status] || latestLiveness.status}.`
                : 'Pendente. Use a camera frontal do celular para concluir.'}
          </div>
        </div>
        {livenessLoading ? <Spinner size={18} /> : <Chip type={livenessApproved ? 'approved' : 'pending'} label={livenessApproved ? 'Aprovada' : 'Pendente'} />}
      </div>
      {!livenessApproved && !livenessLoading && (
        <LivenessCapture compact onComplete={() => refetchLiveness()} />
      )}
    </Card>
  );
  const statusLabel = { pending: '⏳ Em análise', approved: '✓ Aprovado', rejected: '✗ Rejeitado' };

  return (
    <Section title="Meus Documentos" sub="Envie os documentos necessários para aprovação">
      {uploadError && <Alert type="danger">{uploadError}</Alert>}
      {livenessCard}
      <Alert type="info">Envie todos os documentos obrigatórios. Após a análise, você poderá visualizar e aceitar vagas.</Alert>
      {loading ? <Spinner /> : WORKER_REGISTRATION_DOCUMENT_TYPES.map((dt) => {
        const doc = getDoc(dt.key);
        return (
          <Card key={dt.key} style={{ marginBottom: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontWeight: 700, color: C.text }}>{dt.label}</div>
                {doc ? (
                  <div style={{ marginTop: 4, fontSize: 13, fontWeight: 700, color: statusColor[doc.status] }}>
                    {statusLabel[doc.status]}
                    <span style={{ fontSize: 11, color: C.sub, fontWeight: 400, marginLeft: 8 }}>
                      Enviado em {new Date(doc.uploadedAt).toLocaleDateString('pt-BR')}
                    </span>
                  </div>
                ) : (
                  <div style={{ fontSize: 13, color: C.muted, marginTop: 2 }}>Não enviado</div>
                )}
              </div>
              <label style={{
                background: C.infoDim, color: uploading === dt.key ? C.muted : C.info,
                border: `1px solid ${C.info}40`, borderRadius: 8, padding: '8px 16px',
                fontSize: 12, fontWeight: 700, cursor: uploading === dt.key ? 'not-allowed' : 'pointer', fontFamily: C.font,
              }}>
                {uploading === dt.key ? 'Enviando...' : doc ? '↑ Reenviar' : '↑ Enviar'}
                <input
                  type="file"
                  accept="image/*,.pdf"
                  style={{ display: 'none' }}
                  onChange={(e) => handleUpload(dt.key, e.target.files[0])}
                  disabled={uploading === dt.key}
                />
              </label>
            </div>
          </Card>
        );
      })}
    </Section>
  );
};

const WorkerProfile = () => {
  const { user, refreshUser } = useAuth();
  const [baseForm, setBaseForm] = useState({ street: user.street || '', number: user.number || '',
    zip: user.zip || '', city: user.city || '', state: user.state || '', serviceRadius: String(user.serviceRadius || ''),
    areas: user.areas?.length ? user.areas : user.skills || [] });
  const [baseSaving, setBaseSaving] = useState(false);
  const [baseMessage, setBaseMessage] = useState('');
  const saveBase = async () => {
    setBaseMessage('');
    if (!baseForm.city.trim() || !/^[A-Za-z]{2}$/.test(baseForm.state.trim()) ||
        !Number(baseForm.serviceRadius) || Number(baseForm.serviceRadius) <= 0) {
      setBaseMessage('Informe cidade, UF e raio de atendimento válidos.');
      return;
    }
    setBaseSaving(true);
    try {
      await api.auth.updateProfile({ ...baseForm, skills: baseForm.areas, state: baseForm.state.trim().toUpperCase(),
        serviceRadius: Number(baseForm.serviceRadius) });
      await refreshUser();
      setBaseMessage('Base de atendimento atualizada.');
    } catch (error) { setBaseMessage(error.message); }
    finally { setBaseSaving(false); }
  };
  const { data: reviewData, loading: revLoading } = useFetch(() => api.reviews.byWorker(user.id), [user.id]);
  const { data: docData } = useMyDocuments();
  const selfieDoc = (docData?.documents || []).find((d) => d.documentType === 'selfie');
  const selfieSource = resolveAssetUrl(user.profilePhoto) || resolveDocumentUrl(selfieDoc);
  const selfieUrl = useProtectedAssetUrl(selfieSource);
  const [selfieErr, setSelfieErr] = useState(false);
  const [referralMessage, setReferralMessage] = useState('');
  const reviews = reviewData?.reviews || [];
  const workerReferralLink = `${window.location.origin}/indicado/colaborador/${encodeURIComponent(user.id)}`;
  const copyWorkerReferralLink = async () => {
    setReferralMessage('');
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(workerReferralLink);
        setReferralMessage('Link de indicacao de cliente copiado.');
      } else {
        window.prompt('Copie seu link de indicacao:', workerReferralLink);
        setReferralMessage('Link de indicacao exibido para copia.');
      }
    } catch (_) {
      window.prompt('Copie seu link de indicacao:', workerReferralLink);
      setReferralMessage('Link de indicacao exibido para copia.');
    }
  };

  return (
    <Section title="Meu Perfil" sub="Suas informações e especialidades">
      <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
        <Card style={{ flex: 1, minWidth: 260 }}>
          <div style={{ fontWeight: 700, color: C.text, marginBottom: 12 }}>Base de atendimento</div>
          <p style={{ color: C.sub, fontSize: 12, marginBottom: 12 }}>Vagas presenciais dependem da sua cidade e UF. O raio em km é aplicado quando há coordenadas válidas.</p>
          <FInput label="Rua" value={baseForm.street} onChange={(e) => setBaseForm((f) => ({ ...f, street: e.target.value }))} />
          <div className="qs-compact-row" style={{ display: 'flex', gap: 10 }}>
            <div style={{ flex: 1 }}><FInput label="Número" value={baseForm.number} onChange={(e) => setBaseForm((f) => ({ ...f, number: e.target.value }))} /></div>
            <div style={{ flex: 1 }}><FInput label="CEP" value={baseForm.zip} onChange={(e) => setBaseForm((f) => ({ ...f, zip: e.target.value }))} /></div>
          </div>
          <div className="qs-compact-row" style={{ display: 'flex', gap: 10 }}>
            <div style={{ flex: 2 }}><FInput label="Cidade" value={baseForm.city} onChange={(e) => setBaseForm((f) => ({ ...f, city: e.target.value }))} /></div>
            <div style={{ flex: 1 }}><FInput label="UF" value={baseForm.state} onChange={(e) => setBaseForm((f) => ({ ...f, state: e.target.value.toUpperCase() }))} /></div>
          </div>
          <FInput label="Raio de atendimento (km)" type="number" value={baseForm.serviceRadius} onChange={(e) => setBaseForm((f) => ({ ...f, serviceRadius: e.target.value }))} />
          <div style={{ fontSize: 12, color: C.sub, marginBottom: 8 }}>Áreas e especialidades atendidas</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 14 }}>
            {AREAS.map((area) => <button key={area} type="button" onClick={() => setBaseForm((f) => ({ ...f,
              areas: f.areas.includes(area) ? f.areas.filter((item) => item !== area) : [...f.areas, area] }))}
              style={{ padding: '5px 10px', borderRadius: 6, fontSize: 12, cursor: 'pointer',
                background: baseForm.areas.includes(area) ? C.infoDim : 'transparent',
                color: baseForm.areas.includes(area) ? C.info : C.sub,
                border: `1px solid ${baseForm.areas.includes(area) ? C.info : C.border}` }}>{area}</button>)}
          </div>
          {baseMessage && <Alert type={baseMessage.startsWith('Base de') ? 'success' : 'warn'}>{baseMessage}</Alert>}
          <Btn variant="primary" loading={baseSaving} onClick={saveBase}>Salvar base</Btn>
        </Card>
        <Card style={{ flex: 1, minWidth: 260 }}>
          <div style={{ display: 'flex', gap: 14, alignItems: 'center', marginBottom: 20 }}>
            {selfieUrl && !selfieErr ? (
              <img src={selfieUrl} alt={user.name} onError={() => setSelfieErr(true)}
                style={{ width: 80, height: 80, borderRadius: '50%', objectFit: 'cover',
                  border: `2px solid ${C.info}60`, flexShrink: 0 }} />
            ) : (
              <Avatar name={user.name} size={80} color={C.info} src={user.profilePhoto} />
            )}
            <div>
              <div style={{ fontWeight: 800, fontSize: 18, color: C.text }}>{user.name}</div>
              <div style={{ color: C.sub, fontSize: 13 }}>{user.email}</div>
              <Stars rating={user.workerRating} />
            </div>
          </div>
          <Divider />
          {[
            { label: 'Telefone', value: user.phone || 'Não informado' },
            { label: 'Status', value: user.verified ? 'Verificado ✓' : 'Pendente' },
            { label: 'Documentação', value: user.docStatus || 'pending' },
            { label: 'Serviços realizados', value: user.totalJobs || 0 },
            { label: 'Anos de experiência', value: user.yearsExperience ? `${user.yearsExperience} anos` : '—' },
            { label: 'Raio de atendimento', value: user.serviceRadius ? `${user.serviceRadius} km` : '—' },
          ].map((r) => (
            <div key={r.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: `1px solid ${C.border}` }}>
              <span style={{ color: C.sub, fontSize: 14 }}>{r.label}</span>
              <span style={{ fontWeight: 700, color: C.text }}>{r.value}</span>
            </div>
          ))}
          {user.description && (
            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: 12, color: C.sub, marginBottom: 4 }}>Descrição profissional</div>
              <div style={{ fontSize: 14, color: C.text, lineHeight: 1.5 }}>{user.description}</div>
            </div>
          )}
        </Card>

        <Card style={{ flex: 1, minWidth: 260 }}>
          <div style={{ fontWeight: 700, color: C.text, marginBottom: 14 }}>Especialidades e Áreas</div>
          {(user.areas || []).length > 0 && (
            <>
              <div style={{ fontSize: 12, color: C.sub, marginBottom: 8 }}>Áreas de atuação</div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
                {user.areas.map((a) => <span key={a} style={{ background: C.infoDim, color: C.info, border: `1px solid ${C.info}30`, borderRadius: 6, padding: '4px 10px', fontSize: 12, fontWeight: 600 }}>{a}</span>)}
              </div>
            </>
          )}
          {(user.skills || []).length > 0 && (
            <>
              <div style={{ fontSize: 12, color: C.sub, marginBottom: 8 }}>Habilidades</div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
                {user.skills.map((s) => <span key={s} style={{ background: C.infoDim, color: C.info, border: `1px solid ${C.info}40`, borderRadius: 8, padding: '6px 14px', fontWeight: 700, fontSize: 14 }}>{s}</span>)}
              </div>
            </>
          )}
          {user.certifications && (
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 12, color: C.sub, marginBottom: 4 }}>Certificações</div>
              <div style={{ fontSize: 14, color: C.text }}>{user.certifications}</div>
            </div>
          )}
          <Divider />
          <div style={{ fontWeight: 700, color: C.text, marginBottom: 10 }}>Status de Documentação</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Chip type={user.docStatus || 'pending'} />
            {user.verified && <span style={{ color: C.accent, fontSize: 13, fontWeight: 600 }}>✓ Perfil verificado</span>}
          </div>
        </Card>

        <Card style={{ flex: 1, minWidth: 260 }}>
          <div style={{ fontWeight: 700, color: C.text, marginBottom: 10 }}>Indicar cliente</div>
          <p style={{ color: C.sub, fontSize: 13, lineHeight: 1.5, marginBottom: 12 }}>
            Compartilhe este link com clientes. A origem fica registrada para rastreabilidade, sem expor dados do cliente fora da plataforma.
          </p>
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, padding: '10px 12px', color: C.info, fontSize: 12, fontFamily: C.mono, wordBreak: 'break-all', marginBottom: 12 }}>
            {workerReferralLink}
          </div>
          {referralMessage && <Alert type="success">{referralMessage}</Alert>}
          <Btn variant="primary" small onClick={copyWorkerReferralLink}>Copiar link</Btn>
        </Card>
      </div>

      {/* Avaliações recebidas */}
      <div style={{ marginTop: 24 }}>
        <div style={{ fontWeight: 700, fontSize: 16, color: C.text, marginBottom: 12 }}>
          Avaliações recebidas ({reviews.length})
        </div>
        {revLoading ? <Spinner /> : reviews.length === 0 ? (
          <Alert type="info">Você ainda não recebeu avaliações.</Alert>
        ) : reviews.map((r) => (
          <Card key={r.id} style={{ marginBottom: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <Stars rating={r.rating} />
                  <span style={{ fontSize: 12, color: C.sub }}>{r.client?.name}</span>
                </div>
                {r.tags?.length > 0 && (
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 6 }}>
                    {r.tags.map((t) => <span key={t} style={{ background: C.accentDim, color: C.accent, border: `1px solid ${C.accent}30`, borderRadius: 20, padding: '2px 10px', fontSize: 11, fontWeight: 600 }}>{t}</span>)}
                  </div>
                )}
                {r.comment && <p style={{ fontSize: 13, color: C.sub, fontStyle: 'italic' }}>"{r.comment}"</p>}
              </div>
              <span style={{ fontSize: 11, color: C.muted }}>{new Date(r.createdAt).toLocaleDateString('pt-BR')}</span>
            </div>
          </Card>
        ))}
      </div>
    </Section>
  );
};

// ━━━ ADMIN REVIEWS ━━━
const AdminReviews = () => {
  const { data, loading, refetch } = useFetch(() => api.admin.reviews(), []);
  const action = useAction();

  const remove = async (id) => {
    if (!window.confirm('Excluir esta avaliação? A nota do colaborador será recalculada.')) return;
    await action.run(() => api.admin.deleteReview(id));
    refetch();
  };

  return (
    <Section title="Avaliações" sub="Todas as avaliações de colaboradores">
      {action.error && <Alert type="danger">{action.error}</Alert>}
      {loading ? <Spinner /> : (data?.reviews || []).length === 0 ? (
        <Alert type="info">Nenhuma avaliação registrada ainda.</Alert>
      ) : (data?.reviews || []).map((r) => (
        <Card key={r.id} style={{ marginBottom: 10 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                <Stars rating={r.rating} />
                <span style={{ fontWeight: 700, color: C.text }}>{r.rating}/5</span>
              </div>
              <div style={{ fontSize: 12, color: C.sub }}>
                Cliente: <strong style={{ color: C.text }}>{r.client?.name || '—'}</strong>
                {' → '}Worker: <strong style={{ color: C.info }}>{r.worker?.name || '—'}</strong>
              </div>
              {r.tags?.length > 0 && (
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
                  {r.tags.map((t) => <Chip key={t} type="active" label={t} />)}
                </div>
              )}
              {r.comment && <p style={{ fontSize: 13, color: C.sub, marginTop: 6, fontStyle: 'italic' }}>"{r.comment}"</p>}
              <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>
                {new Date(r.createdAt).toLocaleString('pt-BR')}
              </div>
            </div>
            <Btn variant="danger" small loading={action.loading} onClick={() => remove(r.id)}>Excluir</Btn>
          </div>
        </Card>
      ))}
    </Section>
  );
};

// ━━━ ADMIN PROMOTIONS ━━━
const AdminPromotions = () => {
  const [promos, setPromos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null); // null = new, object = edit
  const [form, setForm] = useState({ title: '', description: '', badge: '', imageUrl: '', link: '', active: true, displayOrder: 0, startDate: '', endDate: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const d = await api.promotions.adminList();
      setPromos(d.promotions || []);
    } catch (e) { /* ignore */ }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditing(null);
    setForm({ title: '', description: '', badge: '', imageUrl: '', link: '', active: true, displayOrder: promos.length, startDate: '', endDate: '' });
    setError('');
    setModal(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      title: p.title || '',
      description: p.description || '',
      badge: p.badge || '',
      imageUrl: p.imageUrl || '',
      link: p.link || '',
      active: p.active !== false,
      displayOrder: p.displayOrder || 0,
      startDate: p.startDate ? p.startDate.slice(0, 10) : '',
      endDate: p.endDate ? p.endDate.slice(0, 10) : '',
    });
    setError('');
    setModal(true);
  };

  const save = async () => {
    if (!form.title.trim()) { setError('Título é obrigatório.'); return; }
    setSaving(true); setError('');
    try {
      const payload = {
        ...form,
        displayOrder: parseInt(form.displayOrder) || 0,
        startDate: form.startDate || null,
        endDate: form.endDate || null,
      };
      if (editing) await api.promotions.update(editing.id, payload);
      else await api.promotions.create(payload);
      setModal(false);
      load();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Remover esta promoção?')) return;
    try { await api.promotions.remove(id); load(); } catch { /* ignore */ }
  };

  const toggleActive = async (p) => {
    try { await api.promotions.update(p.id, { active: !p.active }); load(); } catch { /* ignore */ }
  };

  const fmt = (d) => d ? new Date(d).toLocaleDateString('pt-BR') : '—';

  return (
    <Section title="Promoções" subtitle="Gerencie as promoções exibidas na landing page">
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 20 }}>
        <button onClick={openNew} style={{ background: C.accent, color: '#000', border: 'none', borderRadius: 8, padding: '9px 20px', fontWeight: 700, fontSize: 14, cursor: 'pointer', fontFamily: C.font }}>
          + Nova Promoção
        </button>
      </div>

      {loading ? <div style={{ textAlign: 'center', color: C.sub, padding: 40 }}><Spinner /></div> : promos.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, color: C.sub }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>🎯</div>
          <div style={{ fontWeight: 700, fontSize: 16 }}>Nenhuma promoção cadastrada</div>
          <div style={{ fontSize: 13, marginTop: 6 }}>Clique em "Nova Promoção" para começar</div>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 12 }}>
          {promos.map((p) => (
            <Card key={p.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
              {p.imageUrl && (
                <img src={p.imageUrl} alt={p.title} style={{ width: 80, height: 60, objectFit: 'cover', borderRadius: 8, flexShrink: 0 }}
                  onError={(e) => { e.target.style.display = 'none'; }} />
              )}
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                  <span style={{ fontWeight: 800, color: C.text, fontSize: 15 }}>{p.title}</span>
                  {p.badge && <Chip type="quote" label={p.badge} />}
                  <Chip type={p.active ? 'active' : 'cancelled'} label={p.active ? 'Ativo' : 'Inativo'} />
                </div>
                {p.description && <div style={{ color: C.sub, fontSize: 13, marginBottom: 6 }}>{p.description.slice(0, 120)}{p.description.length > 120 ? '…' : ''}</div>}
                <div style={{ display: 'flex', gap: 20, fontSize: 12, color: C.muted }}>
                  <span>Ordem: {p.displayOrder}</span>
                  {p.startDate && <span>Início: {fmt(p.startDate)}</span>}
                  {p.endDate && <span>Fim: {fmt(p.endDate)}</span>}
                  {p.link && <span>🔗 Com link</span>}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                <button onClick={() => toggleActive(p)} style={{ background: p.active ? C.dangerDim : C.accentDim, color: p.active ? C.danger : C.accent, border: `1px solid ${p.active ? C.danger : C.accent}30`, borderRadius: 6, padding: '5px 12px', fontSize: 12, cursor: 'pointer', fontFamily: C.font, fontWeight: 600 }}>
                  {p.active ? 'Desativar' : 'Ativar'}
                </button>
                <button onClick={() => openEdit(p)} style={{ background: C.infoDim, color: C.info, border: `1px solid ${C.info}30`, borderRadius: 6, padding: '5px 12px', fontSize: 12, cursor: 'pointer', fontFamily: C.font, fontWeight: 600 }}>
                  Editar
                </button>
                <button onClick={() => remove(p.id)} style={{ background: C.dangerDim, color: C.danger, border: `1px solid ${C.danger}30`, borderRadius: 6, padding: '5px 12px', fontSize: 12, cursor: 'pointer', fontFamily: C.font, fontWeight: 600 }}>
                  Excluir
                </button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Modal */}
      {modal && (
        <div style={{ position: 'fixed', inset: 0, background: '#00000080', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 20 }}>
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 16, padding: 28, width: '100%', maxWidth: 520, maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <div style={{ fontWeight: 800, fontSize: 18, color: C.text }}>{editing ? 'Editar Promoção' : 'Nova Promoção'}</div>
              <button onClick={() => setModal(false)} style={{ background: 'none', border: 'none', color: C.sub, fontSize: 22, cursor: 'pointer' }}>✕</button>
            </div>

            {error && <div style={{ background: C.dangerDim, border: `1px solid ${C.danger}30`, color: C.danger, borderRadius: 8, padding: '10px 14px', fontSize: 13, marginBottom: 16 }}>{error}</div>}

            {[
              { key: 'title', label: 'Título *', placeholder: 'Ex: 20% de desconto em limpeza' },
              { key: 'badge', label: 'Badge (opcional)', placeholder: 'Ex: 20% OFF, NOVO, LIMITADO' },
              { key: 'description', label: 'Descrição', placeholder: 'Descreva a promoção…', multi: true },
              { key: 'imageUrl', label: 'URL da imagem (opcional)', placeholder: 'https://…' },
              { key: 'link', label: 'Link ao clicar (opcional)', placeholder: 'https://…' },
            ].map(({ key, label, placeholder, multi }) => (
              <div key={key} style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 12, color: C.sub, fontWeight: 600, display: 'block', marginBottom: 6 }}>{label}</label>
                {multi ? (
                  <textarea
                    style={{ width: '100%', background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, padding: '10px 12px', color: C.text, fontSize: 14, fontFamily: C.font, minHeight: 72, resize: 'vertical' }}
                    placeholder={placeholder} value={form[key]}
                    onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
                  />
                ) : (
                  <input
                    style={{ width: '100%', background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, padding: '10px 12px', color: C.text, fontSize: 14, fontFamily: C.font }}
                    placeholder={placeholder} value={form[key]}
                    onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
                  />
                )}
              </div>
            ))}

            <div style={{ display: 'flex', gap: 14, marginBottom: 14 }}>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: 12, color: C.sub, fontWeight: 600, display: 'block', marginBottom: 6 }}>Ordem de exibição</label>
                <input type="number" style={{ width: '100%', background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, padding: '10px 12px', color: C.text, fontSize: 14, fontFamily: C.font }}
                  value={form.displayOrder} onChange={(e) => setForm((f) => ({ ...f, displayOrder: e.target.value }))} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: 12, color: C.sub, fontWeight: 600, display: 'block', marginBottom: 6 }}>Data início</label>
                <input type="date" style={{ width: '100%', background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, padding: '10px 12px', color: C.text, fontSize: 14, fontFamily: C.font }}
                  value={form.startDate} onChange={(e) => setForm((f) => ({ ...f, startDate: e.target.value }))} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: 12, color: C.sub, fontWeight: 600, display: 'block', marginBottom: 6 }}>Data fim</label>
                <input type="date" style={{ width: '100%', background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, padding: '10px 12px', color: C.text, fontSize: 14, fontFamily: C.font }}
                  value={form.endDate} onChange={(e) => setForm((f) => ({ ...f, endDate: e.target.value }))} />
              </div>
            </div>

            <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', marginBottom: 20 }}>
              <input type="checkbox" checked={form.active} onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))} style={{ width: 16, height: 16 }} />
              <span style={{ color: C.text, fontSize: 14, fontWeight: 600 }}>Promoção ativa (visível na landing page)</span>
            </label>

            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setModal(false)} style={{ flex: 1, background: 'none', border: `1px solid ${C.border}`, color: C.sub, borderRadius: 8, padding: '10px 0', cursor: 'pointer', fontFamily: C.font, fontSize: 14 }}>
                Cancelar
              </button>
              <button onClick={save} disabled={saving} style={{ flex: 2, background: C.accent, border: 'none', color: '#000', borderRadius: 8, padding: '10px 0', cursor: 'pointer', fontFamily: C.font, fontSize: 14, fontWeight: 800, opacity: saving ? 0.7 : 1 }}>
                {saving ? 'Salvando…' : editing ? 'Salvar alterações' : 'Criar promoção'}
              </button>
            </div>
          </div>
        </div>
      )}
    </Section>
  );
};

// ━━━ MAIN APP ━━━
const ALLOWED = {
  admin: ['dash', 'jobs', 'clients', 'workers', 'partners', 'partnerperformance', 'pricetable', 'finance', 'withdrawals', 'promotions', 'accounting', 'reviews', 'settings'],
  client: ['request', 'myjobs', 'profile'],
  worker: ['available', 'mywork', 'wallet', 'documents', 'wprofile'],
  partner: ['partnerdash', 'partnerjobs', 'partnerclients', 'partnerworkers'],
};

const getRequestedView = () => {
  const params = new URLSearchParams(window.location.search);
  const queryView = params.get('view');
  const hashView = window.location.hash.replace(/^#\/?/, '');
  return queryView || hashView || null;
};

const getPartnerReferralFromUrl = () => {
  const match = window.location.pathname.match(/^\/parceiro\/([^/]+)\/(colaborador|cliente)\/?$/i);
  if (match) {
    return {
      slug: decodeURIComponent(match[1]),
      audience: match[2].toLowerCase() === 'cliente' ? 'client' : 'worker',
      workerReferralId: '',
    };
  }
  const workerMatch = window.location.pathname.match(/^\/indicado\/colaborador\/([^/]+)\/?$/i);
  if (workerMatch) {
    return {
      slug: '',
      audience: 'client',
      workerReferralId: decodeURIComponent(workerMatch[1]),
    };
  }
  const params = new URLSearchParams(window.location.search);
  const slug = params.get('parceiro') || params.get('partner') || '';
  const workerReferralId = params.get('colaborador') || params.get('worker') || params.get('workerReferral') || '';
  return { slug, audience: 'worker', workerReferralId };
};

const isResetPasswordRoute = () => window.location.pathname === '/reset-password';
const isPartnerAccessRoute = () => /^\/parceiro\/[^/]+\/acesso\/?$/i.test(window.location.pathname);
const getLegalRoute = () => {
  const pathname = window.location.pathname.replace(/\/$/, '') || '/';
  if (pathname === '/privacidade' || pathname === '/politica-de-privacidade') return 'privacy';
  if (pathname === '/termos' || pathname === '/termos-de-uso') return 'terms';
  return null;
};

export default function App() {
  const { user, loading, logout } = useAuth();
  const partnerReferral = getPartnerReferralFromUrl();
  const referralSlug = partnerReferral.slug;
  const referralAudience = partnerReferral.audience;
  const workerReferralId = partnerReferral.workerReferralId;
  const resetPasswordRoute = isResetPasswordRoute();
  const legalRoute = getLegalRoute();
  const [view, setView] = useState(null);

  const getInitialScreen = () => {
    if (resetPasswordRoute) return 'reset';
    if (isPartnerAccessRoute()) return 'login';
    if (referralSlug || workerReferralId) return 'login';
    const p = window.location.pathname;
    const q = window.location.search;
    if (p === '/login' || p === '/cadastrar' || q.includes('login') || q.includes('cadastro')) return 'login';
    return 'landing';
  };

  const [screen, setScreen] = useState(getInitialScreen);
  const [loginTab, setLoginTab] = useState(() => {
    if (isPartnerAccessRoute()) return 'login';
    if (referralSlug || workerReferralId) return 'register';
    const q = window.location.search;
    return (q.includes('cadastrar') || q.includes('cadastro') || window.location.pathname === '/cadastrar') ? 'register' : 'login';
  });

  useEffect(() => {
    if (user) {
      document.body.classList.add('app-mode');
      setScreen('app');
      const allowed = ALLOWED[user.role] || [];
      const requestedView = getRequestedView();
      setView((v) => (
        requestedView && allowed.includes(requestedView)
          ? requestedView
          : v && allowed.includes(v)
            ? v
            : allowed[0]
      ));
    } else if (screen === 'app') {
      document.body.classList.remove('app-mode');
      setScreen('landing');
    }
  }, [user]); // eslint-disable-line

  useEffect(() => {
    if (screen === 'app') document.body.classList.add('app-mode');
    else document.body.classList.remove('app-mode');
  }, [screen]);

  const goToLogin = (tab = 'login') => { setLoginTab(tab); setScreen('login'); };
  const goToLoginFromReset = () => {
    window.history.replaceState({}, '', '/login');
    setLoginTab('login');
    setScreen('login');
  };

  if (legalRoute) return <LegalPage type={legalRoute} />;

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: C.bg }}>
      <div style={{ textAlign: 'center' }}>
        <Spinner size={40} />
        <div style={{ color: C.sub, marginTop: 12, fontSize: 14 }}>Carregando...</div>
      </div>
    </div>
  );

  if (!user) {
    if (resetPasswordRoute || screen === 'reset') return <ResetPasswordPage onGoLogin={goToLoginFromReset} />;
    if (screen === 'landing') return <LandingPage onEnter={goToLogin} />;
    return <LoginPage initialTab={loginTab} onGoLanding={() => setScreen('landing')} referralSlug={referralSlug} referralAudience={referralAudience} workerReferralId={workerReferralId} />;
  }

  const allowed = ALLOWED[user.role] || [];
  const safeView = allowed.includes(view) ? view : allowed[0];
  const setView2 = (v) => { if (allowed.includes(v)) setView(v); };

  const renderView = () => {
    switch (safeView) {
      case 'dash':        return <AdminDash />;
      case 'jobs':        return <AdminJobs />;
      case 'clients':     return <AdminClients />;
      case 'workers':     return <AdminWorkers />;
      case 'partners':    return <AdminPartners />;
      case 'partnerperformance': return <AdminPartnerPerformance />;
      case 'pricetable':  return <AdminPriceTable />;
      case 'finance':     return <AdminFinanceV2 />;
      case 'withdrawals': return <AdminWithdrawals />;
      case 'accounting':  return <Accounting />;
      case 'promotions':  return <AdminPromotions />;
      case 'reviews':     return <AdminReviews />;
      case 'settings':    return <AdminSettings />;
      case 'request':     return <ClientRequest />;
      case 'myjobs':      return <ClientMyJobs />;
      case 'profile':     return <ClientProfile />;
      case 'available':   return <WorkerAvailable setView={setView2} />;
      case 'mywork':      return <WorkerMyWork />;
      case 'wallet':      return <WorkerWallet />;
      case 'documents':   return <WorkerDocuments />;
      case 'wprofile':    return <WorkerProfile />;
      case 'partnerdash': return <PartnerDash />;
      case 'partnerjobs': return <PartnerJobs />;
      case 'partnerclients': return <PartnerClients />;
      case 'partnerworkers': return <PartnerWorkers />;
      default:            return null;
    }
  };

  return (
    <div className="qs-app-shell" style={{ display: 'flex', height: '100vh', minHeight: 0, overflow: 'hidden', background: C.bg }}>
      <Sidebar view={safeView} setView={setView2} user={user} logout={logout} />
      <main className="qs-app-main" style={{ flex: 1, height: '100vh', minHeight: 0, overflow: 'hidden' }}>
        {renderView()}
      </main>
    </div>
  );
}
